import "./index.css";
import baileLogo from "./assets/baile-logo.jpeg";
import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { supabase, supabaseUrl, supabaseAnonKey } from "./lib/supabase";
import { buildMembershipPaymentHistoryViewModel } from "./lib/membershipPaymentHistory";


/* =========================
   UI helpers
========================= */
const Card = React.forwardRef(({ children, className }, ref) => (
  <div ref={ref} className={`card ${className || ""}`}>
    {children}
  </div>
));

const Button = ({ children, className, style, ...props }) => (
  <button className={className} style={style} {...props}>
    {children}
  </button>
);

const Input = (props) => <input {...props} />;
const CalendarIcon = () => <>📅</>;
const UserIcon = () => <>👤</>;
const LogOutIcon = () => <>🔓</>;
const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.8A3.95 3.95 0 0 0 3.8 7.75v8.5a3.95 3.95 0 0 0 3.95 3.95h8.5a3.95 3.95 0 0 0 3.95-3.95v-8.5a3.95 3.95 0 0 0-3.95-3.95h-8.5Zm8.95 1.35a1.15 1.15 0 1 1 0 2.3 1.15 1.15 0 0 1 0-2.3ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 1.8A3.2 3.2 0 1 0 12 15.2 3.2 3.2 0 0 0 12 8.8Z"
    />
  </svg>
);
const WebsiteIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm6.93 9h-3.07a15.8 15.8 0 0 0-1.22-5.02A8.03 8.03 0 0 1 18.93 11Zm-6.93 9c-.87 0-2.28-1.85-2.82-5h5.64c-.54 3.15-1.95 5-2.82 5Zm-3.04-7a18.1 18.1 0 0 1 0-2h6.08a18.1 18.1 0 0 1 0 2H8.96Zm-4.89-2a8.03 8.03 0 0 1 4.29-5.02A15.8 15.8 0 0 0 7.14 11H4.07Zm0 2h3.07a15.8 15.8 0 0 0 1.22 5.02A8.03 8.03 0 0 1 4.07 13Zm9.57 5.02A15.8 15.8 0 0 0 15.86 13h3.07a8.03 8.03 0 0 1-4.29 5.02ZM14.82 9H9.18c.54-3.15 1.95-5 2.82-5s2.28 1.85 2.82 5Z"
    />
  </svg>
);
const isClassFull = (cls, bookedCount) => bookedCount >= cls.spots;
const MOBILE_SCHEDULE_MEDIA_QUERY = "(max-width: 640px)";


/* =========================
   Helpers
========================= */
const toISODate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};


const getMonthDays = (yyyyMM) => {
  const [y, m] = yyyyMM.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0);
  const days = [];
  for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
    days.push(new Date(d));
  }
  return days;
};

const weekdayIndexMonFirst = (date) => (date.getDay() + 6) % 7;
const weekdayShort = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const REVIEW_MOODS = [
  { rating: 7, emoji: "🧡", label: "Love it" },
  { rating: 6, emoji: "🔥", label: "Hot" },
  { rating: 5, emoji: "👑", label: "Top tier" },
  { rating: 4, emoji: "👟", label: "Solid groove" },
  { rating: 3, emoji: "🗣️", label: "So-so" },
  { rating: 2, emoji: "🥰", label: "Soft like" },
  { rating: 1, emoji: "☹️", label: "Did not like" },
];
const getReviewEmoji = (ratingValue) =>
  REVIEW_MOODS.find((item) => item.rating === Number(ratingValue))?.emoji || "🗣️";
const parseLocalIsoDate = (isoDate) => {
  const [year, month, day] = String(isoDate || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};
const getWeekdayLabel = (isoDate) => {
  if (!isoDate) return "";
  const d = parseLocalIsoDate(isoDate);
  if (!d || Number.isNaN(d.getTime())) return "";
  return weekdayShort[d.getDay()];
};
const formatWeekRangeLabel = (dateLike, compact = false) => {
  const start = getWeekStartMonday(dateLike);
  if (!start) return "";
  const end = new Date(start);
  end.setDate(start.getDate() + 6);

  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();

  if (compact) {
    if (sameMonth) {
      return `${start.getDate()}-${end.getDate()} ${new Intl.DateTimeFormat("en-GB", {
        month: "short",
      }).format(start)}`;
    }
    if (sameYear) {
      return `${new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "short",
      }).format(start)} - ${new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "short",
      }).format(end)}`;
    }
    return `${new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(start)} - ${new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(end)}`;
  }

  if (sameMonth) {
    return `${start.getDate()}-${end.getDate()} ${new Intl.DateTimeFormat("en-GB", {
      month: "long",
      year: "numeric",
    }).format(start)}`;
  }
  if (sameYear) {
    return `${new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "long",
    }).format(start)} - ${new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(end)}`;
  }
  return `${new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(start)} - ${new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(end)}`;
};
const formatMonthLabel = (yyyyMM) => {
  const [year, month] = String(yyyyMM || "").split("-").map(Number);
  if (!year || !month) return "";
  const date = new Date(year, month - 1, 1);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    month: "long",
    year: "numeric",
  }).format(date);
};
const getRecentMonthOptions = (count = 24) => {
  const options = [];
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth(), 1);
  for (let index = 0; index < count; index += 1) {
    const date = new Date(base.getFullYear(), base.getMonth() - index, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    options.push({ value, label: formatMonthLabel(value) });
  }
  return options;
};

const getIsMobileScheduleViewport = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(MOBILE_SCHEDULE_MEDIA_QUERY).matches;
};

const getDefaultScheduleViewMode = () =>
  getIsMobileScheduleViewport() ? "week" : "month";
const describeCheckoutIntent = (intent, membershipPlans = []) => {
  if (!intent?.kind) return "";
  if (intent.kind === "credits") {
    return `${intent.amount} credit${Number(intent.amount) === 1 ? "" : "s"}`;
  }
  if (intent.kind === "amiga_ticket") {
    return "an Amiga ticket";
  }
  if (intent.kind === "membership") {
    const planName =
      membershipPlans.find((plan) => plan.id === intent.planId)?.name || "membership";
    return `${planName} membership`;
  }
  return "";
};
const PENDING_CHECKOUT_STORAGE_KEY = "baile_pending_checkout_intent";
const loadPendingCheckoutIntent = () => {
  try {
    const raw = localStorage.getItem(PENDING_CHECKOUT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};
const savePendingCheckoutIntent = (intent) => {
  try {
    if (!intent) {
      localStorage.removeItem(PENDING_CHECKOUT_STORAGE_KEY);
      return;
    }
    localStorage.setItem(PENDING_CHECKOUT_STORAGE_KEY, JSON.stringify(intent));
  } catch {
    // ignore storage errors
  }
};
const formatMonthCount = (value) => {
  const count = Math.max(1, Number(value) || 1);
  return `${count} month${count === 1 ? "" : "s"}`;
};
const formatInvokeError = (error, data) => {
  const payloadError = data?.error;
  if (typeof payloadError === "string" && payloadError.trim()) return payloadError;
  if (payloadError && typeof payloadError === "object") {
    try {
      return JSON.stringify(payloadError);
    } catch {
      return String(payloadError);
    }
  }
  if (typeof error?.message === "string" && error.message.trim()) return error.message;
  if (error) {
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
  return "Unknown error";
};
const getFriendlySignupError = (error) => {
  const rawMessage = String(error?.message || "").trim();
  const normalized = rawMessage.toLowerCase();
  if (!normalized) {
    return {
      message: "Registration failed. Please try again.",
      switchToLogin: false,
    };
  }
  if (normalized.includes("email rate limit exceeded")) {
    return {
      message:
        "Too many confirmation emails were requested. Please wait a few minutes, or log in if this account already exists.",
      switchToLogin: false,
    };
  }
  if (
    normalized.includes("already registered") ||
    normalized.includes("user already registered") ||
    normalized.includes("already exists")
  ) {
    return {
      message: "This email already has an account. Log in instead to continue.",
      switchToLogin: true,
    };
  }
  return {
    message: rawMessage || "Registration failed.",
    switchToLogin: false,
  };
};
const addMonthsClamped = (input, monthsToAdd) => {
  const base = new Date(input);
  if (Number.isNaN(base.getTime())) return null;
  const result = new Date(base);
  const dayOfMonth = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() + (Number(monthsToAdd) || 0));
  const lastDayOfMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(dayOfMonth, lastDayOfMonth));
  return result;
};
const parseTimeMinutes = (timeValue) => {
  if (!timeValue) return Number.POSITIVE_INFINITY;
  const start = String(timeValue).split(" - ")[0].trim();
  const [hh, mm] = start.split(":").map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return Number.POSITIVE_INFINITY;
  return hh * 60 + mm;
};
const getClassTimeRange = (dateIso, timeValue) => {
  if (!dateIso || !timeValue) return null;
  const [startRaw, endRaw] = String(timeValue).split(" - ").map((t) => t.trim());
  const [sh, sm] = (startRaw || "").split(":").map(Number);
  if (Number.isNaN(sh) || Number.isNaN(sm)) return null;
  const start = parseLocalIsoDate(dateIso);
  if (!start || Number.isNaN(start.getTime())) return null;
  start.setHours(sh, sm, 0, 0);
  let end = null;
  if (endRaw) {
    const [eh, em] = endRaw.split(":").map(Number);
    if (!Number.isNaN(eh) && !Number.isNaN(em)) {
      end = parseLocalIsoDate(dateIso);
      end.setHours(eh, em, 0, 0);
    }
  }
  return { start, end };
};
const isClassPast = (cls, now = new Date()) => {
  if (!cls?.date) return false;
  const range = getClassTimeRange(cls.date, cls.time);
  if (range?.end) return now > range.end;
  if (range?.start) return now > range.start;
  const endOfDay = parseLocalIsoDate(cls.date);
  if (!endOfDay || Number.isNaN(endOfDay.getTime())) return false;
  endOfDay.setHours(23, 59, 59, 999);
  return now > endOfDay;
};
const isClassOngoing = (cls, now = new Date()) => {
  if (!cls?.date) return false;
  const range = getClassTimeRange(cls.date, cls.time);
  if (!range?.start) return false;
  const end = range.end || range.start;
  return now >= range.start && now <= end;
};
const isUuid = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "")
  );
const isLikelyEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
const getProfileDisplayName = (profile, fallback) =>
  profile?.name || profile?.email || fallback;
const DEFAULT_MEMBERSHIP_PLAN_PRESETS = [
  { name: "Basic", price: 39, creditsPerMonth: 4, minimumMonths: 12 },
  { name: "Standard", price: 69, creditsPerMonth: 8, minimumMonths: 12 },
  { name: "Premium", price: 99, creditsPerMonth: 12, minimumMonths: 6 },
];
const sortMembershipPlans = (plans) =>
  [...plans].sort((a, b) => {
    const priceA = Number(a?.price) || 0;
    const priceB = Number(b?.price) || 0;
    if (priceA !== priceB) return priceA - priceB;
    const creditsA = Number(a?.creditsPerMonth) || 0;
    const creditsB = Number(b?.creditsPerMonth) || 0;
    if (creditsA !== creditsB) return creditsA - creditsB;
    return String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
      sensitivity: "base",
    });
  });
const isDefaultMembershipPlanPreset = (plan) => {
  const name = String(plan?.name || "").trim().toLowerCase();
  const preset = DEFAULT_MEMBERSHIP_PLAN_PRESETS.find(
    (item) => item.name.toLowerCase() === name
  );
  if (!preset) return false;
  return (
    Number(plan?.price) === Number(preset.price) &&
    Number(plan?.creditsPerMonth) === Number(preset.creditsPerMonth) &&
    Number(plan?.minimumMonths) === Number(preset.minimumMonths)
  );
};
const dedupePublicMembershipPlans = (plans) => {
  const byName = new Map();

  plans.forEach((plan) => {
    const key = String(plan?.name || "").trim().toLowerCase();
    if (!key) return;

    const existing = byName.get(key);
    if (!existing) {
      byName.set(key, plan);
      return;
    }

    const existingIsDefault = isDefaultMembershipPlanPreset(existing);
    const candidateIsDefault = isDefaultMembershipPlanPreset(plan);
    if (existingIsDefault !== candidateIsDefault) {
      byName.set(key, candidateIsDefault ? existing : plan);
      return;
    }

    const existingTs = Date.parse(existing.updatedAt || existing.createdAt || "");
    const candidateTs = Date.parse(plan.updatedAt || plan.createdAt || "");

    if (!Number.isNaN(candidateTs) && !Number.isNaN(existingTs) && candidateTs !== existingTs) {
      if (candidateTs > existingTs) byName.set(key, plan);
      return;
    }

    if (!Number.isNaN(candidateTs) && Number.isNaN(existingTs)) {
      byName.set(key, plan);
      return;
    }

    const existingPrice = Number(existing?.price) || 0;
    const candidatePrice = Number(plan?.price) || 0;
    if (candidatePrice !== existingPrice) {
      if (candidatePrice > existingPrice) byName.set(key, plan);
      return;
    }

    const existingCredits = Number(existing?.creditsPerMonth) || 0;
    const candidateCredits = Number(plan?.creditsPerMonth) || 0;
    if (candidateCredits > existingCredits) {
      byName.set(key, plan);
      return;
    }

    const existingMinimum = Number(existing?.minimumMonths) || 0;
    const candidateMinimum = Number(plan?.minimumMonths) || 0;
    if (candidateMinimum > existingMinimum) {
      byName.set(key, plan);
    }
  });

  return sortMembershipPlans(Array.from(byName.values()));
};
const DEFAULT_EMAIL_BRANDING = {
  brandName: "Baile Project",
  logoUrl: "",
  heroUrl: "",
  accentColor: "#f4c430",
  headerBg: "#111111",
  footerText: "Thanks for dancing with us.",
  signature: "Baile Project",
  siteUrl: "",
};
const normalizeEmailBrandingRow = (row) => ({
  brandName: row?.brand_name || DEFAULT_EMAIL_BRANDING.brandName,
  logoUrl: row?.logo_url || DEFAULT_EMAIL_BRANDING.logoUrl,
  heroUrl: row?.hero_url || DEFAULT_EMAIL_BRANDING.heroUrl,
  accentColor: row?.accent_color || DEFAULT_EMAIL_BRANDING.accentColor,
  headerBg: row?.header_bg || DEFAULT_EMAIL_BRANDING.headerBg,
  footerText: row?.footer_text || DEFAULT_EMAIL_BRANDING.footerText,
  signature: row?.signature || DEFAULT_EMAIL_BRANDING.signature,
  siteUrl: row?.site_url || DEFAULT_EMAIL_BRANDING.siteUrl,
});
const DEFAULT_APP_STYLING = {
  brandName: "Baile Project",
  appTitle: "Class Schedule",
  logoUrl: "",
  appBackground: "#0f0f0f",
  surfaceBackground: "#161616",
  headerBackground: "#0f0f0f",
  inputBackground: "#1f1f1f",
  borderColor: "#2a2a2a",
  textColor: "#f5f5f5",
  mutedTextColor: "#d1d5db",
  buttonFrom: "#f39c12",
  buttonTo: "#f4c430",
  buttonText: "#111111",
};
const DEFAULT_INVOICE_PRESETS = {
  issuerName: "Baile Project",
  issuerAddress: "",
  issuerKvkNumber: "",
  issuerVatNumber: "",
  issuerEmail: "",
  issuerPhone: "",
  defaultDescription: "Dance class credits / membership",
  footerNote: "Thank you for your purchase.",
};
const normalizeAppStylingRow = (row) => ({
  brandName: row?.brand_name || DEFAULT_APP_STYLING.brandName,
  appTitle: row?.app_title || DEFAULT_APP_STYLING.appTitle,
  logoUrl: row?.logo_url || DEFAULT_APP_STYLING.logoUrl,
  appBackground: row?.app_background || DEFAULT_APP_STYLING.appBackground,
  surfaceBackground: row?.surface_background || DEFAULT_APP_STYLING.surfaceBackground,
  headerBackground: row?.header_background || DEFAULT_APP_STYLING.headerBackground,
  inputBackground: row?.input_background || DEFAULT_APP_STYLING.inputBackground,
  borderColor: row?.border_color || DEFAULT_APP_STYLING.borderColor,
  textColor: row?.text_color || DEFAULT_APP_STYLING.textColor,
  mutedTextColor: row?.muted_text_color || DEFAULT_APP_STYLING.mutedTextColor,
  buttonFrom: row?.button_from || DEFAULT_APP_STYLING.buttonFrom,
  buttonTo: row?.button_to || DEFAULT_APP_STYLING.buttonTo,
  buttonText: row?.button_text || DEFAULT_APP_STYLING.buttonText,
});
const normalizeEmailTemplateRow = (row) => ({
  id: row?.id || "",
  key: row?.key || "",
  name: row?.name || "",
  subject: row?.subject || "",
  preheader: row?.preheader || "",
  heroUrl: row?.hero_url || "",
  heading: row?.heading || "",
  greeting: row?.greeting || "",
  body: row?.body || "",
  ctaLabel: row?.cta_label || "",
  ctaUrl: row?.cta_url || "",
  signature: row?.signature || "",
  footer: row?.footer || "",
  isActive: row?.is_active !== false,
});
const escapeEmailHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
const applyEmailTokens = (input, data, { html, accentColor }) =>
  String(input || "").replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, rawKey) => {
    const key = String(rawKey || "");
    const value = data?.[key] ?? "";
    if (!html) return String(value ?? "");
    if (key === "brand_name") {
      return `<span style="background:${accentColor};padding:0 4px;border-radius:3px;">${escapeEmailHtml(
        value
      )}</span>`;
    }
    return escapeEmailHtml(value);
  });
const emailHtmlWithLineBreaks = (value) =>
  String(value ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\n/g, "<br />");
const decodeJwtPayload = (token) => {
  if (!token) return null;
  const parts = String(token).split(".");
  if (parts.length < 2) return null;
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(payload);
    return JSON.parse(json);
  } catch {
    return null;
  }
};
const inlineMarkdownToHtml = (value) => {
  let result = escapeEmailHtml(value);
  result = result.replace(/`([^`]+)`/g, "<code>$1</code>");
  result = result.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  result = result.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  result = result.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  result = result.replace(/_([^_]+)_/g, "<em>$1</em>");
  result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
  return result;
};
const markdownToHtml = (markdown) => {
  const lines = String(markdown || "").replace(/\r\n/g, "\n").split("\n");
  let html = "";
  let inList = false;
  let hasParagraph = false;

  const closeList = () => {
    if (inList) {
      html += "</ul>";
      inList = false;
    }
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      closeList();
      hasParagraph = false;
      return;
    }
    if (/^[-*]\s+/.test(trimmed)) {
      if (!inList) {
        html += "<ul>";
        inList = true;
      }
      const content = trimmed.replace(/^[-*]\s+/, "");
      html += `<li>${inlineMarkdownToHtml(content)}</li>`;
      return;
    }
    closeList();
    const content = inlineMarkdownToHtml(trimmed);
    if (!hasParagraph) {
      html += `<p>${content}`;
      hasParagraph = true;
    } else {
      html += `<br />${content}`;
    }
  });

  if (hasParagraph) html += "</p>";
  closeList();
  return html || "";
};
const buildEmailPreviewHtml = ({ branding, template, data }) => {
  const accentColor = branding.accentColor || DEFAULT_EMAIL_BRANDING.accentColor;
  const headerBg = branding.headerBg || DEFAULT_EMAIL_BRANDING.headerBg;
  const mergedData = {
    ...data,
    brand_name: data?.brand_name || branding.brandName,
    site_url: data?.site_url || branding.siteUrl || "",
  };
  const preheader = applyEmailTokens(template.preheader || "", mergedData, {
    html: true,
    accentColor,
  });
  const subject = applyEmailTokens(template.subject || "", mergedData, {
    html: true,
    accentColor,
  });
  const heading = applyEmailTokens(template.heading || "", mergedData, {
    html: true,
    accentColor,
  });
  const greeting = applyEmailTokens(template.greeting || "", mergedData, {
    html: true,
    accentColor,
  });
  const bodyText = applyEmailTokens(template.body || "", mergedData, {
    html: false,
    accentColor,
  });
  const bodyHtml = markdownToHtml(bodyText);
  const signature = applyEmailTokens(template.signature || "", mergedData, {
    html: true,
    accentColor,
  });
  const footer = applyEmailTokens(
    template.footer || branding.footerText || DEFAULT_EMAIL_BRANDING.footerText,
    mergedData,
    { html: true, accentColor }
  );
  const footerHtml = emailHtmlWithLineBreaks(footer);
  const heroUrl = template.heroUrl || branding.heroUrl;
  const ctaLabel = applyEmailTokens(template.ctaLabel || "", mergedData, {
    html: true,
    accentColor,
  });
  const ctaUrl = applyEmailTokens(template.ctaUrl || "", mergedData, {
    html: false,
    accentColor,
  });

  return `<!doctype html>
<html>
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width" />
    <title>${escapeEmailHtml(template.subject || "")}</title>
    <style>
      .email-preview-meta {
        max-width: 600px;
        margin: 0 auto 12px;
        padding: 12px 16px;
        font-family: "Helvetica Neue", Arial, sans-serif;
        background: rgba(255, 255, 255, 0.75);
        border: 1px solid rgba(0, 0, 0, 0.08);
        border-radius: 14px;
        box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
      }
      .email-preview-meta-label {
        font-size: 11px;
        color: #6b7280;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        margin-bottom: 6px;
      }
      .email-preview-meta-value {
        font-size: 18px;
        font-weight: 600;
        color: #111111;
        margin-bottom: 10px;
      }
      .email-preview-meta-subvalue {
        font-size: 14px;
        color: #374151;
        margin-bottom: 4px;
      }
    </style>
  </head>
  <body style="margin:0;padding:0;background:#e5e5e5;">
    <div class="email-preview-meta">
      ${subject ? `<div class="email-preview-meta-label">Subject</div><div class="email-preview-meta-value">${subject}</div>` : ""}
      ${preheader ? `<div class="email-preview-meta-label">Preheader</div><div class="email-preview-meta-subvalue">${preheader}</div>` : ""}
    </div>
    <div style="display:none;max-height:0;overflow:hidden;font-size:1px;line-height:1px;color:#e5e5e5;">
      ${preheader}
    </div>
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#e5e5e5;padding:24px 0;">
      <tr>
        <td align="center">
          <table role="presentation" width="600" cellspacing="0" cellpadding="0" style="width:600px;max-width:600px;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 10px 30px rgba(0,0,0,0.08);">
            <tr>
              <td style="background:${headerBg};padding:20px 32px;">
                ${
                  branding.logoUrl
                    ? `<img src="${branding.logoUrl}" alt="${escapeEmailHtml(
                        branding.brandName
                      )}" style="height:40px;display:block;" />`
                    : `<div style="font-size:18px;font-weight:700;color:#ffffff;">${escapeEmailHtml(
                        branding.brandName
                      )}</div>`
                }
              </td>
            </tr>
            ${
              heroUrl
                ? `<tr><td><img src="${heroUrl}" alt="" style="width:100%;display:block;" /></td></tr>`
                : ""
            }
            <tr>
              <td style="padding:32px 36px 20px;font-family:'Helvetica Neue',Arial,sans-serif;color:#1f2937;font-size:16px;line-height:1.6;">
                ${greeting ? `<p style="margin:0 0 18px;">${greeting}</p>` : ""}
                ${heading ? `<h1 style="margin:0 0 18px;font-size:22px;line-height:1.3;">${heading}</h1>` : ""}
                ${bodyHtml
                  .replace(/<p>/g, '<p style="margin:0 0 16px;">')
                  .replace(/<ul>/g, '<ul style="margin:0 0 16px;padding-left:18px;">')
                  .replace(/<li>/g, '<li style="margin:0 0 6px;">')}
                ${
                  ctaLabel && ctaUrl
                    ? `<div style="margin:24px 0 10px;">
                        <a href="${ctaUrl}" style="background:${accentColor};color:#111111;text-decoration:none;padding:12px 22px;border-radius:8px;font-weight:600;display:inline-block;">${ctaLabel}</a>
                      </div>`
                    : ""
                }
                ${signature ? `<p style="margin:24px 0 0;font-weight:600;">${signature}</p>` : ""}
              </td>
            </tr>
            <tr>
              <td style="background:#f5f5f5;padding:18px 28px;font-family:'Helvetica Neue',Arial,sans-serif;font-size:12px;color:#6b7280;">
                ${footerHtml}
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
};
const getWeekStartMonday = (dateLike) => {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  const shift = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - shift);
  d.setHours(0, 0, 0, 0);
  return d;
};
const parseBillingAddressParts = (value) => {
  const lines = String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const street = lines[0] || "";
  let postcode = "";
  let city = "";
  if (lines.length >= 3) {
    postcode = lines[1] || "";
    city = lines.slice(2).join(", ");
  } else if (lines.length >= 2) {
    const secondLine = lines[1] || "";
    const match = secondLine.match(/^(\S+)\s+(.+)$/);
    postcode = match ? match[1] : secondLine;
    city = match ? match[2] : "";
  }
  return {
    street,
    postcode,
    city,
  };
};
const buildBillingAddress = ({ street, postcode, city }) =>
  [street, postcode, city]
    .map((line) => String(line || "").trim())
    .filter(Boolean)
    .join("\n");


export default function DanceSchoolApp() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authMode, setAuthMode] = useState("login");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [registerName, setRegisterName] = useState("");
  const [registerAcceptedTerms, setRegisterAcceptedTerms] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authNotice, setAuthNotice] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [membershipCheckoutError, setMembershipCheckoutError] = useState("");
  const [membershipCheckoutStatus, setMembershipCheckoutStatus] = useState("");
  const [membershipAcceptedTerms, setMembershipAcceptedTerms] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpQuestionDraft, setHelpQuestionDraft] = useState("");
  const [helpRequestStatus, setHelpRequestStatus] = useState("");
  const [helpRequestSending, setHelpRequestSending] = useState(false);
  const [classes, setClasses] = useState([]);
  const [view, setView] = useState("schedule");
  const [reviews, setReviews] = useState([]);
  const [reviewToken, setReviewToken] = useState("");
  const [reviewRating, setReviewRating] = useState(7);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewStatus, setReviewStatus] = useState("");
  const [selectedPastReviewClassId, setSelectedPastReviewClassId] = useState("");
  const [pastReviewRating, setPastReviewRating] = useState(7);
  const [pastReviewComment, setPastReviewComment] = useState("");
  const [pastReviewStatus, setPastReviewStatus] = useState("");
  const [publicView, setPublicView] = useState("login");
  const [pendingCheckoutIntent, setPendingCheckoutIntent] = useState(null);
  const [pendingCheckoutSubmitting, setPendingCheckoutSubmitting] = useState(false);
  const [publicMembershipPlanId, setPublicMembershipPlanId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [scheduleViewMode, setScheduleViewMode] = useState(getDefaultScheduleViewMode);
  const [isMobileScheduleViewport, setIsMobileScheduleViewport] = useState(
    getIsMobileScheduleViewport
  );
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return toISODate(now);
  });
  const [selectedClass, setSelectedClass] = useState(null);
  const [showNoCreditsModal, setShowNoCreditsModal] = useState(false);
  const [profileNameDraft, setProfileNameDraft] = useState("");
  const [profileNameStatus, setProfileNameStatus] = useState("");
  const [profileNameSaving, setProfileNameSaving] = useState(false);
  const [billingAddressStreetDraft, setBillingAddressStreetDraft] = useState("");
  const [billingAddressPostcodeDraft, setBillingAddressPostcodeDraft] = useState("");
  const [billingAddressCityDraft, setBillingAddressCityDraft] = useState("");
  const [billingAddressStatus, setBillingAddressStatus] = useState("");
  const [billingAddressSaving, setBillingAddressSaving] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bookedCounts, setBookedCounts] = useState({});
  const [creditLedger, setCreditLedger] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [waitlists, setWaitlists] = useState({});
  const [profiles, setProfiles] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState("");
  const [invoiceDownloadLoading, setInvoiceDownloadLoading] = useState({});
  const [invoiceRetryLoading, setInvoiceRetryLoading] = useState({});
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [invoiceDateFrom, setInvoiceDateFrom] = useState("");
  const [invoiceDateTo, setInvoiceDateTo] = useState("");
  const [invoicePresets, setInvoicePresets] = useState(DEFAULT_INVOICE_PRESETS);
  const [invoicePresetsDraft, setInvoicePresetsDraft] = useState(DEFAULT_INVOICE_PRESETS);
  const [invoicePresetsStatus, setInvoicePresetsStatus] = useState("");
  const [, setNotificationQueue] = useState([]);
  const [cancelPolicyHours, setCancelPolicyHours] = useState(4);
  const [showAllUserCredits, setShowAllUserCredits] = useState(false);
  const [pastClassesOpen, setPastClassesOpen] = useState(true);
  const [reviewPromptDismissed, setReviewPromptDismissed] = useState(false);
  const [auditDetailsOpen, setAuditDetailsOpen] = useState({});
  const [passwordChangeStatus, setPasswordChangeStatus] = useState("");
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [currentPasswordDraft, setCurrentPasswordDraft] = useState("");
  const [newPasswordDraft, setNewPasswordDraft] = useState("");
  const [confirmNewPasswordDraft, setConfirmNewPasswordDraft] = useState("");
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [membershipPlanDrafts, setMembershipPlanDrafts] = useState([]);
  const [memberships, setMemberships] = useState({});
  const [membershipDrafts, setMembershipDrafts] = useState({});
  const [legacyMembershipDrafts, setLegacyMembershipDrafts] = useState({});
  const [membershipUsage, setMembershipUsage] = useState({});
  const [membershipPaymentHistory, setMembershipPaymentHistory] = useState({});
  const [membershipActionStatus, setMembershipActionStatus] = useState({});
  const [legacyReminderSending, setLegacyReminderSending] = useState({});
  const [membershipReminderSending, setMembershipReminderSending] = useState({});
  const [membershipReminderSent, setMembershipReminderSent] = useState({});
  const [legacyReminderBulkSending, setLegacyReminderBulkSending] = useState(false);
  const [userDeleteLoading, setUserDeleteLoading] = useState({});
  const [creditPricePerUnit, setCreditPricePerUnit] = useState(10);
  const [creditPriceDraft, setCreditPriceDraft] = useState(10);
  const [creditPack10Price, setCreditPack10Price] = useState(90);
  const [creditPack10PriceDraft, setCreditPack10PriceDraft] = useState(90);
  const [userSortMode, setUserSortMode] = useState("default");
  const [reviewInviteEnabled, setReviewInviteEnabled] = useState(true);
  const [reviewInviteEnabledDraft, setReviewInviteEnabledDraft] = useState(true);
  const [reviewInviteFrequencyDays, setReviewInviteFrequencyDays] = useState(7);
  const [reviewInviteFrequencyDaysDraft, setReviewInviteFrequencyDaysDraft] = useState(7);
  const [reviewInviteExcludeMembers, setReviewInviteExcludeMembers] = useState(true);
  const [reviewInviteExcludeMembersDraft, setReviewInviteExcludeMembersDraft] = useState(true);
  const [reviewInviteDelayHours, setReviewInviteDelayHours] = useState(2);
  const [reviewInviteDelayHoursDraft, setReviewInviteDelayHoursDraft] = useState(2);
  const [adminAccountTab, setAdminAccountTab] = useState(() => {
    try {
      const saved = localStorage.getItem("adminAccountTab");
      return saved || "info";
    } catch {
      return "info";
    }
  });
  const [userAccountTab, setUserAccountTab] = useState("account");
  const [analyticsRange, setAnalyticsRange] = useState("month");
  const [analyticsMonth, setAnalyticsMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [analytics, setAnalytics] = useState({
    classesHappened: 0,
    totalPeopleParticipated: 0,
    uniqueParticipants: 0,
    creditsSold: 0,
    payoutTotal: 0,
    newAccounts: 0,
    activeMemberships: 0,
    newMemberships: 0,
    membershipChurn: 0,
  });
  const [analyticsMonthly, setAnalyticsMonthly] = useState([]);
  const [selectedAnalyticsMetric, setSelectedAnalyticsMetric] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState("");
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);
  const [emailTemplateDrafts, setEmailTemplateDrafts] = useState({});
  const [selectedEmailTemplateKey, setSelectedEmailTemplateKey] = useState("");
  const [, setEmailBranding] = useState(DEFAULT_EMAIL_BRANDING);
  const [emailBrandingDraft, setEmailBrandingDraft] = useState(DEFAULT_EMAIL_BRANDING);
  const [emailTemplateStatus, setEmailTemplateStatus] = useState("");
  const [emailBrandingStatus, setEmailBrandingStatus] = useState("");
  const [emailTestStatus, setEmailTestStatus] = useState("");
  const [emailDebugInfo, setEmailDebugInfo] = useState(null);
  const [appStyling, setAppStyling] = useState(DEFAULT_APP_STYLING);
  const [appStylingDraft, setAppStylingDraft] = useState(DEFAULT_APP_STYLING);
  const [appStylingStatus, setAppStylingStatus] = useState("");
  const [devToolsVisible, setDevToolsVisible] = useState(false);
  const [devToolsUnlocked, setDevToolsUnlocked] = useState(() => {
    try {
      return localStorage.getItem("devToolsUnlocked") === "1";
    } catch {
      return false;
    }
  });
  const [viewingAsNormalAccount, setViewingAsNormalAccount] = useState(() => {
    try {
      return localStorage.getItem("devToolsViewingAsNormal") === "1";
    } catch {
      return false;
    }
  });
  const [devToolsPasswordDraft, setDevToolsPasswordDraft] = useState("");
  const [devToolsLoading, setDevToolsLoading] = useState(false);
  const [devToolsStatus, setDevToolsStatus] = useState("");

  const isAdmin = profiles[user?.id]?.role === "admin";
  const canUseDevTools = String(user?.email || "").trim().toLowerCase() === "kevinkager95@gmail.com";
  const effectiveIsAdmin = canUseDevTools && devToolsUnlocked && !viewingAsNormalAccount
    ? true
    : Boolean(isAdmin && !viewingAsNormalAccount);
  const logoSrc = appStyling.logoUrl || baileLogo;
  const appThemeStyle = useMemo(
    () => ({
      "--baile-yellow": appStyling.buttonTo,
      "--baile-orange": appStyling.buttonFrom,
      "--baile-dark": appStyling.appBackground,
      "--baile-dark-soft": appStyling.surfaceBackground,
      "--baile-gray": appStyling.inputBackground,
      "--baile-border": appStyling.borderColor,
      "--baile-text": appStyling.textColor,
      "--baile-text-muted": appStyling.mutedTextColor,
      "--app-button-text": appStyling.buttonText,
      "--app-header-bg": appStyling.headerBackground,
    }),
    [appStyling]
  );

  useEffect(() => {
    if (view !== "schedule") return;
    const now = new Date();
    setSelectedMonth(
      `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
    );
    setSelectedDate(toISODate(now));
  }, [view]);

  useEffect(() => {
    try {
      localStorage.setItem("devToolsUnlocked", devToolsUnlocked ? "1" : "0");
    } catch {
      // ignore storage errors
    }
  }, [devToolsUnlocked]);

  useEffect(() => {
    try {
      localStorage.setItem("devToolsViewingAsNormal", viewingAsNormalAccount ? "1" : "0");
    } catch {
      // ignore storage errors
    }
  }, [viewingAsNormalAccount]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("review") || params.get("review_token") || "";
    if (token) {
      setReviewToken(token);
      // A review-invite link should open the review form, including for
      // people who are already signed in (whose default view is Schedule).
      setView("review");
      setPublicView("review");
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    try {
      localStorage.setItem("adminAccountTab", adminAccountTab);
    } catch {
      // ignore storage errors
    }
  }, [adminAccountTab, isAdmin]);


  const actionLabels = {
    BOOK_CLASS: "booked a class",
    CANCEL_CLASS: "cancelled a class",
    JOIN_WAITLIST: "joined the waitlist",
    LEAVE_WAITLIST: "left the waitlist",
    WAITLIST_PROMOTION: "was promoted from the waitlist",
    CREATE_CLASS: "created a class",
    EDIT_CLASS: "edited a class",
    EDIT_CLASS_SERIES: "edited a class series",
    DELETE_CLASS: "deleted a class",
    DUPLICATE_CLASS: "duplicated a class",
    CANCEL_CLASS_ADMIN: "cancelled a class",
    ADD_LOCATION: "added a location",
    REMOVE_LOCATION: "removed a location",
    ADD_TEACHER: "added a teacher",
    REMOVE_TEACHER: "removed a teacher",
    CHANGE_USER_CREDITS: "adjusted user credits",
    PURCHASE_CREDITS_INITIATED: "started a credit purchase",
    PURCHASE_CREDITS_FAILED: "failed a credit purchase",
    PURCHASE_CREDITS_REFUNDED: "refunded a credit purchase",
    PURCHASE_AMIGA_TICKET_INITIATED: "started an amiga ticket purchase",
    PURCHASE_AMIGA_TICKET_FAILED: "failed an amiga ticket purchase",
    AMIGA_TICKET_PRICE_UPDATED: "updated amiga ticket price",
    CHANGE_USER_AMIGA_TICKETS: "adjusted amiga tickets",
    MEMBERSHIP_SUBSCRIBE_INITIATED: "started membership checkout",
    MEMBERSHIP_SUBSCRIBE_FAILED: "failed membership checkout",
    MEMBERSHIP_ACTIVATED: "activated a membership",
    MEMBERSHIP_ACTIVATED_SERVER: "activated a membership (server)",
    MEMBERSHIP_CANCELLED: "cancelled a membership",
    MEMBERSHIP_PLAN_CHANGED: "changed membership plan",
    MEMBERSHIP_CREDITS_GRANTED: "received monthly membership credits",
    MEMBERSHIP_REFUNDED: "refunded membership payment",
    LEGACY_MEMBERSHIP_REMINDER_SENT: "sent a legacy membership payment reminder",
    PAYMENT_REFUNDED: "payment refunded",
    PAYMENT_CHARGED_BACK: "payment charged back",
    INVOICE_CREATED: "created an invoice",
    INVOICE_PDF_GENERATED: "generated invoice PDF",
    INVOICE_PDF_FAILED: "invoice PDF failed",
    CREDITS_GRANTED_SERVER: "granted credits (server)",
    USER_REGISTERED: "created an account",
    CANCEL_POLICY_UPDATED: "updated the cancellation policy",
    MEMBERSHIP_PLAN_UPDATED: "updated membership plan settings",
    CREDIT_PRICE_UPDATED: "updated credit price",
    RESET_CLASS_ATTENDANCE: "reset class attendance",
    EMAIL_TEMPLATE_UPDATED: "updated an email template",
    EMAIL_BRANDING_UPDATED: "updated email branding",
    APP_STYLING_UPDATED: "updated app styling",
    INVOICE_PRESETS_UPDATED: "updated invoice presets",
    REVIEW_INVITE_SETTINGS_UPDATED: "updated review invite settings",
    REVIEW_DELETED: "deleted a review",
  };

  const getAuditCategory = (action) => {
    if (["CREATE_CLASS", "EDIT_CLASS", "EDIT_CLASS_SERIES", "DELETE_CLASS", "DUPLICATE_CLASS", "CANCEL_CLASS_ADMIN", "RESET_CLASS_ATTENDANCE"].includes(action)) {
      return "Classes";
    }
    if (["BOOK_CLASS", "CANCEL_CLASS", "WAITLIST_PROMOTION", "JOIN_WAITLIST", "LEAVE_WAITLIST"].includes(action)) {
      return "Bookings & Waitlist";
    }
    if (["EMAIL_TEMPLATE_UPDATED", "EMAIL_BRANDING_UPDATED"].includes(action)) {
      return "Email";
    }
    if (["APP_STYLING_UPDATED"].includes(action)) {
      return "Admin & Settings";
    }
    if (["CHANGE_USER_CREDITS", "PURCHASE_CREDITS_INITIATED", "PURCHASE_CREDITS_FAILED", "PURCHASE_CREDITS_REFUNDED", "PURCHASE_AMIGA_TICKET_INITIATED", "PURCHASE_AMIGA_TICKET_FAILED", "CHANGE_USER_AMIGA_TICKETS", "PAYMENT_REFUNDED", "PAYMENT_CHARGED_BACK", "INVOICE_CREATED", "INVOICE_PDF_GENERATED", "INVOICE_PDF_FAILED", "CREDITS_GRANTED_SERVER"].includes(action)) {
      return "Credits & Payments";
    }
    if (["MEMBERSHIP_ACTIVATED", "MEMBERSHIP_ACTIVATED_SERVER", "MEMBERSHIP_CANCELLED", "MEMBERSHIP_PLAN_CHANGED", "MEMBERSHIP_CREDITS_GRANTED", "MEMBERSHIP_SUBSCRIBE_INITIATED", "MEMBERSHIP_SUBSCRIBE_FAILED", "MEMBERSHIP_REFUNDED"].includes(action)) {
      return "Memberships";
    }
    if (["USER_REGISTERED", "CANCEL_POLICY_UPDATED", "MEMBERSHIP_PLAN_UPDATED", "CREDIT_PRICE_UPDATED", "AMIGA_TICKET_PRICE_UPDATED", "REVIEW_INVITE_SETTINGS_UPDATED", "INVOICE_PRESETS_UPDATED", "REVIEW_DELETED", "ADD_LOCATION", "REMOVE_LOCATION", "ADD_TEACHER", "REMOVE_TEACHER"].includes(action)) {
      return "Admin & Settings";
    }
    return "Other";
  };

  const logAction = ({ actor, action, details }) => {
    const optimistic = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      actor,
      action,
      details,
    };
    setAuditLog((prev) => [optimistic, ...prev]);

    if (!user?.id) return;

    supabase
      .from("audit_log")
      .insert({
        actor_user_id: actor === "system" ? null : user.id,
        action,
        details: { ...(details || {}), actorEmail: actor },
      })
      .then(() => {
        if (isAdmin) {
          loadAuditLog();
        }
      })
      .catch(() => {});
  };

  const [showAuditLog, setShowAuditLog] = useState(false);
  const [creditHistoryOpen, setCreditHistoryOpen] = useState({});
  const [creditHistoryDateOpen, setCreditHistoryDateOpen] = useState({});
  const [auditDateOpen, setAuditDateOpen] = useState({});
  const [attendanceOpen, setAttendanceOpen] = useState({});
  const [adminAddClass, setAdminAddClass] = useState(null);
  const [adminAddSearchDraft, setAdminAddSearchDraft] = useState("");
  const [adminAddSearchTerm, setAdminAddSearchTerm] = useState("");
  const [adminAddCreditSource, setAdminAddCreditSource] = useState("regular");
  const [adminAddLoadingUserId, setAdminAddLoadingUserId] = useState("");
  const [adminAddStatus, setAdminAddStatus] = useState("");
  const [auditSearch, setAuditSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [amigaTicketLedger, setAmigaTicketLedger] = useState([]);
  const [amigaTicketPricePerUnit, setAmigaTicketPricePerUnit] = useState(38);
  const [amigaTicketPriceDraft, setAmigaTicketPriceDraft] = useState(38);

  const requestPasswordChange = async () => {
    if (!user?.id || !user?.email) return;
    if (!newPasswordDraft || !confirmNewPasswordDraft) {
      setPasswordChangeStatus("Please fill in all password fields.");
      return;
    }
    if (!isPasswordRecovery && !currentPasswordDraft) {
      setPasswordChangeStatus("Please fill in all password fields.");
      return;
    }
    if (newPasswordDraft !== confirmNewPasswordDraft) {
      setPasswordChangeStatus("New password and confirmation do not match.");
      return;
    }
    if (newPasswordDraft.length < 6) {
      setPasswordChangeStatus("New password must be at least 6 characters.");
      return;
    }

    setPasswordChangeLoading(true);
    if (!isPasswordRecovery) {
      setPasswordChangeStatus("Verifying current password...");
      const { error: reauthError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPasswordDraft,
      });
      if (reauthError) {
        setPasswordChangeLoading(false);
        setPasswordChangeStatus("Current password is incorrect.");
        return;
      }
    }

    setPasswordChangeStatus("Updating password...");
    const { error } = await supabase.auth.updateUser({ password: newPasswordDraft });
    setPasswordChangeLoading(false);
    if (error) {
      setPasswordChangeStatus(error.message || "Could not update password.");
      return;
    }

    setCurrentPasswordDraft("");
    setNewPasswordDraft("");
    setConfirmNewPasswordDraft("");
    setIsPasswordRecovery(false);
    setPasswordChangeStatus("Password updated.");
  };

  const renderPasswordRecoveryModal = () => {
    if (!isPasswordRecovery || !user?.id) return null;

    return (
      <div className="modal-backdrop password-recovery-backdrop">
        <div
          className="modal-card password-recovery-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="password-recovery-title"
        >
          <div className="modal-header">
            <div>
              <strong id="password-recovery-title">Set your password</strong>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                Your account is almost ready. Choose a password first to continue to the new platform.
              </div>
            </div>
          </div>
          <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 12 }}>
            After saving your password, you can immediately continue and reserve your spots for April.
          </div>
          <div className="form-field">
            <label>New password</label>
            <Input
              autoFocus
              type="password"
              value={newPasswordDraft}
              onChange={(e) => {
                setNewPasswordDraft(e.target.value);
                if (passwordChangeStatus) setPasswordChangeStatus("");
              }}
              placeholder="Choose a password"
            />
          </div>
          <div className="form-field" style={{ marginTop: 10 }}>
            <label>Confirm new password</label>
            <Input
              type="password"
              value={confirmNewPasswordDraft}
              onChange={(e) => {
                setConfirmNewPasswordDraft(e.target.value);
                if (passwordChangeStatus) setPasswordChangeStatus("");
              }}
              placeholder="Repeat your password"
            />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <Button
              className="ghost-button"
              type="button"
              onClick={logout}
              disabled={passwordChangeLoading}
            >
              Log out
            </Button>
            <Button type="button" onClick={requestPasswordChange} disabled={passwordChangeLoading}>
              {passwordChangeLoading ? "Saving..." : "Save password"}
            </Button>
          </div>
          {passwordChangeStatus && (
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 10 }}>{passwordChangeStatus}</div>
          )}
        </div>
      </div>
    );
  };

  const formatEuro = (value) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR" }).format(
      Number(value) || 0
    );

  const creditBalances = useMemo(() => {
    const balances = {};
    const now = new Date();
    creditLedger.forEach((entry) => {
      if (entry.expiresAt && new Date(entry.expiresAt) <= now) return;
      balances[entry.userId] = (balances[entry.userId] || 0) + entry.delta;
    });
    return balances;
  }, [creditLedger]);

  const getPurchasedBalance = (userId) => Math.max(0, creditBalances[userId] || 0);

  const amigaTicketBalances = useMemo(() => {
    const balances = {};
    const now = new Date();
    amigaTicketLedger.forEach((entry) => {
      if (entry.expiresAt && new Date(entry.expiresAt) <= now) return;
      balances[entry.userId] = (balances[entry.userId] || 0) + entry.delta;
    });
    return balances;
  }, [amigaTicketLedger]);

  const getAmigaTicketBalance = (userId) => Math.max(0, amigaTicketBalances[userId] || 0);

  const getMembershipPlanForUser = (userId) => {
    const membership = memberships[userId];
    if (!membership?.planId) return null;
    return membershipPlans.find((p) => p.id === membership.planId) || null;
  };

  const getMembershipCreditsPerMonth = (userId) => {
    const membership = memberships[userId];
    if (profiles[userId]?.legacy_member && membership?.customCreditsPerMonth != null) {
      const customCredits = Number(membership.customCreditsPerMonth);
      if (Number.isFinite(customCredits) && customCredits >= 0) return customCredits;
    }
    const plan = getMembershipPlanForUser(userId);
    return Number(plan?.creditsPerMonth) || 0;
  };

  const getMembershipMonthlyPrice = (userId) => {
    const membership = memberships[userId];
    if (profiles[userId]?.legacy_member && membership?.customMonthlyPriceEur != null) {
      const customPrice = Number(membership.customMonthlyPriceEur);
      if (Number.isFinite(customPrice) && customPrice >= 0) return customPrice;
    }
    const plan = getMembershipPlanForUser(userId);
    return Number(plan?.price) || 0;
  };

  const getMembershipMinimumMonths = (userId) => {
    const plan = getMembershipPlanForUser(userId);
    return Math.max(1, Number(plan?.minimumMonths) || 1);
  };

  const getMembershipMinimumTermEndDate = (userId) => {
    const startedAt = memberships[userId]?.startedAt;
    if (!startedAt) return "";
    const endDate = addMonthsClamped(startedAt, getMembershipMinimumMonths(userId));
    if (!endDate || Number.isNaN(endDate.getTime())) return "";
    return endDate.toLocaleDateString();
  };

  const canCancelMembershipNow = (userId) => {
    const startedAt = memberships[userId]?.startedAt;
    if (!startedAt) return true;
    const minimumTermEnd = addMonthsClamped(startedAt, getMembershipMinimumMonths(userId));
    if (!minimumTermEnd || Number.isNaN(minimumTermEnd.getTime())) return true;
    return new Date() >= minimumTermEnd;
  };

  const getLegacyMembershipDraft = (userId) => {
    const membership = memberships[userId] || {};
    return (
      legacyMembershipDrafts[userId] || {
        enabled: Boolean(profiles[userId]?.legacy_member),
        endDate: membership.cancelledAt ? String(membership.cancelledAt).slice(0, 10) : "",
        creditsPerMonth:
          membership.customCreditsPerMonth != null && membership.customCreditsPerMonth !== ""
            ? String(membership.customCreditsPerMonth)
            : "",
        monthlyPriceEur:
          membership.customMonthlyPriceEur != null && membership.customMonthlyPriceEur !== ""
            ? String(membership.customMonthlyPriceEur)
            : "",
      }
    );
  };

  const getLegacyMigrationStatus = (userId) => {
    const profile = profiles[userId] || {};
    const membership = memberships[userId] || null;
    if (!profile.legacy_member) return { kind: "none", label: "" };
    if (!membership || membership.status !== "active") {
      return { kind: "needs_membership", label: "Legacy member: activate membership" };
    }
    if (membership.customCreditsPerMonth == null || membership.customMonthlyPriceEur == null) {
      return { kind: "needs_settings", label: "Legacy member: add custom amount + credits" };
    }
    const subStatus = String(profile.mollie_subscription_status || "").toLowerCase();
    if (profile.mollie_subscription_id && ["active", "pending", "authorized"].includes(subStatus)) {
      return { kind: "active", label: "Legacy member: auto-billing active" };
    }
    return { kind: "ready", label: "Legacy member: payment setup still incomplete" };
  };
  const getReadyLegacyReminderUserIds = () => {
    return Object.keys(profiles).filter(
      (userId) => getLegacyMigrationStatus(userId).kind === "ready"
    );
  };
  const getLegacyFirstPaymentConfirmation = (userId) => {
    return (
      auditLog.find(
        (entry) =>
          entry.action === "MEMBERSHIP_SUBSCRIPTION_CREATED_SERVER" &&
          entry.details?.userId === userId
      ) || null
    );
  };

  const setLegacyMembershipDraftField = (userId, field, value) => {
    setLegacyMembershipDrafts((prev) => ({
      ...prev,
      [userId]: {
        ...getLegacyMembershipDraft(userId),
        [field]: value,
      },
    }));
  };

  const getMembershipRemaining = (userId) => {
    const membership = memberships[userId];
    const isActiveNow =
      membership?.status === "active" &&
      (!membership?.cancelledAt || new Date(membership.cancelledAt) > new Date());
    if (!membership || !isActiveNow) return 0;
    const creditsPerMonth = getMembershipCreditsPerMonth(userId);
    if (creditsPerMonth <= 0) return 0;
    const period = new Date().toISOString().slice(0, 7);
    const used = membershipUsage[userId]?.[period] || 0;
    return Math.max(0, creditsPerMonth - used);
  };

  const getMembershipUsed = (userId) => {
    const period = new Date().toISOString().slice(0, 7);
    return membershipUsage[userId]?.[period] || 0;
  };

  const getNextRenewalDate = (userId) => {
    const membership = memberships[userId];
    const isActiveNow =
      membership?.status === "active" &&
      (!membership?.cancelledAt || new Date(membership.cancelledAt) > new Date());
    if (!membership || !isActiveNow) return "";
    if (membership.cancelledAt && new Date(membership.cancelledAt) > new Date()) {
      return new Date(membership.cancelledAt).toLocaleDateString();
    }
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return next.toLocaleDateString();
  };

  const isMembershipActiveNow = (userId) => {
    const membership = memberships[userId];
    if (!membership || membership.status !== "active") return false;
    if (!membership.cancelledAt) return true;
    return new Date(membership.cancelledAt) > new Date();
  };

  const isMembershipCancellationScheduled = (userId) => {
    const membership = memberships[userId];
    return Boolean(
      membership?.status === "active" &&
        membership?.cancelledAt &&
        new Date(membership.cancelledAt) > new Date()
    );
  };

  const getAvailableCredits = (userId) =>
    getPurchasedBalance(userId) + getMembershipRemaining(userId);

  const filteredUserIds = Object.keys(profiles)
    .filter((userId) => {
      const term = userSearch.trim().toLowerCase();
      if (term) {
        const profile = profiles[userId];
        const email = String(profile?.email || userId).toLowerCase();
        const name = String(profile?.name || "").toLowerCase();
        if (!email.includes(term) && !name.includes(term)) return false;
      }

      if (userSortMode === "members_only") {
        return isMembershipActiveNow(userId);
      }

      return true;
    })
    .sort((leftUserId, rightUserId) => {
      if (userSortMode === "signup_desc") {
        const leftSignupTime = Date.parse(profiles[leftUserId]?.created_at || "") || 0;
        const rightSignupTime = Date.parse(profiles[rightUserId]?.created_at || "") || 0;
        if (rightSignupTime !== leftSignupTime) return rightSignupTime - leftSignupTime;
      }

      if (userSortMode === "credits_desc") {
        const creditDelta = getAvailableCredits(rightUserId) - getAvailableCredits(leftUserId);
        if (creditDelta !== 0) return creditDelta;
      }

      const leftEmail = String(profiles[leftUserId]?.email || leftUserId).toLowerCase();
      const rightEmail = String(profiles[rightUserId]?.email || rightUserId).toLowerCase();
      return leftEmail.localeCompare(rightEmail);
    });
  const adminAddBookedUserIds = useMemo(() => {
    if (!adminAddClass?.id) return new Set();
    return new Set(
      bookings
        .filter((booking) => booking.classId === adminAddClass.id && booking.status === "booked")
        .map((booking) => booking.userId)
    );
  }, [adminAddClass?.id, bookings]);
  const adminAddSearchResults = useMemo(() => {
    const term = adminAddSearchTerm.trim().toLowerCase();
    if (!adminAddClass?.id || term.length < 2) return [];
    return Object.entries(profiles)
      .filter(([profileUserId, profile]) => {
        if (profileUserId === user?.id) return false;
        if (profile?.role === "admin") return false;
        const name = String(profile?.name || "").toLowerCase();
        const email = String(profile?.email || profileUserId).toLowerCase();
        return name.includes(term) || email.includes(term);
      })
      .sort(([, left], [, right]) =>
        String(left?.email || left?.name || "").localeCompare(
          String(right?.email || right?.name || ""),
          undefined,
          { sensitivity: "base" }
        )
      )
      .slice(0, 20)
      .map(([profileUserId, profile]) => ({
        userId: profileUserId,
        profile,
        isBooked: adminAddBookedUserIds.has(profileUserId),
      }));
  }, [adminAddBookedUserIds, adminAddClass?.id, adminAddSearchTerm, profiles, user?.id]);

  const addCreditEntry = async ({ userId, delta, reason, expiresAt }) => {
    const { data, error } = await supabase
      .from("credit_ledger")
      .insert({
        user_id: userId,
        delta,
        reason,
        expires_at: expiresAt || null,
      })
      .select("*")
      .single();

    if (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return;
    }
    if (data) {
      setCreditLedger((prev) => [
        {
          id: data.id,
          userId: data.user_id,
          delta: data.delta,
          reason: data.reason,
          expiresAt: data.expires_at,
          timestamp: data.created_at,
        },
        ...prev,
      ]);
    }
  };

  const addAmigaTicketEntry = async ({ userId, delta, reason, expiresAt }) => {
    const { data, error } = await supabase
      .from("amiga_ticket_ledger")
      .insert({
        user_id: userId,
        delta,
        reason,
        expires_at: expiresAt || null,
      })
      .select("*")
      .single();

    if (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return;
    }
    if (data) {
      setAmigaTicketLedger((prev) => [
        {
          id: data.id,
          userId: data.user_id,
          delta: data.delta,
          reason: data.reason,
          expiresAt: data.expires_at,
          timestamp: data.created_at,
        },
        ...prev,
      ]);
    }
  };

  const enqueueNotification = ({ type, userId, classId, meta }) => {
    const optimistic = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type,
      userId,
      classId,
      meta,
      createdAt: new Date().toISOString(),
      status: "pending",
    };
    setNotificationQueue((prev) => [optimistic, ...prev]);

    supabase
      .from("notifications")
      .insert({
        type,
        user_id: userId,
        class_id: classId,
        meta,
        status: "pending",
      })
      .then(() => {})
      .catch(() => {});
  };

  const getAuthHeaders = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    let activeSession = session || null;
    if (activeSession?.expires_at && activeSession.expires_at * 1000 < Date.now() + 30_000) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      activeSession = refreshed?.session || activeSession;
    }
    return activeSession?.access_token
      ? { Authorization: `Bearer ${activeSession.access_token}` }
      : undefined;
  };

  const sendTransactionalEmail = async ({ templateKey, userId, data }) => {
    if (!templateKey || !userId) return;
    try {
      const authHeaders = await getAuthHeaders();
      // eslint-disable-next-line no-console
      console.log("Calling send-transactional-email with:", { templateKey, userId, authHeaders, data });
      const { error, data: responseData } = await supabase.functions.invoke("send-transactional-email", {
        headers: authHeaders,
        body: {
          templateKey,
          userId,
          data,
        },
      });
      if (error) {
        // eslint-disable-next-line no-console
        console.error("Email send error:", error, "Response:", responseData);
        throw new Error(error);
      }
      // eslint-disable-next-line no-console
      console.log("Email sent successfully:", { templateKey, userId, responseData });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Email function error:", error);
      throw error;
    }
  };

  const sendAttentionCall = async (cls) => {
    if (!cls?.id) return;
    try {
      const authHeaders = await getAuthHeaders();
      const { data, error } = await supabase.functions.invoke("send-attention-call", {
        headers: authHeaders,
        body: { classId: cls.id },
      });
      if (error || data?.error) {
        // eslint-disable-next-line no-console
        console.error(error || data?.error);
        alert(error?.message || data?.error || "Failed to send attention call.");
        return;
      }
      alert(`Attention call sent to ${data?.sent ?? 0} people.`);
      logAction({
        actor: user.email,
        action: "ATTENTION_CALL_SENT",
        details: { classId: cls.id, name: cls.name, date: cls.date },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      alert(error?.message || "Failed to send attention call.");
    }
  };

  const submitHelpRequest = async () => {
    const question = String(helpQuestionDraft || "").trim();
    if (!question) {
      setHelpRequestStatus("Please write your question first.");
      return;
    }
    setHelpRequestStatus("");
    setHelpRequestSending(true);
    try {
      const authHeaders = await getAuthHeaders();
      const { data, error } = await supabase.functions.invoke("send-help-request", {
        headers: authHeaders,
        body: {
          question,
          currentView: view,
        },
      });
      if (error || data?.error) {
        setHelpRequestStatus(
          error?.message || data?.error || "Could not send your question. Please try again."
        );
        return;
      }
      setHelpRequestStatus("Your question has been sent. We will get back to you soon.");
      setHelpQuestionDraft("");
    } catch (err) {
      setHelpRequestStatus(err?.message || "Could not send your question. Please try again.");
    } finally {
      setHelpRequestSending(false);
    }
  };

  const autoPromoteWaitlist = async (classId) => {
    if (!classId) return;
    try {
      const authHeaders = await getAuthHeaders();
      const { error } = await supabase.functions.invoke("waitlist-promote", {
        headers: authHeaders,
        body: { classId },
      });
      if (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const getClassEmailData = (classId) => {
    const cls = classes.find((item) => item.id === classId);
    if (!cls) return {};
    return {
      class_name: cls.name,
      class_date: cls.date,
      class_time: cls.time,
      class_location: cls.location || "",
      class_teacher: cls.teacher || "",
    };
  };

  const creditPackages = useMemo(() => {
    const amounts = [1, 10];
    return amounts.map((amount) => ({
      label: `${amount} credits`,
      amount,
      price:
        amount === 10
          ? formatEuro(creditPack10Price)
          : formatEuro(creditPricePerUnit * amount),
    }));
  }, [creditPricePerUnit, creditPack10Price]);

  const amigaTicketPack = useMemo(
    () => ({
      label: "Amiga ticket",
      amount: 1,
      price: formatEuro(amigaTicketPricePerUnit),
    }),
    [amigaTicketPricePerUnit]
  );

  useEffect(() => {
    if (!publicMembershipPlanId && membershipPlans[0]?.id) {
      setPublicMembershipPlanId(membershipPlans[0].id);
    }
  }, [publicMembershipPlanId, membershipPlans]);

  const cancelPolicyOptions = [1, 4, 24];

  const promptAuthForCheckout = (intent) => {
    setPendingCheckoutIntent(intent);
    savePendingCheckoutIntent(intent);
    setPublicView("login");
    setAuthMode("register");
    setAuthError("");
    setAuthNotice(
      `Create an account or log in first. Mollie payment for ${describeCheckoutIntent(
        intent,
        membershipPlans
      )} comes in the next step.`
    );
  };

  const purchaseCredits = async (pack) => {
    if (!user?.id) {
      promptAuthForCheckout({ kind: "credits", amount: pack.amount });
      return;
    }
    setCheckoutError("");
    setMembershipCheckoutError("");
    setMembershipCheckoutStatus("");
    setCheckoutLoading(true);
    logAction({
      actor: user.email,
      action: "PURCHASE_CREDITS_INITIATED",
      details: { amount: pack.amount, price: pack.price, currency: "eur" },
    });
    try {
      // Ready for Mollie: backend should create a payment and return { url }
      const { data, error } = await supabase.functions.invoke("mollie-create-payment", {
        body: { credits: pack.amount },
      });
      if (error) throw error;
      if (!data?.url) throw new Error("Missing checkout URL");
      window.location.href = data.url;
    } catch (err) {
    setCheckoutError("Payment is not available yet. Please try again later.");
    logAction({
      actor: user.email,
      action: "PURCHASE_CREDITS_FAILED",
      details: { amount: pack.amount },
    });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const purchaseAmigaTicket = async () => {
    if (!user?.id) {
      promptAuthForCheckout({ kind: "amiga_ticket", amount: 1 });
      return;
    }
    setCheckoutError("");
    setMembershipCheckoutError("");
    setMembershipCheckoutStatus("");
    setCheckoutLoading(true);
    logAction({
      actor: user.email,
      action: "PURCHASE_AMIGA_TICKET_INITIATED",
      details: { amount: 1, price: amigaTicketPack.price, currency: "eur" },
    });
    try {
      // Ready for Mollie: backend should create a payment and return { url }
      const { data, error } = await supabase.functions.invoke("mollie-create-amiga-ticket", {
        body: { tickets: 1 },
      });
      if (error) throw error;
      if (!data?.url) throw new Error("Missing checkout URL");
      window.location.href = data.url;
    } catch (err) {
      setCheckoutError("Payment is not available yet. Please try again later.");
      logAction({
        actor: user.email,
        action: "PURCHASE_AMIGA_TICKET_FAILED",
        details: { amount: 1 },
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const subscribeMembership = async (plan, options = {}) => {
    if (!plan?.id) return;
    const requireLegacySetup = Boolean(options?.requireLegacySetup);
    if (!user?.id) {
      if (requireLegacySetup) {
        setMembershipCheckoutError(
          "This legacy payment link is only available for accounts with configured legacy settings."
        );
        return;
      }
      if (!membershipAcceptedTerms) {
        setMembershipCheckoutError(
          "Please accept the membership terms and privacy terms before continuing."
        );
        setPublicView("prices");
        return;
      }
      promptAuthForCheckout({ kind: "membership", planId: plan.id });
      return;
    }
    setMembershipCheckoutError("");
    setMembershipCheckoutStatus("");
    setCheckoutError("");
    if (requireLegacySetup) {
      const legacyStatus = getLegacyMigrationStatus(user.id);
      if (legacyStatus.kind !== "ready") {
        setMembershipCheckoutError(
          profiles[user.id]?.legacy_member
            ? "No legacy payment settings are configured for this account yet."
            : "This account does not have legacy membership settings configured."
        );
        return;
      }
    }
    if (!membershipAcceptedTerms) {
      setMembershipCheckoutError(
        "Please accept the membership terms and privacy terms before continuing."
      );
      return;
    }
    setCheckoutLoading(true);
    logAction({
      actor: user.email,
      action: "MEMBERSHIP_SUBSCRIBE_INITIATED",
      details: { planId: plan.id, price: plan.price, currency: "eur" },
    });
    try {
      // Ready for Mollie: backend should create a subscription checkout and return { url }
      const { data, error } = await supabase.functions.invoke("mollie-create-subscription", {
        body: { planId: plan.id },
      });
      if (error) throw error;
      if (!data?.url) throw new Error("Missing checkout URL");
      window.location.href = data.url;
    } catch (err) {
    setMembershipCheckoutError("Membership checkout is not available yet. Please try again later.");
    logAction({
      actor: user.email,
      action: "MEMBERSHIP_SUBSCRIBE_FAILED",
      details: { planId: plan.id },
    });
    } finally {
      setCheckoutLoading(false);
    }
  };

  useEffect(() => {
    purchaseCreditsRef.current = purchaseCredits;
    purchaseAmigaTicketRef.current = purchaseAmigaTicket;
    subscribeMembershipRef.current = subscribeMembership;
  });


  const cancelMembershipAtRenewal = async () => {
    if (!user?.id) return;
    const membership = memberships[user.id];
    if (!membership || membership.status !== "active") return;
    if (membership.cancelledAt && new Date(membership.cancelledAt) > new Date()) {
      setMembershipCheckoutStatus(
        `Cancellation already scheduled for ${new Date(membership.cancelledAt).toLocaleDateString()}.`
      );
      return;
    }
    if (!canCancelMembershipNow(user.id)) {
      const minimumTermEndDate = getMembershipMinimumTermEndDate(user.id);
      setMembershipCheckoutError("");
      setMembershipCheckoutStatus(
        `Your minimum membership term ends on ${minimumTermEndDate || "the next eligible date"}.`
      );
      return;
    }
    setMembershipCheckoutError("");
    setMembershipCheckoutStatus("");
    setCheckoutLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("mollie-cancel-subscription", {
        body: {},
      });
      if (error) throw error;
      const effectiveAt = data?.effectiveAt
        ? new Date(data.effectiveAt)
        : new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1);
      await loadFinancialData();
      setMembershipCheckoutStatus(
        `Subscription will end on ${effectiveAt.toLocaleDateString()}. You can keep using membership credits until then.`
      );
    } catch (err) {
      if (err?.context?.earliestCancellationAt) {
        setMembershipCheckoutStatus(
          `Your minimum membership term ends on ${new Date(
            err.context.earliestCancellationAt
          ).toLocaleDateString()}.`
        );
        return;
      }
      setMembershipCheckoutError(
        err?.message || "Could not schedule cancellation. Please try again later."
      );
    } finally {
      setCheckoutLoading(false);
    }
  };

  const setUserMembership = async ({ userId, planId }) => {
    if (!planId) return;
    setMembershipActionStatus((prev) => ({
      ...prev,
      [userId]: "Saving membership...",
    }));

    let resolvedPlanId = planId;
    if (!isUuid(planId)) {
      const selectedPlanName =
        membershipPlans.find((p) => p.id === planId)?.name || String(planId);
      let { data: mappedPlan, error: mappedPlanError } = await supabase
        .from("membership_plans")
        .select("id,name")
        .ilike("name", selectedPlanName)
        .maybeSingle();

      if ((!mappedPlan?.id || mappedPlanError) && !mappedPlanError) {
        const sourcePlan = membershipPlans.find(
          (p) => p.id === planId || p.name.toLowerCase() === selectedPlanName.toLowerCase()
        );
        if (sourcePlan) {
          const { data: createdPlan, error: createPlanError } = await supabase
            .from("membership_plans")
            .insert({
              name: sourcePlan.name,
              price_eur: Number(sourcePlan.price) || 0,
              credits_per_month: Number(sourcePlan.creditsPerMonth) || 0,
              minimum_months: Math.max(1, Number(sourcePlan.minimumMonths) || 1),
            })
            .select("id,name")
            .single();
          if (!createPlanError && createdPlan?.id) {
            mappedPlan = createdPlan;
            mappedPlanError = null;
            await loadFinancialData();
          }
        }
      }

      if (mappedPlanError || !mappedPlan?.id) {
        setMembershipActionStatus((prev) => ({
          ...prev,
          [userId]: `Failed: ${
            mappedPlanError?.message || "membership plan is not synced in database."
          }`,
        }));
        return;
      }
      resolvedPlanId = mappedPlan.id;
    }

    const previousPlanId = memberships[userId]?.planId;
    const legacyDraft = getLegacyMembershipDraft(userId);
    const isLegacyMember = Boolean(profiles[userId]?.legacy_member);
    const parsedLegacyCredits = Number.parseInt(String(legacyDraft.creditsPerMonth || "").trim(), 10);
    const parsedLegacyPrice = Number.parseFloat(
      String(legacyDraft.monthlyPriceEur || "").trim().replace(",", ".")
    );
    const legacyCancelledAt = legacyDraft.endDate
      ? `${legacyDraft.endDate}T23:59:59.999Z`
      : null;
    const payload = {
      user_id: userId,
      plan_id: resolvedPlanId,
      status: "active",
      started_at: new Date().toISOString(),
      cancelled_at: isLegacyMember ? legacyCancelledAt : null,
      custom_credits_per_month:
        isLegacyMember && Number.isFinite(parsedLegacyCredits) && parsedLegacyCredits >= 0
          ? parsedLegacyCredits
          : null,
      custom_monthly_price_eur:
        isLegacyMember && Number.isFinite(parsedLegacyPrice) && parsedLegacyPrice >= 0
          ? Number(parsedLegacyPrice.toFixed(2))
          : null,
    };

    let error = null;

    // eslint-disable-next-line no-console
    console.log("setUserMembership debug:", {
      effectiveIsAdmin,
      isAdmin,
      canUseDevTools,
      devToolsUnlocked,
      viewingAsNormalAccount,
      userEmail: user?.email,
      userId,
      planId: resolvedPlanId,
    });

    // If viewing as admin via the dev-tools toggle, perform the membership change
    // via the server-side admin function so row-level security is respected.
    if (effectiveIsAdmin) {
      try {
        const authHeaders = await getAuthHeaders();
        const { data, error: invokeError } = await supabase.functions.invoke("admin-mobile-action", {
          headers: authHeaders,
          body: {
            action: "set_membership",
            payload: {
              userId,
              planId: resolvedPlanId,
              status: payload.status,
              startedAt: payload.started_at,
              cancelledAt: payload.cancelled_at,
              customCreditsPerMonth: payload.custom_credits_per_month,
              customMonthlyPriceEur: payload.custom_monthly_price_eur,
            },
          },
        });
        // eslint-disable-next-line no-console
        console.log("admin-mobile-action response:", { data, invokeError });
        if (invokeError || data?.error) {
          error = invokeError || new Error(data?.error || "Admin action failed.");
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("admin-mobile-action error:", e);
        error = e;
      }
    } else {
      // eslint-disable-next-line no-console
      console.log("Not effectiveIsAdmin, trying direct upsert. isAdmin:", isAdmin);
      let upsertResponse = await supabase
        .from("memberships")
        .upsert(payload, { onConflict: "user_id" });
      error = upsertResponse.error;
      if (error) {
        // eslint-disable-next-line no-console
        console.error("Direct upsert failed:", error);
      }
    }

    // Fallback when user_id is not unique and upsert cannot run.
    if (
      error?.message?.toLowerCase().includes("on conflict") ||
      error?.message?.toLowerCase().includes("constraint")
    ) {
      const { data: existing, error: selectError } = await supabase
        .from("memberships")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();
      if (selectError) {
        error = selectError;
      } else if (existing?.id) {
        const updateRes = await supabase
          .from("memberships")
          .update(payload)
          .eq("id", existing.id);
        error = updateRes.error;
      } else {
        const insertRes = await supabase.from("memberships").insert(payload);
        error = insertRes.error;
      }
    }

    if (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setMembershipActionStatus((prev) => ({
        ...prev,
        [userId]: `Failed: ${error.message || "Could not activate membership."}`,
      }));
      return;
    }
    const period = new Date().toISOString().slice(0, 7);
    await updateMembershipUsage(userId, -getMembershipUsed(userId));
    if (previousPlanId && previousPlanId !== planId) {
      logAction({
        actor: profiles[userId]?.email || userId,
        action: "MEMBERSHIP_PLAN_CHANGED",
        details: { from: previousPlanId, to: resolvedPlanId },
      });
    } else {
      logAction({
        actor: profiles[userId]?.email || userId,
      action: "MEMBERSHIP_ACTIVATED",
      details: {
        planId: resolvedPlanId,
        legacyMember: isLegacyMember,
        customCreditsPerMonth: payload.custom_credits_per_month,
        customMonthlyPriceEur: payload.custom_monthly_price_eur,
        endDate: legacyDraft.endDate || null,
      },
    });
    }
    logAction({
      actor: "system",
      action: "MEMBERSHIP_CREDITS_GRANTED",
      details: { userId, planId: resolvedPlanId, period },
    });
    await loadFinancialData();
    setMembershipActionStatus((prev) => ({
      ...prev,
      [userId]: "Membership activated.",
    }));
  };

  const toggleLegacyMemberTag = async (userId, enabled) => {
    setMembershipActionStatus((prev) => ({
      ...prev,
      [userId]: enabled ? "Enabling legacy member tag..." : "Removing legacy member tag...",
    }));
    const authHeaders = await getAuthHeaders();
    const { data, error } = await supabase.functions.invoke("admin-mobile-action", {
      headers: authHeaders,
      body: {
        action: "update_legacy_member_tag",
        payload: { userId, enabled: Boolean(enabled) },
      },
    });
    if (error || data?.error) {
      const message = formatInvokeError(error, data);
      setMembershipActionStatus((prev) => ({
        ...prev,
        [userId]: `Failed: ${message}`,
      }));
      return;
    }
    const refreshedProfile = data?.profile || null;
    if (Boolean(refreshedProfile.legacy_member) !== Boolean(enabled)) {
      setMembershipActionStatus((prev) => ({
        ...prev,
        [userId]: `Failed: Legacy member tag is still ${refreshedProfile.legacy_member ? "enabled" : "disabled"} in the database.`,
      }));
      return;
    }

    if (!enabled) {
      await supabase
        .from("memberships")
        .update({
          custom_credits_per_month: null,
          custom_monthly_price_eur: null,
        })
        .eq("user_id", userId);
    }
    setProfiles((prev) => ({
      ...prev,
      [refreshedProfile.id]: { ...(prev[refreshedProfile.id] || {}), ...refreshedProfile },
    }));
    setLegacyMembershipDraftField(userId, "enabled", Boolean(enabled));
    if (!enabled) {
      setLegacyMembershipDrafts((prev) => ({
        ...prev,
        [userId]: {
          ...getLegacyMembershipDraft(userId),
          enabled: false,
          endDate: "",
          creditsPerMonth: "",
          monthlyPriceEur: "",
        },
      }));
    }
    setMembershipActionStatus((prev) => ({
      ...prev,
      [userId]: enabled ? "Legacy member tag enabled." : "Legacy member tag removed.",
    }));
  };

  const deleteUserAccount = async (userId) => {
    const profile = profiles[userId];
    const email = profile?.email || userId;
    const confirmed = window.confirm(
      `Delete ${email} and all linked app data? This is intended for test accounts and cannot be undone.`
    );
    if (!confirmed) return;

    setUserDeleteLoading((prev) => ({ ...prev, [userId]: true }));
    setMembershipActionStatus((prev) => ({
      ...prev,
      [userId]: "Deleting user account...",
    }));
    try {
      const authHeaders = await getAuthHeaders();
      const { data, error } = await supabase.functions.invoke("admin-mobile-action", {
        headers: authHeaders,
        body: {
          action: "delete_user_account",
          payload: { userId },
        },
      });
      if (error || data?.error) {
        setMembershipActionStatus((prev) => ({
          ...prev,
          [userId]: `Failed: ${formatInvokeError(error, data)}`,
        }));
        return;
      }

      setProfiles((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      setMemberships((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      setMembershipUsage((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      setCreditLedger((prev) => prev.filter((entry) => entry.userId !== userId));
      setAmigaTicketLedger((prev) => prev.filter((entry) => entry.userId !== userId));
      setMembershipActionStatus((prev) => ({
        ...prev,
        [userId]: `Deleted ${email}.`,
      }));
      logAction({
        actor: user.email,
        action: "USER_ACCOUNT_DELETED",
        details: { targetUser: email, userId },
      });
    } catch (error) {
      setMembershipActionStatus((prev) => ({
        ...prev,
        [userId]: `Failed: ${error?.message || "Could not delete user."}`,
      }));
    } finally {
      setUserDeleteLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const saveLegacyMembershipOverrides = async (userId) => {
    const membership = memberships[userId];
    if (!membership?.planId) {
      setMembershipActionStatus((prev) => ({
        ...prev,
        [userId]: "Activate a membership first, then save legacy settings.",
      }));
      return;
    }
    const draft = getLegacyMembershipDraft(userId);
    const parsedCredits = Number.parseInt(String(draft.creditsPerMonth || "").trim(), 10);
    const parsedPrice = Number.parseFloat(String(draft.monthlyPriceEur || "").trim().replace(",", "."));
    const payload = {
      cancelled_at: draft.endDate ? `${draft.endDate}T23:59:59.999Z` : null,
      custom_credits_per_month:
        Number.isFinite(parsedCredits) && parsedCredits >= 0 ? parsedCredits : null,
      custom_monthly_price_eur:
        Number.isFinite(parsedPrice) && parsedPrice >= 0 ? Number(parsedPrice.toFixed(2)) : null,
    };
    setMembershipActionStatus((prev) => ({ ...prev, [userId]: "Saving legacy membership settings..." }));
    const { error } = await supabase.from("memberships").update(payload).eq("user_id", userId);
    if (error) {
      setMembershipActionStatus((prev) => ({
        ...prev,
        [userId]: `Failed: ${error.message || "Could not save legacy membership settings."}`,
      }));
      return;
    }
    await loadFinancialData();
    setMembershipActionStatus((prev) => ({
      ...prev,
      [userId]: "Legacy membership settings saved.",
    }));
  };

  const sendLegacyMembershipReminder = async (userId, options = {}) => {
    const { silent = false } = options;
    setLegacyReminderSending((prev) => ({ ...prev, [userId]: true }));
    setMembershipActionStatus((prev) => ({ ...prev, [userId]: "Sending legacy payment reminder..." }));
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(
        `${supabaseUrl}/functions/v1/send-legacy-membership-reminder`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: supabaseAnonKey,
            ...(authHeaders || {}),
          },
          body: JSON.stringify({ userId }),
        }
      );
      const raw = await response.text();
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        data = raw || null;
      }
      if (!response.ok || data?.error) {
        const message =
          typeof data === "string" && data.trim()
            ? data
            : formatInvokeError({ message: raw || response.statusText }, data);
        setMembershipActionStatus((prev) => ({
          ...prev,
          [userId]: `Failed: ${message}`,
        }));
        return { ok: false, error: message, data };
      }
      setMembershipActionStatus((prev) => ({
        ...prev,
        [userId]: `Legacy payment reminder sent to ${data?.sentTo || profiles[userId]?.email || userId}.`,
      }));
      if (!silent) {
        logAction({
          actor: user.email,
          action: "LEGACY_MEMBERSHIP_REMINDER_SENT",
          details: { targetUser: profiles[userId]?.email || userId, resendId: data?.resendId || null },
        });
      }
      return { ok: true, data };
    } catch (err) {
      const message = formatInvokeError(err);
      setMembershipActionStatus((prev) => ({
        ...prev,
        [userId]: `Failed: ${message}`,
      }));
      return { ok: false, error: message };
    } finally {
      setLegacyReminderSending((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const createMembershipRetryPaymentUrl = async (entry, targetUserId = null) => {
    if (!entry?.id || !entry?.period) {
      throw new Error("Missing payment history reference");
    }
    const authHeaders = await getAuthHeaders();
    const payload = {
      paymentHistoryId: entry.id,
      period: entry.period,
      ...(targetUserId ? { targetUserId } : {}),
    };
    const { data, error } = await supabase.functions.invoke("membership-retry-payment", {
      headers: authHeaders,
      body: payload,
    });
    if (error || !data?.url) {
      throw new Error(formatInvokeError(error, data));
    }
    return data.url;
  };

  const sendMembershipPaymentReminder = async (userId, entry) => {
    if (!userId || !entry) return;
    const key = `${userId}-${entry.period}`;
    setMembershipReminderSending((prev) => ({ ...prev, [key]: true }));
    setMembershipReminderSent((prev) => ({ ...prev, [key]: false }));
    setMembershipActionStatus((prev) => ({ ...prev, [userId]: "Sending payment reminder..." }));
    try {
      const authHeaders = await getAuthHeaders();
      const { data, error } = await supabase.functions.invoke("send-membership-payment-reminder", {
        headers: authHeaders,
        body: { paymentHistoryId: entry.id, userId },
      });
      if (error || data?.error) throw new Error(formatInvokeError(error, data));
      setMembershipActionStatus((prev) => ({
        ...prev,
        [userId]: `Reminder sent to ${data?.sentTo || profiles[userId]?.email || userId}`,
      }));
      setMembershipReminderSent((prev) => ({ ...prev, [key]: true }));
      logAction({ actor: user.email, action: "MEMBERSHIP_PAYMENT_REMINDER_SENT", details: { targetUser: profiles[userId]?.email || userId, period: entry.period } });
    } catch (err) {
      setMembershipActionStatus((prev) => ({ ...prev, [userId]: `Failed: ${String(err?.message || err)}` }));
    } finally {
      setMembershipReminderSending((prev) => ({ ...prev, [key]: false }));
    }
  };
  const sendAllLegacyMembershipReminders = async () => {
    const readyUserIds = getReadyLegacyReminderUserIds();
    if (readyUserIds.length === 0) {
      alert("No legacy members are ready for a payment reminder.");
      return;
    }

    setLegacyReminderBulkSending(true);
    let successCount = 0;
    let failedCount = 0;
    const failedMessages = [];

    try {
      for (const userId of readyUserIds) {
        const result = await sendLegacyMembershipReminder(userId, { silent: true });
        if (result?.ok) {
          successCount += 1;
        } else {
          failedCount += 1;
          failedMessages.push(
            `${profiles[userId]?.email || userId}: ${result?.error || "Could not send reminder."}`
          );
        }
      }

      logAction({
        actor: user.email,
        action: "LEGACY_MEMBERSHIP_REMINDER_SENT",
        details: {
          bulk: true,
          attemptedCount: readyUserIds.length,
          successCount,
          failedCount,
        },
      });

      alert(
        failedCount
          ? `Sent ${successCount} legacy payment reminders. ${failedCount} failed.\n\n${failedMessages.join("\n")}`
          : `Sent ${successCount} legacy payment reminders.`
      );
    } finally {
      setLegacyReminderBulkSending(false);
    }
  };

  const clearUserMembership = async (userId) => {
    setMembershipActionStatus((prev) => ({
      ...prev,
      [userId]: "Deactivating membership...",
    }));
    const membershipPlanName =
      membershipPlans.find((plan) => plan.id === memberships[userId]?.planId)?.name ||
      memberships[userId]?.planId ||
      "Membership";
    const { error } = await supabase
      .from("memberships")
      .update({ status: "cancelled", cancelled_at: new Date().toISOString() })
      .eq("user_id", userId);
    if (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setMembershipActionStatus((prev) => ({
        ...prev,
        [userId]: `Failed: ${error.message || "Could not deactivate membership."}`,
      }));
      return;
    }
    await loadFinancialData();
    logAction({
      actor: profiles[userId]?.email || userId,
      action: "MEMBERSHIP_CANCELLED",
      details: { userId },
    });
    sendTransactionalEmail({
      templateKey: "membership_ending",
      userId,
      data: {
        membership_name: membershipPlanName,
        membership_end_date: new Date().toLocaleDateString(),
        site_url: window.location.origin,
      },
    });
    setMembershipActionStatus((prev) => ({
      ...prev,
      [userId]: "Membership deactivated.",
    }));
  };

  const bookingsByClass = useMemo(() => {
    const counts = {};
    bookings.forEach((b) => {
      if (b.status !== "booked") return;
      counts[b.classId] = (counts[b.classId] || 0) + 1;
    });
    return counts;
  }, [bookings]);

  const amigaSeatBonusByClass = useMemo(() => {
    const counts = {};
    bookings.forEach((b) => {
      if (b.status !== "booked") return;
      if (["amiga_ticket", "admin_amiga_ticket"].includes(b.creditSource)) {
        counts[b.classId] = (counts[b.classId] || 0) + 1;
      }
    });
    return counts;
  }, [bookings]);

  const ghostSpotsByClass = useMemo(
    () =>
      classes.reduce((acc, cls) => {
        acc[cls.id] = Math.max(0, Number(cls.ghostSpots) || 0);
        return acc;
      }, {}),
    [classes]
  );

  const getBookedCount = (classId) =>
    (bookedCounts[classId] ?? (bookingsByClass[classId] || 0)) +
    (amigaSeatBonusByClass[classId] || 0);
  const getGhostSpotCount = (classId) => ghostSpotsByClass[classId] || 0;
  const getDisplayGhostSpotCount = (cls) => {
    const realSpotsLeft = Math.max(0, Number(cls?.spots || 0) - getBookedCount(cls?.id));
    const maxDisplayGhosts = realSpotsLeft > 0 ? realSpotsLeft - 1 : 0;
    return Math.min(getGhostSpotCount(cls?.id), maxDisplayGhosts);
  };
  const getDisplayBookedCount = (cls) => getBookedCount(cls?.id) + getDisplayGhostSpotCount(cls);
  const getDisplaySpotsLeft = (cls) => Math.max(0, Number(cls?.spots || 0) - getDisplayBookedCount(cls));
  const publicMembershipPlans = useMemo(
    () => dedupePublicMembershipPlans(membershipPlans),
    [membershipPlans]
  );
  useEffect(() => {
    if (
      publicMembershipPlans.length > 0 &&
      !publicMembershipPlans.some((plan) => plan.id === publicMembershipPlanId)
    ) {
      setPublicMembershipPlanId(publicMembershipPlans[0].id);
    }
  }, [publicMembershipPlanId, publicMembershipPlans]);
  const adminVisibleMembershipPlanDrafts = useMemo(
    () =>
      dedupePublicMembershipPlans(
        membershipPlanDrafts.length ? membershipPlanDrafts : membershipPlans
      ),
    [membershipPlanDrafts, membershipPlans]
  );
  const resolveVisibleMembershipPlanId = (planId, visiblePlans) =>
    visiblePlans.some((plan) => plan.id === planId) ? planId : visiblePlans[0]?.id;
  const getUserBookings = (userId) =>
    bookings.filter((b) => b.userId === userId && b.status === "booked");
  const getAttendanceList = (classId) =>
    bookings
      .filter((b) => b.classId === classId && b.status === "booked")
      .flatMap((b) => {
        const name = getProfileDisplayName(profiles[b.userId], b.userId);
        if (["amiga_ticket", "admin_amiga_ticket"].includes(b.creditSource)) {
          return [
            {
              bookingId: b.id,
              userId: b.userId,
              creditSource: b.creditSource,
              removable: b.creditSource === "admin_amiga_ticket",
              name,
              tag: b.creditSource === "admin_amiga_ticket" ? "Admin Amiga ticket" : "Amiga ticket",
            },
            { name, tag: "Amiga guest" },
          ];
        }
        if (b.creditSource === "membership") {
          return [{ bookingId: b.id, userId: b.userId, creditSource: b.creditSource, name, tag: "Member" }];
        }
        if (b.creditSource === "admin") {
          return [
            {
              bookingId: b.id,
              userId: b.userId,
              creditSource: b.creditSource,
              removable: true,
              name,
              tag: "Admin add",
            },
          ];
        }
        return [{ bookingId: b.id, userId: b.userId, creditSource: b.creditSource, name, tag: null }];
      });

  const getClassStartDateTime = (cls) => {
    if (!cls?.date || !cls?.time) return null;
    const timePart = String(cls.time).split(" - ")[0].trim();
    const [hh, mm] = timePart.split(":").map(Number);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    const d = new Date(cls.date);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(hh, mm, 0, 0);
    return d;
  };

  const scrollToAdminForm = () => {
    setTimeout(() => {
      if (!adminFormRef.current) return;

      const y =
        adminFormRef.current.getBoundingClientRect().top +
        window.pageYOffset -
        80; // extra offset

      window.scrollTo({
        top: y,
        behavior: "smooth",
      });
    }, 0);
  };

  const goToPreviousMonth = () => {
  const [year, month] = selectedMonth.split("-").map(Number);
  const newMonth = month === 1 ? 12 : month - 1;
  const newYear = month === 1 ? year - 1 : year;
  setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, "0")}`);
};

const goToNextMonth = () => {
  const [year, month] = selectedMonth.split("-").map(Number);
  const newMonth = month === 12 ? 1 : month + 1;
  const newYear = month === 12 ? year + 1 : year;
  setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, "0")}`);
};

  const goToPreviousWeek = () => {
    const d = new Date(selectedDate);
    if (Number.isNaN(d.getTime())) return;
    d.setDate(d.getDate() - 7);
    setSelectedDate(toISODate(d));
  };

  const goToNextWeek = () => {
    const d = new Date(selectedDate);
    if (Number.isNaN(d.getTime())) return;
    d.setDate(d.getDate() + 7);
    setSelectedDate(toISODate(d));
  };

  /* admin state */
  const [adminDate, setAdminDate] = useState(null);
  const [editingClass, setEditingClass] = useState(null);
  const [editSeries, setEditSeries] = useState(false);

  const adminFormRef = useRef(null);
  const weekScrollRef = useRef(null);
  const weekSuppressClickRef = useRef(false);
  const scheduleViewWasChangedRef = useRef(false);
  const purchaseCreditsRef = useRef(null);
  const purchaseAmigaTicketRef = useRef(null);
  const subscribeMembershipRef = useRef(null);
  const weekDragStateRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    dragging: false,
  });

  const handleWeekPointerDown = useCallback(
    (e) => {
      if (scheduleViewMode !== "week") return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      const shell = weekScrollRef.current;
      if (!shell) return;
      const target = e.target instanceof Element ? e.target : null;
      if (
        target?.closest(
          "button, a, input, select, textarea, label, [role='button'], .class-item"
        )
      ) {
        return;
      }
      const canScrollX = shell.scrollWidth - shell.clientWidth > 1;
      if (!canScrollX) return;
      weekDragStateRef.current = {
        active: true,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startScrollLeft: shell.scrollLeft,
        dragging: false,
      };
      if (shell.setPointerCapture) shell.setPointerCapture(e.pointerId);
    },
    [scheduleViewMode]
  );

  const handleWeekPointerMove = useCallback((e) => {
    const shell = weekScrollRef.current;
    const state = weekDragStateRef.current;
    if (!shell || !state.active || state.pointerId !== e.pointerId) return;

    const dx = e.clientX - state.startX;
    const dy = e.clientY - state.startY;
    if (!state.dragging) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      if (Math.abs(dx) <= Math.abs(dy)) return;
      state.dragging = true;
      weekSuppressClickRef.current = true;
      shell.classList.add("is-dragging");
    }
    shell.scrollLeft = state.startScrollLeft - dx;
    e.preventDefault();
  }, []);

  const handleWeekPointerEnd = useCallback((e) => {
    const shell = weekScrollRef.current;
    const state = weekDragStateRef.current;
    if (!state.active || state.pointerId !== e.pointerId) return;
    if (shell?.releasePointerCapture) {
      try {
        shell.releasePointerCapture(e.pointerId);
      } catch {
        // no-op when capture is already released
      }
    }
    shell?.classList.remove("is-dragging");
    weekDragStateRef.current = {
      active: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      startScrollLeft: 0,
      dragging: false,
    };
    if (weekSuppressClickRef.current) {
      window.setTimeout(() => {
        weekSuppressClickRef.current = false;
      }, 0);
    }
  }, []);

  const handleWeekClickCapture = useCallback(
    (e) => {
      if (scheduleViewMode !== "week") return;
      if (!weekSuppressClickRef.current) return;
      e.preventDefault();
      e.stopPropagation();
    },
    [scheduleViewMode]
  );

  const handleWeekWheel = useCallback(
    (e) => {
      if (scheduleViewMode !== "week") return;
      const shell = weekScrollRef.current;
      if (!shell) return;
      const canScrollX = shell.scrollWidth - shell.clientWidth > 1;
      if (!canScrollX) return;

      const horizontalDelta = Math.abs(e.deltaX) > 0 ? e.deltaX : e.shiftKey ? e.deltaY : 0;
      if (horizontalDelta === 0) return;

      shell.scrollLeft += horizontalDelta;
      e.preventDefault();
    },
    [scheduleViewMode]
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_SCHEDULE_MEDIA_QUERY);
    const syncScheduleViewport = (event) => {
      const matches = typeof event?.matches === "boolean" ? event.matches : mediaQuery.matches;
      setIsMobileScheduleViewport(matches);
      if (!scheduleViewWasChangedRef.current) {
        setScheduleViewMode(matches ? "week" : "month");
      }
    };

    syncScheduleViewport();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", syncScheduleViewport);
      return () => mediaQuery.removeEventListener("change", syncScheduleViewport);
    }

    mediaQuery.addListener(syncScheduleViewport);
    return () => mediaQuery.removeListener(syncScheduleViewport);
  }, []);

  const handleScheduleViewModeChange = useCallback((mode) => {
    scheduleViewWasChangedRef.current = true;
    setScheduleViewMode(mode);
  }, []);


  const [newClass, setNewClass] = useState({
    name: "",
    date: "",
    time: "",
    spots: "",
    location: "",
    teacher: "",
    color: "#f4c430",
    repeatWeeks: 1,
  });
  const [customTime, setCustomTime] = useState("");
  const [isCustomTime, setIsCustomTime] = useState(false);
  const [classTemplates, setClassTemplates] = useState([]);
  const [classTemplateStatus, setClassTemplateStatus] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const [locations, setLocations] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const locationMap = useMemo(
    () =>
      locations.reduce((acc, loc) => {
        acc[loc.name] = loc.id;
        return acc;
      }, {}),
    [locations]
  );
  const teacherMap = useMemo(
    () =>
      teachers.reduce((acc, teacher) => {
        acc[teacher.name] = teacher.id;
        return acc;
      }, {}),
    [teachers]
  );

  const timeSlots = [
    "17:00 - 18:00",
    "18:00 - 19:00",
    "19:00 - 20:00",
    "20:00 - 21:00",
  ];
  const classColorOptions = [
    { label: "Baile Yellow", value: "#f4c430" },
    { label: "Orange", value: "#f59e0b" },
    { label: "Teal", value: "#14b8a6" },
    { label: "Blue", value: "#3b82f6" },
    { label: "Violet", value: "#8b5cf6" },
    { label: "Rose", value: "#f43f5e" },
    { label: "Green", value: "#22c55e" },
    { label: "Slate", value: "#94a3b8" },
  ];

  const shiftClassDate = (days) => {
    const base = newClass.date || adminDate;
    if (!base) return;
    const d = new Date(base);
    if (Number.isNaN(d.getTime())) return;
    d.setDate(d.getDate() + days);
    setNewClass({ ...newClass, date: toISODate(d) });
  };

  const applyTemplate = (templateId) => {
    if (!templateId) return;
    const template = classTemplates.find((t) => t.id === templateId);
    if (!template) return;
    setNewClass((prev) => ({
      ...prev,
      name: template.name,
      time: template.time,
      spots: template.spots,
      location: template.location,
      teacher: template.teacher || "",
      color: template.color || "#f4c430",
    }));
    setIsCustomTime(template.time && !timeSlots.includes(template.time));
  };

  const saveTemplate = async () => {
    if (!newClass.name || !newClass.time || !newClass.spots || !newClass.location) return;
    if (!user?.id || !isAdmin) return;
    setClassTemplateStatus("");
    const templatePayload = {
      name: newClass.name,
      time: newClass.time,
      spots: Number(newClass.spots),
      location: newClass.location,
      teacher: newClass.teacher || "",
      color: newClass.color || "#f4c430",
      created_by: user.id,
    };
    const { data, error } = await supabase
      .from("class_templates")
      .insert(templatePayload)
      .select("*")
      .single();
    if (error || !data) {
      setClassTemplateStatus(error?.message || "Could not save template.");
      return;
    }
    const template = {
      id: data.id,
      name: data.name,
      time: data.time,
      spots: Number(data.spots) || 0,
      location: data.location,
      teacher: data.teacher || "",
      color: data.color || "#f4c430",
    };
    setClassTemplates((prev) => [template, ...prev]);
    setSelectedTemplateId(String(template.id));
    setClassTemplateStatus("Template saved.");
  };

  const removeTemplate = async (templateId) => {
    if (!templateId || !user?.id || !isAdmin) return;
    setClassTemplateStatus("");
    const { error } = await supabase.from("class_templates").delete().eq("id", templateId);
    if (error) {
      setClassTemplateStatus(error.message || "Could not remove template.");
      return;
    }
    setClassTemplates((prev) => prev.filter((t) => t.id !== templateId));
    if (selectedTemplateId === templateId) setSelectedTemplateId("");
    setClassTemplateStatus("Template removed.");
  };


  /* =========================
     Auth
  ========================= */
  useEffect(() => {
    let isMounted = true;
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    if (hashParams.get("type") === "recovery") {
      setIsPasswordRecovery(true);
      setView("account");
      setPasswordChangeStatus("Set your new password to finish account recovery.");
    }

    supabase.auth.getSession().then(({ data }) => {
      if (!isMounted) return;
      setUser(data.session?.user ?? null);
      if (!data.session?.user) return;
      const savedIntent = loadPendingCheckoutIntent();
      if (savedIntent) {
        setPendingCheckoutIntent((current) => current || savedIntent);
      }
    });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "PASSWORD_RECOVERY") {
          setIsPasswordRecovery(true);
          setView("account");
          setPasswordChangeStatus("Set your new password to finish account recovery.");
        }
        setUser(session?.user ?? null);
        if (session?.user) {
          const savedIntent = loadPendingCheckoutIntent();
          if (savedIntent) {
            setPendingCheckoutIntent((current) => current || savedIntent);
          }
        }
      }
    );

    return () => {
      isMounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  const ensureProfile = async (authUser) => {
    if (!authUser?.id) return;
    const metadataName = String(authUser?.user_metadata?.name || "").trim();
    const metadataBillingAddress = String(authUser?.user_metadata?.billing_address || "").trim();
    const { data: existing, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", authUser.id)
      .maybeSingle();

    if (!existing && !error) {
      const { data: created } = await supabase
        .from("profiles")
        .insert({
          id: authUser.id,
          email: authUser.email,
          role: "user",
          name: metadataName || authUser.email,
          billing_address: metadataBillingAddress || null,
        })
        .select("*")
        .single();
      if (created) {
        setProfiles((prev) => ({ ...prev, [created.id]: created }));
      }
      return;
    }

    if (existing) {
      const profilePatch = {};
      if (!existing.name && (metadataName || authUser.email)) {
        profilePatch.name = metadataName || authUser.email;
      }
      if (!existing.billing_address && metadataBillingAddress) {
        profilePatch.billing_address = metadataBillingAddress;
      }
      if (Object.keys(profilePatch).length > 0) {
        const { data: updatedName } = await supabase
          .from("profiles")
          .update(profilePatch)
          .eq("id", authUser.id)
          .select("*")
          .single();
        if (updatedName) {
          setProfiles((prev) => ({ ...prev, [updatedName.id]: updatedName }));
          return;
        }
      }
      setProfiles((prev) => ({ ...prev, [existing.id]: existing }));
    }
  };

  useEffect(() => {
    if (!user) {
      setProfiles({});
      // Reset dev tools viewing state when logging out
      setViewingAsNormalAccount(false);
      return;
    }
    // Reset dev tools viewing state when user changes to ensure admin shows as admin
    // and new users don't inherit the previous user's view mode
    setViewingAsNormalAccount(false);
    ensureProfile(user);
  }, [user]);

  const profileNameValue = profiles[user?.id]?.name || "";
  const profileBillingAddressValue = profiles[user?.id]?.billing_address || "";

  useEffect(() => {
    if (!user?.id) return;
    setProfileNameDraft(profileNameValue);
  }, [user?.id, profileNameValue]);

  useEffect(() => {
    if (!user?.id) return;
    const parts = parseBillingAddressParts(profileBillingAddressValue);
    setBillingAddressStreetDraft(parts.street);
    setBillingAddressPostcodeDraft(parts.postcode);
    setBillingAddressCityDraft(parts.city);
  }, [user?.id, profileBillingAddressValue]);

  const saveProfileName = async () => {
    if (!user?.id) return;
    const name = profileNameDraft.trim();
    if (!name) {
      setProfileNameStatus("Name is required for invoices.");
      return;
    }
    if (isLikelyEmail(name)) {
      setProfileNameStatus("Please enter your real name, not an email address.");
      return;
    }
    setProfileNameSaving(true);
    setProfileNameStatus("");
    const { data, error } = await supabase
      .from("profiles")
      .update({ name: name || null })
      .eq("id", user.id)
      .select("*")
      .single();
    setProfileNameSaving(false);
    if (error) {
      setProfileNameStatus("Could not save your name.");
      return;
    }
    if (data) {
      setProfiles((prev) => ({ ...prev, [data.id]: data }));
      setProfileNameStatus("Name saved.");
    }
  };

  const saveBillingAddress = async () => {
    if (!user?.id) return;
    const hasAnyField = [
      billingAddressStreetDraft,
      billingAddressPostcodeDraft,
      billingAddressCityDraft,
    ].some((value) => String(value || "").trim());
    const hasAllFields = [
      billingAddressStreetDraft,
      billingAddressPostcodeDraft,
      billingAddressCityDraft,
    ].every((value) => String(value || "").trim());
    if (hasAnyField && !hasAllFields) {
      setBillingAddressStatus("Please fill in street, postcode, and city.");
      return;
    }
    const billingAddress = buildBillingAddress({
      street: billingAddressStreetDraft,
      postcode: billingAddressPostcodeDraft,
      city: billingAddressCityDraft,
    });
    setBillingAddressSaving(true);
    setBillingAddressStatus("");
    const { data, error } = await supabase
      .from("profiles")
      .update({ billing_address: billingAddress || null })
      .eq("id", user.id)
      .select("*")
      .single();
    setBillingAddressSaving(false);
    if (error) {
      setBillingAddressStatus("Could not save billing address.");
      return;
    }
    if (data) {
      setProfiles((prev) => ({ ...prev, [data.id]: data }));
      setBillingAddressStatus("Billing address saved.");
    }
  };

  const loadCoreData = useCallback(async () => {
    const [{ data: locationsData }, { data: teachersData }, { data: classesData }] =
      await Promise.all([
        supabase.from("locations").select("id,name").eq("active", true),
        supabase.from("teachers").select("id,name").eq("active", true),
        supabase
          .from("classes")
          .select(
            "id,name,class_date,class_time,spots,ghost_spots,is_cancelled,class_color,location:locations(name),teacher:teachers(name)"
          ),
      ]);

    if (Array.isArray(locationsData) && locationsData.length > 0) {
      setLocations(locationsData.map((loc) => ({ id: loc.id, name: loc.name })));
    }
    if (Array.isArray(teachersData) && teachersData.length > 0) {
      setTeachers(teachersData.map((t) => ({ id: t.id, name: t.name })));
    }
    if (Array.isArray(classesData)) {
      setClasses(
        classesData.map((cls) => ({
          id: cls.id,
          name: cls.name,
          date: cls.class_date,
          time: cls.class_time,
          spots: cls.spots,
          ghostSpots: Number(cls.ghost_spots) || 0,
          location: cls.location?.name || "",
          teacher: cls.teacher?.name || "",
          color: cls.class_color || "#f4c430",
          isCancelled: cls.is_cancelled,
        }))
      );
    }

    const { data: bookedCountData } = await supabase.rpc("get_booked_counts");
    if (Array.isArray(bookedCountData)) {
      setBookedCounts(
        bookedCountData.reduce((acc, row) => {
          acc[row.class_id] = row.booked || 0;
          return acc;
        }, {})
      );
    }
  }, []);

  const loadClassTemplates = useCallback(async () => {
    if (!user?.id || !effectiveIsAdmin) {
      setClassTemplates([]);
      return;
    }

    const mapTemplateRow = (row) => ({
      id: row.id,
      name: row.name,
      time: row.time,
      spots: Number(row.spots) || 0,
      location: row.location,
      teacher: row.teacher || "",
      color: row.color || "#f4c430",
    });

    const { data, error } = await supabase
      .from("class_templates")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      setClassTemplateStatus(error.message || "Could not load class templates.");
      return;
    }

    if (Array.isArray(data) && data.length > 0) {
      setClassTemplates(data.map(mapTemplateRow));
      return;
    }

    // One-time import of legacy local-only templates (if they still exist in this browser).
    let legacyTemplates = [];
    try {
      const raw = localStorage.getItem("classTemplates");
      const parsed = raw ? JSON.parse(raw) : null;
      legacyTemplates = Array.isArray(parsed) ? parsed : [];
    } catch {
      legacyTemplates = [];
    }
    if (!legacyTemplates.length) {
      setClassTemplates([]);
      return;
    }

    const insertPayload = legacyTemplates
      .map((template) => ({
        name: String(template?.name || "").trim(),
        time: String(template?.time || "").trim(),
        spots: Number(template?.spots) || 0,
        location: String(template?.location || "").trim(),
        teacher: String(template?.teacher || "").trim() || null,
        color: String(template?.color || "#f4c430"),
        created_by: user.id,
      }))
      .filter((template) => template.name && template.time && template.spots > 0 && template.location);

    if (!insertPayload.length) {
      setClassTemplates([]);
      return;
    }

    const { data: importedRows, error: importError } = await supabase
      .from("class_templates")
      .insert(insertPayload)
      .select("*")
      .order("created_at", { ascending: false });

    if (importError) {
      setClassTemplateStatus(importError.message || "Could not import saved templates.");
      return;
    }

    if (Array.isArray(importedRows)) {
      setClassTemplates(importedRows.map(mapTemplateRow));
      setClassTemplateStatus("Imported saved templates from this browser.");
      try {
        localStorage.removeItem("classTemplates");
      } catch {
        // ignore storage errors
      }
    }
  }, [user?.id, effectiveIsAdmin]);

  const loadReviews = async () => {
    const { data, error } = await supabase
      .from("class_reviews")
      .select("*")
      .order("created_at", { ascending: false });
    if (error || !data) return;
    setReviews(data);
  };

  const deleteReviewByAdmin = async (review) => {
    if (!isAdmin || !review?.id) return;
    const confirmed = window.confirm("Delete this review?");
    if (!confirmed) return;
    const authHeaders = await getAuthHeaders();
    const { data, error } = await supabase.functions.invoke("delete-review-admin", {
      headers: authHeaders,
      body: { reviewId: review.id },
    });
    if (error) {
      const detail =
        error?.context?.details ||
        error?.context?.response?.text ||
        error?.message ||
        "Failed to delete review.";
      alert(String(detail));
      return;
    }
    if (data?.error) {
      alert(data.error || "Failed to delete review.");
      return;
    }
    logAction({
      actor: user.email,
      action: "REVIEW_DELETED",
      details: { reviewId: review.id, className: review.class_name || review.className || "" },
    });
    await loadReviews();
  };

  useEffect(() => {
    if (!user?.id) return;
    loadCoreData();
  }, [user?.id, loadCoreData]);

  useEffect(() => {
    if (!user?.id || !effectiveIsAdmin) {
      setClassTemplates([]);
      return;
    }
    loadClassTemplates();
  }, [user?.id, effectiveIsAdmin, loadClassTemplates]);

  useEffect(() => {
    if (view === "reviews" || !user) {
      loadReviews();
    }
  }, [view, user]);

  const loadBookingsData = async () => {
    const { data: bookingsData } = await supabase
      .from("bookings")
      .select("id,user_id,class_id,status,credit_source,created_at,cancelled_at");
    if (Array.isArray(bookingsData)) {
      setBookings(
        bookingsData.map((b) => ({
          id: b.id,
          userId: b.user_id,
          classId: b.class_id,
          status: b.status,
          creditSource: b.credit_source,
          createdAt: b.created_at,
          cancelledAt: b.cancelled_at,
        }))
      );
    }

    const { data: waitlistData } = await supabase
      .from("waitlist")
      .select("id,user_id,class_id,created_at");
    if (Array.isArray(waitlistData)) {
      setWaitlists(
        waitlistData.reduce((acc, entry) => {
          if (!acc[entry.class_id]) acc[entry.class_id] = [];
          acc[entry.class_id].push(entry.user_id);
          return acc;
        }, {})
      );
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    loadBookingsData();
  }, [user?.id]);

  const loadPublicPricingData = useCallback(async () => {
    const plansQuery = supabase.from("membership_plans").select("*");
    const settingsQuery = supabase
      .from("app_settings")
      .select("key,value")
      .in("key", ["credit_price_per_unit", "credit_price_pack_10", "amiga_ticket_price"]);
    

    const [{ data: plans }, { data: settingsData, error: settingsError }] = await Promise.all([
      plansQuery,
      settingsQuery,
    ]);

    const mappedPlans = sortMembershipPlans(
      (Array.isArray(plans) ? plans : []).map((plan) => ({
        id: plan.id,
        name: plan.name,
        price: Number(plan.price_eur) || 0,
        creditsPerMonth: plan.credits_per_month,
        minimumMonths: Math.max(1, Number(plan.minimum_months) || 1),
        createdAt: plan.created_at || null,
        updatedAt: plan.updated_at || null,
      }))
    );
    setMembershipPlans(mappedPlans);
    setMembershipPlanDrafts(mappedPlans);

    if (settingsError) {
      // eslint-disable-next-line no-console
      console.error("Failed to load public pricing settings", settingsError);
      return;
    }

    if (Array.isArray(settingsData)) {
      const lookup = settingsData.reduce((acc, entry) => {
        acc[entry.key] = entry.value;
        return acc;
      }, {});
      const parsedCreditPrice = Math.max(0, Number(lookup.credit_price_per_unit ?? creditPricePerUnit) || 0);
      const parsedCreditPack10Price = Math.max(
        0,
        Number(lookup.credit_price_pack_10 ?? parsedCreditPrice * 10) || 0
      );
      const parsedAmigaPrice = Math.max(
        0,
        Number(lookup.amiga_ticket_price ?? amigaTicketPricePerUnit) || 0
      );
      setCreditPricePerUnit(parsedCreditPrice);
      setCreditPriceDraft(parsedCreditPrice);
      setCreditPack10Price(parsedCreditPack10Price);
      setCreditPack10PriceDraft(parsedCreditPack10Price);
      setAmigaTicketPricePerUnit(parsedAmigaPrice);
      setAmigaTicketPriceDraft(parsedAmigaPrice);
    }
  }, [creditPricePerUnit, amigaTicketPricePerUnit]);

  useEffect(() => {
    if (user?.id) return;
    loadPublicPricingData();
  }, [user?.id, loadPublicPricingData]);

  const unlockDevTools = async () => {
    if (!user?.email || !devToolsPasswordDraft.trim()) {
      setDevToolsStatus("Please enter your password.");
      return;
    }

    setDevToolsLoading(true);
    setDevToolsStatus("Verifying password...");
    const { data, error } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: devToolsPasswordDraft,
    });
    setDevToolsLoading(false);

    if (error || !data?.session) {
      setDevToolsStatus("Password verification failed.");
      return;
    }

    setDevToolsUnlocked(true);
    setDevToolsPasswordDraft("");
    setDevToolsVisible(true);
    setDevToolsStatus("Developer tools unlocked.");
  };

  const toggleViewingAsNormalAccount = () => {
    setViewingAsNormalAccount((prev) => {
      const next = !prev;
      setDevToolsStatus(next ? "Viewing the app as a regular account." : "Returned to admin view.");
      return next;
    });
  };

  const loadFinancialData = useCallback(async () => {
    const plansQuery = supabase.from("membership_plans").select("*");
    const settingsQuery = supabase
      .from("app_settings")
      .select("key,value")
      .in("key", [
        "credit_price_per_unit",
        "credit_price_pack_10",
        "amiga_ticket_price",
        "review_invite_enabled",
        "review_invite_frequency_days",
        "review_invite_exclude_members",
        "review_invite_delay_hours",
        "invoice_issuer_name",
        "invoice_issuer_address",
        "invoice_issuer_kvk_number",
        "invoice_issuer_vat_number",
        "invoice_issuer_email",
        "invoice_issuer_phone",
        "invoice_default_description",
        "invoice_footer_note",
      ]);
    const membershipsQuery = effectiveIsAdmin
      ? supabase.from("memberships").select("*")
      : supabase.from("memberships").select("*").eq("user_id", user.id);
    const usageQuery = effectiveIsAdmin
      ? supabase.from("membership_usage").select("*")
      : supabase.from("membership_usage").select("*").eq("user_id", user.id);
    const membershipPaymentHistoryQuery = effectiveIsAdmin
      ? supabase.from("membership_payment_history").select("*")
      : supabase.from("membership_payment_history").select("*").eq("user_id", user.id);
    const ledgerQuery = effectiveIsAdmin
      ? supabase.from("credit_ledger").select("*")
      : supabase.from("credit_ledger").select("*").eq("user_id", user.id);
    const amigaLedgerQuery = effectiveIsAdmin
      ? supabase.from("amiga_ticket_ledger").select("*")
      : supabase.from("amiga_ticket_ledger").select("*").eq("user_id", user.id);

    const [
      { data: plans },
      { data: membershipsData },
      { data: usageData },
      { data: membershipPaymentHistoryData },
      { data: ledgerData },
      { data: settingsData, error: settingsError },
      { data: amigaLedgerData },
    ] = await Promise.all([
      plansQuery,
      membershipsQuery,
      usageQuery,
      membershipPaymentHistoryQuery,
      ledgerQuery,
      settingsQuery,
      amigaLedgerQuery,
    ]);

    const mappedPlans = sortMembershipPlans(
      (Array.isArray(plans) ? plans : []).map((plan) => ({
        id: plan.id,
        name: plan.name,
        price: Number(plan.price_eur) || 0,
        creditsPerMonth: plan.credits_per_month,
        minimumMonths: Math.max(1, Number(plan.minimum_months) || 1),
        createdAt: plan.created_at || null,
        updatedAt: plan.updated_at || null,
      }))
    );
    setMembershipPlans(mappedPlans);
    setMembershipPlanDrafts(mappedPlans);

    if (Array.isArray(membershipsData)) {
      const mapped = membershipsData.reduce((acc, row) => {
        acc[row.user_id] = {
          planId: row.plan_id,
          status: row.status,
          startedAt: row.started_at,
          cancelledAt: row.cancelled_at,
          customCreditsPerMonth: row.custom_credits_per_month,
          customMonthlyPriceEur: row.custom_monthly_price_eur,
        };
        return acc;
      }, {});
      setMemberships(mapped);
    }

    if (Array.isArray(usageData)) {
      const usageMap = usageData.reduce((acc, row) => {
        acc[row.user_id] = acc[row.user_id] || {};
        acc[row.user_id][row.period] = row.used;
        return acc;
      }, {});
      setMembershipUsage(usageMap);
    }

    if (Array.isArray(membershipPaymentHistoryData)) {
      const historyMap = membershipPaymentHistoryData.reduce((acc, row) => {
        acc[row.user_id] = acc[row.user_id] || [];
        acc[row.user_id].push({
          id: row.id,
          userId: row.user_id,
          period: row.period,
          status: row.status,
          amount_eur: Number(row.amount_eur || 0),
          payment_id: row.payment_id,
          payment_url: row.payment_url,
          failure_reason: row.failure_reason,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
        });
        return acc;
      }, {});
      setMembershipPaymentHistory(historyMap);
    }

    if (Array.isArray(ledgerData)) {
      setCreditLedger(
        ledgerData
          .map((row) => ({
            id: row.id,
            userId: row.user_id,
            delta: row.delta,
            reason: row.reason,
            expiresAt: row.expires_at,
            timestamp: row.created_at,
          }))
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      );
    }

    if (Array.isArray(amigaLedgerData)) {
      setAmigaTicketLedger(
        amigaLedgerData
          .map((row) => ({
            id: row.id,
            userId: row.user_id,
            delta: row.delta,
            reason: row.reason,
            expiresAt: row.expires_at,
            timestamp: row.created_at,
          }))
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      );
    }

    if (settingsError) {
      // eslint-disable-next-line no-console
      console.error("Failed to load app settings", settingsError);
    }

    if (Array.isArray(settingsData)) {
      const parseSettingBool = (value, fallback) => {
        if (value == null) return fallback;
        if (typeof value === "boolean") return value;
        const normalized = String(value).trim().toLowerCase();
        if (["true", "1", "yes", "on"].includes(normalized)) return true;
        if (["false", "0", "no", "off"].includes(normalized)) return false;
        return fallback;
      };
      const parseSettingNumber = (value, fallback) => {
        if (value == null) return fallback;
        if (typeof value === "number" && Number.isFinite(value)) return value;
        const normalized = String(value).trim().replace(",", ".");
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : fallback;
      };
      const settingsLookup = (settingsData || []).reduce((acc, row) => {
        const normalizedKey = String(row?.key || "").trim().toLowerCase();
        if (!normalizedKey) return acc;
        acc.set(normalizedKey, row?.value);
        return acc;
      }, new Map());
      const settingText = (key, fallback = "") => {
        const rawValue = settingsLookup.get(String(key).trim().toLowerCase());
        if (rawValue == null) return fallback;
        return String(rawValue);
      };

      const parsedCreditPrice = parseSettingNumber(
        settingsLookup.get("credit_price_per_unit"),
        creditPricePerUnit
      );
      setCreditPricePerUnit(parsedCreditPrice);
      setCreditPriceDraft(parsedCreditPrice);
      const parsedCreditPack10Price = parseSettingNumber(
        settingsLookup.get("credit_price_pack_10"),
        parsedCreditPrice * 10
      );
      setCreditPack10Price(parsedCreditPack10Price);
      setCreditPack10PriceDraft(parsedCreditPack10Price);
      const parsedAmigaPrice = parseSettingNumber(
        settingsLookup.get("amiga_ticket_price"),
        amigaTicketPricePerUnit
      );
      setAmigaTicketPricePerUnit(parsedAmigaPrice);
      setAmigaTicketPriceDraft(parsedAmigaPrice);

      const inviteEnabledSetting = settingsLookup.get("review_invite_enabled");
      const nextInviteEnabled = parseSettingBool(inviteEnabledSetting, true);
      setReviewInviteEnabled(nextInviteEnabled);
      setReviewInviteEnabledDraft(nextInviteEnabled);

      const inviteFrequencySetting = settingsLookup.get("review_invite_frequency_days");
      const nextFrequency = Number(inviteFrequencySetting);
      const resolvedFrequency = Number.isFinite(nextFrequency) && nextFrequency > 0 ? nextFrequency : 7;
      setReviewInviteFrequencyDays(resolvedFrequency);
      setReviewInviteFrequencyDaysDraft(resolvedFrequency);

      const inviteExcludeMembersSetting = settingsLookup.get("review_invite_exclude_members");
      const nextExcludeMembers = parseSettingBool(inviteExcludeMembersSetting, true);
      setReviewInviteExcludeMembers(nextExcludeMembers);
      setReviewInviteExcludeMembersDraft(nextExcludeMembers);

      const inviteDelaySetting = settingsLookup.get("review_invite_delay_hours");
      const nextDelay = Number(inviteDelaySetting);
      const resolvedDelay = Number.isFinite(nextDelay) && nextDelay >= 0 ? nextDelay : 2;
      setReviewInviteDelayHours(resolvedDelay);
      setReviewInviteDelayHoursDraft(resolvedDelay);

      const nextInvoicePresets = {
        issuerName: settingText("invoice_issuer_name", DEFAULT_INVOICE_PRESETS.issuerName),
        issuerAddress: settingText("invoice_issuer_address", DEFAULT_INVOICE_PRESETS.issuerAddress),
        issuerKvkNumber: settingText(
          "invoice_issuer_kvk_number",
          DEFAULT_INVOICE_PRESETS.issuerKvkNumber
        ),
        issuerVatNumber: settingText("invoice_issuer_vat_number", DEFAULT_INVOICE_PRESETS.issuerVatNumber),
        issuerEmail: settingText("invoice_issuer_email", DEFAULT_INVOICE_PRESETS.issuerEmail),
        issuerPhone: settingText("invoice_issuer_phone", DEFAULT_INVOICE_PRESETS.issuerPhone),
        defaultDescription: settingText(
          "invoice_default_description",
          DEFAULT_INVOICE_PRESETS.defaultDescription
        ),
        footerNote: settingText("invoice_footer_note", DEFAULT_INVOICE_PRESETS.footerNote),
      };
      setInvoicePresets(nextInvoicePresets);
      setInvoicePresetsDraft(nextInvoicePresets);
    } else {
      setCreditPriceDraft(creditPricePerUnit);
      setCreditPack10PriceDraft(creditPack10Price);
      setAmigaTicketPriceDraft(amigaTicketPricePerUnit);
      setReviewInviteEnabledDraft(reviewInviteEnabled);
      setReviewInviteFrequencyDaysDraft(reviewInviteFrequencyDays);
      setReviewInviteExcludeMembersDraft(reviewInviteExcludeMembers);
      setReviewInviteDelayHoursDraft(reviewInviteDelayHours);
      setInvoicePresets(DEFAULT_INVOICE_PRESETS);
      setInvoicePresetsDraft(DEFAULT_INVOICE_PRESETS);
    }
  }, [
    creditPricePerUnit,
    creditPack10Price,
    amigaTicketPricePerUnit,
    reviewInviteEnabled,
    reviewInviteFrequencyDays,
    reviewInviteExcludeMembers,
    reviewInviteDelayHours,
    effectiveIsAdmin,
    user?.id,
  ]);

  useEffect(() => {
    if (!user?.id) return;
    loadFinancialData();
  }, [user?.id, loadFinancialData]);

  useEffect(() => {
    if (!user?.id) return;
    const params = new URLSearchParams(window.location.search);
    const membershipReturn = params.get("membership");
    if (membershipReturn !== "success" && membershipReturn !== "return") return;

    let cancelled = false;
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const clearMembershipParam = () => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("membership");
        const nextUrl = `${url.pathname}${url.search}${url.hash}`;
        window.history.replaceState({}, "", nextUrl);
      } catch {
        // ignore URL parsing errors
      }
    };

    const syncMembershipAfterReturn = async () => {
      setView("account");
      setMembershipCheckoutError("");
      setMembershipCheckoutStatus("Payment received. Finalizing your membership...");

      for (let attempt = 0; attempt < 12; attempt += 1) {
        const { data, error } = await supabase
          .from("memberships")
          .select("status")
          .eq("user_id", user.id)
          .maybeSingle();
        if (cancelled) return;
        // Always refresh financial data while returning from checkout so
        // membership payment history has time to reflect webhook updates.
        await loadFinancialData();
        if (cancelled) return;
        if (!error && data?.status === "active") {
          setMembershipCheckoutStatus("Membership activated. Monthly credits are now available.");
          clearMembershipParam();
          return;
        }
        await wait(2500);
      }

      await loadFinancialData();
      if (cancelled) return;
      setMembershipCheckoutStatus("");
      setMembershipCheckoutError(
        "Returned from checkout, but membership is still not active. If payment was completed, webhook processing may still be pending."
      );
      clearMembershipParam();
    };

    syncMembershipAfterReturn();

    return () => {
      cancelled = true;
    };
  }, [user?.id, loadFinancialData]);

  useEffect(() => {
    if (!user?.id || !effectiveIsAdmin) return;
    if (view !== "account") return;

    const intervalId = window.setInterval(() => {
      loadFinancialData();
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [user?.id, effectiveIsAdmin, view, loadFinancialData]);

  useEffect(() => {
    if (membershipPlans.length === 0) return;
    if (membershipPlanDrafts.length > 0) return;
    setMembershipPlanDrafts(membershipPlans);
  }, [membershipPlans, membershipPlanDrafts.length]);

  const loadAppStyling = useCallback(async () => {
    const { data, error } = await supabase
      .from("app_styling")
      .select("*")
      .eq("key", "default")
      .maybeSingle();
    if (error) return;
    const styling = normalizeAppStylingRow(data);
    setAppStyling(styling);
    setAppStylingDraft(styling);
  }, []);

  useEffect(() => {
    loadAppStyling();
  }, [loadAppStyling]);

  const loadEmailSettings = useCallback(async () => {
    if (!isAdmin) return;
    setEmailTemplatesLoading(true);
    const [{ data: templatesData }, { data: brandingData }] = await Promise.all([
      supabase.from("email_templates").select("*").order("name", { ascending: true }),
      supabase.from("email_branding").select("*").eq("key", "default").maybeSingle(),
    ]);

    const templates = Array.isArray(templatesData)
      ? templatesData.map((row) => normalizeEmailTemplateRow(row))
      : [];
    const branding = normalizeEmailBrandingRow(brandingData);
    const nextDrafts = templates.reduce((acc, template) => {
      acc[template.key] = { ...template };
      return acc;
    }, {});

    setEmailTemplates(templates);
    setEmailTemplateDrafts(nextDrafts);
    setEmailBranding(branding);
    setEmailBrandingDraft(branding);

    if (!selectedEmailTemplateKey && templates.length > 0) {
      setSelectedEmailTemplateKey(templates[0].key);
    }
    setEmailTemplatesLoading(false);
  }, [isAdmin, selectedEmailTemplateKey]);

  const updateEmailTemplateDraft = (field, value) => {
    if (!selectedEmailTemplateKey) return;
    setEmailTemplateDrafts((prev) => ({
      ...prev,
      [selectedEmailTemplateKey]: {
        ...(prev[selectedEmailTemplateKey] || {}),
        [field]: value,
      },
    }));
  };

  const saveEmailTemplate = async () => {
    if (!selectedEmailTemplateKey) return;
    const draft = emailTemplateDrafts[selectedEmailTemplateKey];
    if (!draft) return;
    setEmailTemplateStatus("Saving template...");
    const { error } = await supabase
      .from("email_templates")
      .update({
        subject: draft.subject,
        preheader: draft.preheader,
        hero_url: draft.heroUrl,
        heading: draft.heading,
        greeting: draft.greeting,
        body: draft.body,
        cta_label: draft.ctaLabel,
        cta_url: draft.ctaUrl,
        signature: draft.signature,
        footer: draft.footer,
        is_active: draft.isActive,
        updated_at: new Date().toISOString(),
      })
      .eq("key", selectedEmailTemplateKey);
    if (error) {
      setEmailTemplateStatus(error.message || "Failed to save template.");
      return;
    }
    logAction({
      actor: user.email,
      action: "EMAIL_TEMPLATE_UPDATED",
      details: { template: selectedEmailTemplateKey },
    });
    setEmailTemplateStatus("Template saved.");
    await loadEmailSettings();
  };

  const sendTestEmail = async () => {
    if (!selectedEmailTemplateKey || !user?.email) return;
    setEmailTestStatus("Sending test email...");
    const sampleData = {
      first_name: profileNameDraft || "Kevin",
      brand_name: emailBrandingDraft.brandName,
      site_url: emailBrandingDraft.siteUrl || window.location.origin,
      class_name: "Salsa Basics",
      class_date: "2026-02-07",
      class_time: "19:00",
      class_location: "Studio A",
      class_teacher: "Ana",
      review_url: `${window.location.origin}?review=test-token`,
      spots_left: 4,
      credits: 5,
      membership_name: "Standard",
      amount_paid: "€49.00",
    };
    try {
      const authHeaders = await getAuthHeaders();
      const { data, error } = await supabase.functions.invoke("send-transactional-email", {
        headers: authHeaders,
        body: {
          templateKey: selectedEmailTemplateKey,
          toEmail: user.email,
          data: sampleData,
        },
      });
      if (error) {
        const detail = error?.context?.details || error?.context?.response?.text || "";
        setEmailTestStatus(
          `Failed to send test email: ${error.message || "Unknown error"}${detail ? ` — ${detail}` : ""}`
        );
        return;
      }
      if (data?.error) {
        setEmailTestStatus(`Failed to send test email: ${data.error}`);
        return;
      }
      setEmailTestStatus(`Sent test email to ${user.email}.`);
    } catch (err) {
      setEmailTestStatus(`Failed to send test email: ${err?.message || err}`);
    }
  };

  const saveEmailBranding = async () => {
    setEmailBrandingStatus("Saving branding...");
    const { error } = await supabase.from("email_branding").upsert(
      {
        key: "default",
        brand_name: emailBrandingDraft.brandName,
        logo_url: emailBrandingDraft.logoUrl,
        hero_url: emailBrandingDraft.heroUrl,
        accent_color: emailBrandingDraft.accentColor,
        header_bg: emailBrandingDraft.headerBg,
        footer_text: emailBrandingDraft.footerText,
        signature: emailBrandingDraft.signature,
        site_url: emailBrandingDraft.siteUrl,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "key" }
    );
    if (error) {
      setEmailBrandingStatus(error.message || "Failed to save branding.");
      return;
    }
    logAction({
      actor: user.email,
      action: "EMAIL_BRANDING_UPDATED",
      details: {},
    });
    setEmailBrandingStatus("Branding saved.");
    await loadEmailSettings();
  };

  const saveAppStyling = async () => {
    if (!isAdmin) return;
    setAppStylingStatus("Saving app styling...");
    const { error } = await supabase.from("app_styling").upsert(
      {
        key: "default",
        brand_name: appStylingDraft.brandName,
        app_title: appStylingDraft.appTitle,
        logo_url: appStylingDraft.logoUrl,
        app_background: appStylingDraft.appBackground,
        surface_background: appStylingDraft.surfaceBackground,
        header_background: appStylingDraft.headerBackground,
        input_background: appStylingDraft.inputBackground,
        border_color: appStylingDraft.borderColor,
        text_color: appStylingDraft.textColor,
        muted_text_color: appStylingDraft.mutedTextColor,
        button_from: appStylingDraft.buttonFrom,
        button_to: appStylingDraft.buttonTo,
        button_text: appStylingDraft.buttonText,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "key" }
    );
    if (error) {
      setAppStylingStatus(error.message || "Failed to save app styling.");
      return;
    }
    setAppStyling({ ...appStylingDraft });
    logAction({
      actor: user.email,
      action: "APP_STYLING_UPDATED",
      details: {},
    });
    setAppStylingStatus("App styling saved.");
  };

  useEffect(() => {
    if (!user?.id || !isAdmin) return;
    if (view !== "account") return;
    if (adminAccountTab !== "emails") return;
    loadEmailSettings();
  }, [user?.id, isAdmin, view, adminAccountTab, loadEmailSettings]);

  useEffect(() => {
    if (!user?.id || !isAdmin) return;
    if (view !== "account") return;
    if (adminAccountTab !== "styling") return;
    loadAppStyling();
  }, [user?.id, isAdmin, view, adminAccountTab, loadAppStyling]);

  useEffect(() => {
    if (!user?.id || !isAdmin) return;
    if (view !== "account" || adminAccountTab !== "emails") return;
    supabase.auth.getSession().then(({ data }) => {
      const accessToken = data?.session?.access_token || "";
      const payload = decodeJwtPayload(accessToken);
      setEmailDebugInfo({
        sessionIss: payload?.iss || "",
        sessionRef: payload?.ref || "",
        sessionExp: payload?.exp || null,
        anonRef: decodeJwtPayload(supabaseAnonKey || "")?.ref || "",
        supabaseUrl: supabaseUrl || "",
      });
    });
  }, [user?.id, isAdmin, view, adminAccountTab]);

  useEffect(() => {
    if (!selectedEmailTemplateKey) return;
    if (!emailTemplateDrafts[selectedEmailTemplateKey] && emailTemplates.length > 0) {
      const template = emailTemplates.find((t) => t.key === selectedEmailTemplateKey);
      if (template) {
        setEmailTemplateDrafts((prev) => ({
          ...prev,
          [selectedEmailTemplateKey]: { ...template },
        }));
      }
    }
  }, [selectedEmailTemplateKey, emailTemplateDrafts, emailTemplates]);

  const loadAuditLog = useCallback(async () => {
    if (!isAdmin) return;
    const { data } = await supabase
      .from("audit_log")
      .select("id,actor_user_id,action,details,created_at")
      .order("created_at", { ascending: false })
      .limit(200);
    if (Array.isArray(data)) {
      setAuditLog(
        data.map((entry) => ({
          id: entry.id,
          actor: entry.details?.actorEmail || "system",
          action: entry.action,
          details: entry.details,
          timestamp: entry.created_at,
        }))
      );
    }
  }, [isAdmin]);

  const loadInvoices = useCallback(async () => {
    if (!user?.id) return;
    setInvoicesLoading(true);
    setInvoicesError("");
    const query = isAdmin
      ? supabase
          .from("invoices")
          .select(
            "id,invoice_number,user_id,payment_id,invoice_date,amount_incl,pdf_url,customer_name,customer_email"
          )
          .order("invoice_date", { ascending: false })
          .order("invoice_number", { ascending: false })
      : supabase
          .from("invoices")
          .select(
            "id,invoice_number,user_id,payment_id,invoice_date,amount_incl,pdf_url,customer_name,customer_email"
          )
          .eq("user_id", user.id)
          .order("invoice_date", { ascending: false })
          .order("invoice_number", { ascending: false });
    const { data, error } = await query;
    setInvoicesLoading(false);
    if (error) {
      setInvoicesError(error.message || "Failed to load invoices.");
      return;
    }
    setInvoices(data || []);
  }, [isAdmin, user?.id]);

  const openInvoicePdf = async (path, key) => {
    if (!path) return;
    const idKey = key || path;
    setInvoiceDownloadLoading((prev) => ({ ...prev, [idKey]: true }));
    const { data, error } = await supabase.storage
      .from("Invoices")
      .createSignedUrl(path, 60 * 10);
    setInvoiceDownloadLoading((prev) => ({ ...prev, [idKey]: false }));
    if (error || !data?.signedUrl) {
      alert(error?.message || "Could not open invoice.");
      return;
    }
    window.open(data.signedUrl, "_blank", "noreferrer");
  };

  const regenerateInvoicePdf = async (invoiceId) => {
    if (!invoiceId) return;
    setInvoiceRetryLoading((prev) => ({ ...prev, [invoiceId]: true }));
    const { data, error } = await supabase.functions.invoke("invoice-regenerate", {
      body: { invoiceId },
    });
    setInvoiceRetryLoading((prev) => ({ ...prev, [invoiceId]: false }));
    if (error) {
      alert(error.message || "Could not generate PDF.");
      return;
    }
    if (data?.pdf_url) {
      await loadInvoices();
    }
  };

  const loadAdminAnalytics = useCallback(async () => {
    if (!isAdmin) return;
    setAnalyticsLoading(true);
    setAnalyticsError("");
    try {
      const authHeaders = await getAuthHeaders();
      const { data, error } = await supabase.functions.invoke("admin-mobile-action", {
        headers: authHeaders,
        body: {
          action: "get_admin_analytics",
          payload: {
            range: analyticsRange,
            month: analyticsMonth,
          },
        },
      });
      if (error || data?.error) {
        throw new Error(formatInvokeError(error, data));
      }
      setAnalytics({
        classesHappened: Number(data?.analytics?.classesHappened || 0),
        totalPeopleParticipated: Number(data?.analytics?.totalPeopleParticipated || 0),
        uniqueParticipants: Number(data?.analytics?.uniqueParticipants || 0),
        creditsSold: Number(data?.analytics?.creditsSold || 0),
        payoutTotal: Number(data?.analytics?.payoutTotal || 0),
        newAccounts: Number(data?.analytics?.newAccounts || 0),
        activeMemberships: Number(data?.analytics?.activeMemberships || 0),
        newMemberships: Number(data?.analytics?.newMemberships || 0),
        membershipChurn: Number(data?.analytics?.membershipChurn || 0),
      });
      setAnalyticsMonthly(Array.isArray(data?.analytics?.monthlyAnalytics) ? data.analytics.monthlyAnalytics : []);
    } catch (error) {
      setAnalyticsError(error?.message || "Could not load analytics.");
    } finally {
      setAnalyticsLoading(false);
    }
  }, [isAdmin, analyticsRange, analyticsMonth]);

  const saveInvoicePresets = async () => {
    if (!isAdmin) return;
    const nextPresets = {
      issuerName: String(invoicePresetsDraft.issuerName || "").trim(),
      issuerAddress: String(invoicePresetsDraft.issuerAddress || "").trim(),
      issuerKvkNumber: String(invoicePresetsDraft.issuerKvkNumber || "").trim(),
      issuerVatNumber: String(invoicePresetsDraft.issuerVatNumber || "").trim(),
      issuerEmail: String(invoicePresetsDraft.issuerEmail || "").trim(),
      issuerPhone: String(invoicePresetsDraft.issuerPhone || "").trim(),
      defaultDescription: String(invoicePresetsDraft.defaultDescription || "").trim(),
      footerNote: String(invoicePresetsDraft.footerNote || "").trim(),
    };

    const payload = [
      { key: "invoice_issuer_name", value: nextPresets.issuerName || DEFAULT_INVOICE_PRESETS.issuerName },
      { key: "invoice_issuer_address", value: nextPresets.issuerAddress },
      { key: "invoice_issuer_kvk_number", value: nextPresets.issuerKvkNumber },
      { key: "invoice_issuer_vat_number", value: nextPresets.issuerVatNumber },
      { key: "invoice_issuer_email", value: nextPresets.issuerEmail },
      { key: "invoice_issuer_phone", value: nextPresets.issuerPhone },
      {
        key: "invoice_default_description",
        value: nextPresets.defaultDescription || DEFAULT_INVOICE_PRESETS.defaultDescription,
      },
      { key: "invoice_footer_note", value: nextPresets.footerNote || DEFAULT_INVOICE_PRESETS.footerNote },
    ];
    setInvoicePresetsStatus("Saving invoice presets...");
    const { error } = await supabase.from("app_settings").upsert(payload, { onConflict: "key" });
    if (error) {
      setInvoicePresetsStatus(error.message || "Failed to save invoice presets.");
      return;
    }
    setInvoicePresets({
      ...nextPresets,
      issuerName: nextPresets.issuerName || DEFAULT_INVOICE_PRESETS.issuerName,
      defaultDescription:
        nextPresets.defaultDescription || DEFAULT_INVOICE_PRESETS.defaultDescription,
      footerNote: nextPresets.footerNote || DEFAULT_INVOICE_PRESETS.footerNote,
    });
    logAction({
      actor: user.email,
      action: "INVOICE_PRESETS_UPDATED",
      details: {
        issuerName: nextPresets.issuerName || DEFAULT_INVOICE_PRESETS.issuerName,
      },
    });
    setInvoicePresetsStatus("Invoice presets saved.");
  };

  useEffect(() => {
    if (!user?.id || !isAdmin) return;
    loadAuditLog();
  }, [user?.id, isAdmin, loadAuditLog]);

  useEffect(() => {
    if (!user?.id || !isAdmin) return;
    if (view !== "account") return;
    if (adminAccountTab !== "analytics") return;
    loadAdminAnalytics();
  }, [user?.id, isAdmin, view, adminAccountTab, loadAdminAnalytics]);

  useEffect(() => {
    if (!user?.id || !pendingCheckoutIntent) return;
    if (pendingCheckoutSubmitting) return;

    const run = async () => {
      setPendingCheckoutSubmitting(true);
      try {
        if (pendingCheckoutIntent.kind === "credits") {
          const pack = creditPackages.find(
            (item) => Number(item.amount) === Number(pendingCheckoutIntent.amount)
          );
          if (pack) {
            setPendingCheckoutIntent(null);
            savePendingCheckoutIntent(null);
            await purchaseCreditsRef.current?.(pack);
            return;
          }
        }
        if (pendingCheckoutIntent.kind === "amiga_ticket") {
          setPendingCheckoutIntent(null);
          savePendingCheckoutIntent(null);
          await purchaseAmigaTicketRef.current?.();
          return;
        }
        if (pendingCheckoutIntent.kind === "membership") {
          const plan = membershipPlans.find((item) => item.id === pendingCheckoutIntent.planId);
          if (plan) {
            setPendingCheckoutIntent(null);
            savePendingCheckoutIntent(null);
            await subscribeMembershipRef.current?.(plan);
            return;
          }
        }
        setPendingCheckoutIntent(null);
        savePendingCheckoutIntent(null);
      } finally {
        setPendingCheckoutSubmitting(false);
      }
    };

    run();
  }, [
    user?.id,
    pendingCheckoutIntent,
    pendingCheckoutSubmitting,
    creditPackages,
    membershipPlans,
  ]);

  useEffect(() => {
    if (!user?.id) return;
    if (pendingCheckoutIntent) return;
    let active = true;

    const loadClaimedIntent = async () => {
      const authHeaders = await getAuthHeaders();
      const { data, error } = await supabase.functions.invoke("claim-pending-checkout", {
        headers: authHeaders,
        body: {},
      });
      if (!active || error || data?.error || !data?.intent) return;
      setPendingCheckoutIntent(data.intent);
      savePendingCheckoutIntent(data.intent);
    };

    loadClaimedIntent();
    return () => {
      active = false;
    };
  }, [user?.id, pendingCheckoutIntent]);

  useEffect(() => {
    if (!user?.id || !isAdmin) return;
    if (view !== "account") return;
    if (adminAccountTab !== "invoices") return;
    loadInvoices();
  }, [user?.id, isAdmin, view, adminAccountTab, loadInvoices]);

  useEffect(() => {
    if (!user?.id || isAdmin) return;
    if (view !== "account") return;
    if (userAccountTab !== "invoices") return;
    loadInvoices();
  }, [user?.id, isAdmin, view, userAccountTab, loadInvoices]);


  const fetchAllProfiles = useCallback(async () => {
    const { data, error } = await supabase.from("profiles").select("*");
    if (error || !data) return;
    const mapped = data.reduce((acc, profile) => {
      acc[profile.id] = profile;
      return acc;
    }, {});
    setProfiles(mapped);
  }, []);

  const adminRole = profiles[user?.id]?.role || "";

  useEffect(() => {
    if (!user?.id) return;
    if (adminRole !== "admin") return;
    fetchAllProfiles();
  }, [user?.id, adminRole, fetchAllProfiles]);

  const login = async () => {
    if (!email || !password) {
      setAuthError("Email and password are required.");
      return;
    }
    setAuthError("");
    setAuthNotice("");
    setAuthLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setAuthLoading(false);
    if (error) {
      setAuthError(error.message || "Login failed.");
      return;
    }
    if (data?.user) {
      setUser(data.user);
      ensureProfile(data.user);
      if (pendingCheckoutIntent) {
        setAuthNotice(
          `Logged in. Continuing with ${describeCheckoutIntent(
            pendingCheckoutIntent,
            membershipPlans
          )}...`
        );
      }
    }
  };

  const register = async () => {
    const trimmedEmail = String(email || "").trim();
    const fullName = registerName.trim();
    if (
      !trimmedEmail ||
      !password ||
      !confirmPassword ||
      !fullName
    ) {
      setAuthError("Email, password, and full name are required.");
      return;
    }
    if (password !== confirmPassword) {
      setAuthError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setAuthError("Password must be at least 8 characters long.");
      return;
    }
    if (!/\d/.test(password)) {
      setAuthError("Password must include at least 1 number.");
      return;
    }
    if (!isLikelyEmail(trimmedEmail)) {
      setAuthError("Please enter a valid email address (for example: name@example.com).");
      return;
    }
    if (isLikelyEmail(fullName)) {
      setAuthError("Please enter your full name, not an email address.");
      return;
    }
    if (!registerAcceptedTerms) {
      setAuthError("Please accept the general and privacy terms to create an account.");
      return;
    }
    setAuthError("");
    setAuthNotice("");
    setAuthLoading(true);
    const { data: emailCheckData, error: emailCheckError } = await supabase.functions.invoke(
      "check-email-registration",
      {
        body: { email: trimmedEmail },
      }
    );
    if (emailCheckError) {
      setAuthLoading(false);
      setAuthError(emailCheckError.message || "Could not verify this email address right now.");
      return;
    }
    if (emailCheckData?.exists) {
      setAuthLoading(false);
      setAuthMode("login");
      setPassword("");
      setConfirmPassword("");
      setRegisterName("");
      setRegisterAcceptedTerms(false);
      setAuthError("This email already has an account. Log in instead to continue.");
      setAuthNotice("");
      return;
    }
    const { data, error } = await supabase.auth.signUp({
      email: trimmedEmail,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}${window.location.pathname}`,
        data: {
          name: fullName,
          pending_checkout_intent: pendingCheckoutIntent || null,
        },
      },
    });
    setAuthLoading(false);
    if (error) {
      const friendlyError = getFriendlySignupError(error);
      if (friendlyError.switchToLogin) {
        setAuthMode("login");
        setPassword("");
        setConfirmPassword("");
        setRegisterName("");
        setRegisterAcceptedTerms(false);
      }
      setAuthError(friendlyError.message);
      return;
    }
    if (data?.user) {
      await ensureProfile(data.user);
    }
    setRegisterName("");
    setRegisterAcceptedTerms(false);
    if (data?.session && pendingCheckoutIntent) {
      setAuthNotice(
        `Account created. Continuing with ${describeCheckoutIntent(
          pendingCheckoutIntent,
          membershipPlans
        )}...`
      );
    } else if (data?.session) {
      setAuthNotice("Account created successfully.");
    } else {
      setAuthNotice(
        pendingCheckoutIntent
          ? `Step 1 of 2 complete. Check your email to confirm your account. After confirmation, you will be brought back here and continue to Mollie payment for ${describeCheckoutIntent(
              pendingCheckoutIntent,
              membershipPlans
            )}. No payment has been taken yet.`
          : "Check your email to confirm your account."
      );
    }
  };

  const sendPasswordReset = async () => {
    if (!email) {
      setAuthError("Enter your email first, then click Forgot password.");
      return;
    }
    setAuthError("");
    setAuthNotice("");
    setAuthLoading(true);
    const redirectTo = `${window.location.origin}${window.location.pathname}`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    setAuthLoading(false);
    if (error) {
      setAuthError(error.message || "Could not send reset email.");
      return;
    }
    setAuthNotice("If this email exists, a password reset link has been sent.");
  };

  const logout = () => {
    supabase.auth.signOut();
    setUser(null);
    setProfiles({});
    setAuthMode("login");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setRegisterName("");
    setRegisterAcceptedTerms(false);
    setMembershipAcceptedTerms(false);
    setPendingCheckoutIntent(null);
    savePendingCheckoutIntent(null);
    setPendingCheckoutSubmitting(false);
  };

  const submitReview = async () => {
    if (!reviewToken) return;
    if (!reviewComment.trim()) {
      setReviewStatus("Please add a short review.");
      return;
    }
    setReviewStatus("Submitting...");
    const { error } = await supabase.functions.invoke("submit-review", {
      body: {
        token: reviewToken,
        rating: reviewRating,
        comment: reviewComment.trim(),
      },
    });
    if (error) {
      let detail = "";
      try {
        const raw = String((await error.context?.text?.()) || "");
        try {
          detail = String(JSON.parse(raw)?.error || raw);
        } catch {
          detail = raw;
        }
        detail = detail.slice(0, 200);
      } catch {
        // response body unavailable
      }
      setReviewStatus(detail ? `Could not submit review: ${detail}` : "Could not submit review.");
      return;
    }
    setReviewStatus("Thanks for your review!");
    setReviewComment("");
  };

  const submitPastClassReview = async (cls) => {
    if (!user?.id || !cls?.id) return;
    if (!pastReviewComment.trim()) {
      setPastReviewStatus("Please add a short review.");
      return;
    }

    setPastReviewStatus("Submitting...");
    const authHeaders = await getAuthHeaders();
    const { data, error } = await supabase.functions.invoke("submit-class-review", {
      headers: authHeaders,
      body: {
        classId: cls.id,
        rating: pastReviewRating,
        comment: pastReviewComment.trim(),
      },
    });

    if (error) {
      const detail =
        error?.context?.details ||
        error?.context?.response?.text ||
        error?.message ||
        "Could not submit review.";
      setPastReviewStatus(String(detail));
      return;
    }
    if (data?.error) {
      setPastReviewStatus(data.error || "Could not submit review.");
      return;
    }

    setPastReviewStatus("Thanks for your review!");
    setPastReviewComment("");
    setSelectedPastReviewClassId("");
    await loadReviews();
  };

  /* =========================
     Booking
  ========================= */
  const hasUserBooked = (id) =>
    bookings.some(
      (b) => b.userId === user?.id && b.classId === id && b.status === "booked"
    );

  const hasUserOnWaitlist = (id, userId) =>
    (waitlists[id] || []).some((entry) => entry === userId);

  const getOverdueFailedMembershipPayment = (userId) => {
    const graceDays = 5;
    const now = Date.now();
    const rows = buildMembershipPaymentHistoryViewModel(membershipPaymentHistory[userId] || []);
    const overdueRows = rows.filter((row) => {
      if (!row?.canRetry) return false;
      const source = (membershipPaymentHistory[userId] || []).find((raw) => raw.id === row.id);
      const createdAt = source?.createdAt || source?.updatedAt || null;
      const timestamp = createdAt ? new Date(createdAt).getTime() : NaN;
      if (!Number.isFinite(timestamp)) return false;
      return now - timestamp >= graceDays * 24 * 60 * 60 * 1000;
    });

    if (overdueRows.length === 0) return null;
    overdueRows.sort((a, b) => String(a.period || "").localeCompare(String(b.period || "")));
    return overdueRows[0];
  };

  const addBooking = async ({ userId, classId, creditSource }) => {
    const { error } = await supabase.from("bookings").insert({
      user_id: userId,
      class_id: classId,
      credit_source: creditSource,
      status: "booked",
    });
    if (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      alert(error.message || "Could not reserve the class.");
      return false;
    }
    await loadBookingsData();
    await loadCoreData();
    return true;
  };

  const openAdminAddUserModal = (cls) => {
    if (!isAdmin || !cls?.id) return;
    setAdminAddClass(cls);
    setAdminAddSearchDraft("");
    setAdminAddSearchTerm("");
    setAdminAddCreditSource("regular");
    setAdminAddLoadingUserId("");
    setAdminAddStatus("");
  };

  const closeAdminAddUserModal = () => {
    setAdminAddClass(null);
    setAdminAddSearchDraft("");
    setAdminAddSearchTerm("");
    setAdminAddCreditSource("regular");
    setAdminAddLoadingUserId("");
    setAdminAddStatus("");
  };

  const adminAddUserToClass = async (targetUserId) => {
    if (!isAdmin || !adminAddClass?.id || !targetUserId) return;
    if (adminAddBookedUserIds.has(targetUserId)) {
      setAdminAddStatus("This member is already booked for this class.");
      return;
    }

    const seatsNeeded = adminAddCreditSource === "amiga_ticket" ? 2 : 1;
    const seatsLeft = Math.max(
      0,
      Number(adminAddClass.spots || 0) - getBookedCount(adminAddClass.id)
    );
    if (adminAddClass.isCancelled) {
      setAdminAddStatus("This class is cancelled.");
      return;
    }
    if (seatsLeft < seatsNeeded) {
      setAdminAddStatus(
        adminAddCreditSource === "amiga_ticket"
          ? "Not enough spots left for an Amiga ticket."
          : "No spots left in this class."
      );
      return;
    }

    setAdminAddLoadingUserId(targetUserId);
    setAdminAddStatus("");
    try {
      const creditSource =
        adminAddCreditSource === "amiga_ticket" ? "admin_amiga_ticket" : "admin";
      const authHeaders = await getAuthHeaders();
      const { data, error } = await supabase.functions.invoke("admin-mobile-action", {
        headers: authHeaders,
        body: {
          action: "admin_add_class_booking",
          payload: {
            userId: targetUserId,
            classId: adminAddClass.id,
            creditSource,
          },
        },
      });
      if (error || data?.error) {
        setAdminAddStatus(formatInvokeError(error, data));
        return;
      }
      await loadBookingsData();
      await loadCoreData();
      await handleBookingConfirmed({
        userId: targetUserId,
        classId: adminAddClass.id,
        creditSource,
      });
      setAdminAddStatus(
        `${getProfileDisplayName(profiles[targetUserId], targetUserId)} added${
          creditSource === "admin_amiga_ticket" ? " with an Amiga ticket" : ""
        }.`
      );
      setAdminAddSearchTerm(adminAddSearchDraft.trim());
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setAdminAddStatus(error?.message || "Could not add this member to the class.");
    } finally {
      setAdminAddLoadingUserId("");
    }
  };

  const adminRemoveManualBooking = async (entry, cls) => {
    if (!isAdmin || !entry?.bookingId || !cls?.id || !entry.removable) return;
    const confirmed = window.confirm(`Remove ${entry.name} from "${cls.name}"?`);
    if (!confirmed) return;
    try {
      const authHeaders = await getAuthHeaders();
      const { data, error } = await supabase.functions.invoke("admin-mobile-action", {
        headers: authHeaders,
        body: {
          action: "admin_remove_class_booking",
          payload: { bookingId: entry.bookingId },
        },
      });
      if (error || data?.error) {
        alert(formatInvokeError(error, data));
        return;
      }
      await loadBookingsData();
      await loadCoreData();
      logAction({
        actor: user.email,
        action: "ADMIN_REMOVE_CLASS_BOOKING",
        details: {
          bookingId: entry.bookingId,
          targetUser: profiles[entry.userId]?.email || entry.userId,
          classId: cls.id,
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      alert(error?.message || "Could not remove this manually added booking.");
    }
  };

  const cancelBookingEntry = async ({ userId, classId }) => {
    const { data, error } = await supabase
      .from("bookings")
      .update({ status: "cancelled", cancelled_at: new Date().toISOString() })
      .eq("user_id", userId)
      .eq("class_id", classId)
      .eq("status", "booked")
      .select("id")
      .maybeSingle();
    if (error) {
      throw new Error(error.message || "Could not cancel the booking.");
    }
    if (!data) {
      throw new Error("Booking was not found or was already cancelled.");
    }
    await loadBookingsData();
    await loadCoreData();
  };

  const addToWaitlist = async ({ userId, classId }) => {
    const { error } = await supabase.from("waitlist").insert({
      user_id: userId,
      class_id: classId,
    });
    if (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      alert(error.message || "Could not join the waitlist.");
      return false;
    }
    await loadBookingsData();
    return true;
  };

  const removeFromWaitlist = async ({ userId, classId }) => {
    await supabase.from("waitlist").delete().eq("user_id", userId).eq("class_id", classId);
    await loadBookingsData();
  };

  const updateMembershipUsage = async (userId, delta) => {
    const period = new Date().toISOString().slice(0, 7);
    const current = membershipUsage[userId]?.[period] || 0;
    const next = Math.max(0, current + delta);
    setMembershipUsage((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || {}),
        [period]: next,
      },
    }));
    const { error } = await supabase
      .from("membership_usage")
      .upsert({ user_id: userId, period, used: next }, { onConflict: "user_id,period" });
    if (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const handleBookingConfirmed = async ({ userId, classId, creditSource }) => {
    if (!isAdmin && creditSource === "membership") {
      await updateMembershipUsage(userId, +1);
    } else if (!isAdmin && creditSource === "amiga_ticket") {
      await addAmigaTicketEntry({
        userId,
        delta: -1,
        reason: "BOOK_CLASS",
      });
    } else if (!isAdmin) {
      await addCreditEntry({
        userId,
        delta: -1,
        reason: "BOOK_CLASS",
      });
    }
    logAction({
      actor: profiles[userId]?.email || userId,
      action: "BOOK_CLASS",
      details: { classId },
    });
    enqueueNotification({
      type: "BOOKING_CONFIRMED",
      userId,
      classId,
      meta: { email: profiles[userId]?.email || userId },
    });
    sendTransactionalEmail({
      templateKey: "class_reserved",
      userId,
      data: { ...getClassEmailData(classId), site_url: window.location.origin },
    });
    if (!isAdmin && creditSource === "purchased" && getPurchasedBalance(userId) === 2) {
      sendTransactionalEmail({
        templateKey: "credits_low",
        userId,
        data: {
          credits_remaining: 1,
          site_url: window.location.origin,
        },
      });
    }
  };

  const handleBookingCancelled = async ({ userId, classId, creditSource }) => {
    if (["admin", "admin_amiga_ticket"].includes(creditSource)) {
      // Admin-created bookings are complimentary and should not create credit refunds.
    } else if (!isAdmin && creditSource === "membership") {
      await updateMembershipUsage(userId, -1);
    } else if (!isAdmin && creditSource === "amiga_ticket") {
      await addAmigaTicketEntry({
        userId,
        delta: +1,
        reason: "CANCEL_CLASS",
      });
    } else if (!isAdmin) {
      const expiresAt = new Date();
      expiresAt.setMonth(expiresAt.getMonth() + 12);
      await addCreditEntry({
        userId,
        delta: +1,
        reason: "CANCEL_CLASS",
        expiresAt: expiresAt.toISOString(),
      });
    }
    logAction({
      actor: profiles[userId]?.email || userId,
      action: "CANCEL_CLASS",
      details: { classId },
    });
    enqueueNotification({
      type: "BOOKING_CANCELLED",
      userId,
      classId,
      meta: { email: profiles[userId]?.email || userId },
    });
  };

  const handleWaitlistJoined = ({ userId, classId }) => {
    logAction({
      actor: profiles[userId]?.email || userId,
      action: "JOIN_WAITLIST",
      details: { classId },
    });
  };

  const handleWaitlistLeft = ({ userId, classId }) => {
    logAction({
      actor: profiles[userId]?.email || userId,
      action: "LEAVE_WAITLIST",
      details: { classId },
    });
  };

  const reserveSpot = async (id, options = {}) => {
    const cls = classes.find((c) => c.id === id);
if (!cls) return;
    const creditSourceOverride = options.creditSource;
    const seatsNeeded = creditSourceOverride === "amiga_ticket" ? 2 : 1;
    const seatsLeft = cls.spots - getBookedCount(id);
    if (isClassPast(cls)) {
      alert("This class already happened.");
      return;
    }
    if (isClassOngoing(cls)) {
      alert("This class is already in progress.");
      return;
    }
    if (cls.isCancelled) {
      alert("This class has been cancelled.");
      return;
    }

    if (!isAdmin) {
      const overduePayment = getOverdueFailedMembershipPayment(user?.id);
      if (overduePayment) {
        alert(
          `Please complete your overdue membership payment (${overduePayment.periodLabel}) to book classes. You had 5 days to complete this payment.`
        );
        setView("account");
        setUserAccountTab("credits");
        return;
      }
    }

    if (hasUserBooked(id)) return;
    if (hasUserOnWaitlist(id, user.id)) return;

    if (creditSourceOverride === "amiga_ticket") {
      if (getAmigaTicketBalance(user.id) <= 0) {
        alert("You don't have an Amiga ticket available.");
        return;
      }
      if (seatsLeft < seatsNeeded) {
        alert("Not enough spots left for an Amiga ticket.");
        return;
      }
    }

    if (isClassFull(cls, getBookedCount(id))) {
      if (creditSourceOverride === "amiga_ticket") {
        alert("Not enough spots left for an Amiga ticket.");
        return;
      }
  const joined = await addToWaitlist({ userId: user.id, classId: id });
  if (joined) {
    handleWaitlistJoined({ userId: user.id, classId: id });
    alert("Class is full. You’ve been added to the waitlist.");
  }
  return;
}


    if (!isAdmin && creditSourceOverride !== "amiga_ticket" && getAvailableCredits(user.id) <= 0) {
      setShowNoCreditsModal(true);
      return;
    }

    const creditSource =
      creditSourceOverride ||
      (!isAdmin && getMembershipRemaining(user.id) > 0 ? "membership" : "purchased");
    const booked = await addBooking({ userId: user.id, classId: id, creditSource });
    if (booked) {
      await handleBookingConfirmed({ userId: user.id, classId: id, creditSource });
    }
  };

  const leaveWaitlist = async (id) => {
    if (!user?.id) return;

    const waitlist = waitlists[id] || [];
    if (!waitlist.includes(user.id)) return;

    await removeFromWaitlist({ userId: user.id, classId: id });

    handleWaitlistLeft({ userId: user.id, classId: id });
  };

  const openCreditsMembershipsTab = () => {
    setShowNoCreditsModal(false);
    setSelectedClass(null);
    setView("account");
    setUserAccountTab("credits");
  };

  const cancelBooking = async (id) => {
    if (!user?.id) return;
    const cls = classes.find((c) => c.id === id);
    if (!cls) return;
    if (!hasUserBooked(id)) return;

    try {
      if (!isAdmin) {
        const classStart = getClassStartDateTime(cls);
        if (classStart) {
          const cutoff = new Date(classStart.getTime() - cancelPolicyHours * 60 * 60 * 1000);
          if (new Date() > cutoff) {
            alert(`Cancellations are only allowed ${cancelPolicyHours}h before class.`);
            return;
          }
        }
        const activeBooking = bookings.find(
          (b) => b.userId === user.id && b.classId === id && b.status === "booked"
        );
        const creditSource = activeBooking?.creditSource || "purchased";
        await cancelBookingEntry({ userId: user.id, classId: id });
        await handleBookingCancelled({ userId: user.id, classId: id, creditSource });
        await autoPromoteWaitlist(id);
        return;
      }
      const activeBooking = bookings.find(
        (b) => b.userId === user.id && b.classId === id && b.status === "booked"
      );
      const creditSource = activeBooking?.creditSource || "purchased";
      await cancelBookingEntry({ userId: user.id, classId: id });
      await handleBookingCancelled({ userId: user.id, classId: id, creditSource });
      await autoPromoteWaitlist(id);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      alert(error?.message || "Could not cancel the booking. Please try again.");
    }
  };

  const refundForClassCancellation = async ({ userId, classId, creditSource }) => {
    if (["admin", "admin_amiga_ticket"].includes(creditSource)) {
      enqueueNotification({
        type: "CLASS_CANCELLED",
        userId,
        classId,
        meta: { email: profiles[userId]?.email || userId },
      });
      sendTransactionalEmail({
        templateKey: "class_cancelled",
        userId,
        data: { ...getClassEmailData(classId), site_url: window.location.origin },
      });
      return;
    }
    if (creditSource === "membership") {
      await updateMembershipUsage(userId, -1);
    } else if (creditSource === "amiga_ticket") {
      await addAmigaTicketEntry({
        userId,
        delta: +1,
        reason: "CLASS_CANCELLED",
      });
    } else {
      const expiresAt = new Date();
      expiresAt.setMonth(expiresAt.getMonth() + 12);
      await addCreditEntry({
        userId,
        delta: +1,
        reason: "CLASS_CANCELLED",
        expiresAt: expiresAt.toISOString(),
      });
    }
    enqueueNotification({
      type: "CLASS_CANCELLED",
      userId,
      classId,
      meta: { email: profiles[userId]?.email || userId },
    });
    sendTransactionalEmail({
      templateKey: "class_cancelled",
      userId,
      data: { ...getClassEmailData(classId), site_url: window.location.origin },
    });
  };

  const cancelClassByAdmin = async (cls) => {
    if (!cls) return;
    if (cls.isCancelled) return;

    setClasses((prev) =>
      prev.map((c) => (c.id === cls.id ? { ...c, isCancelled: true } : c))
    );

    await supabase
      .from("classes")
      .update({ is_cancelled: true })
      .eq("id", cls.id);

    const affectedBookings = bookings.filter(
      (b) => b.classId === cls.id && b.status === "booked"
    );

    await Promise.all(
      affectedBookings.map(async (b) => {
        await cancelBookingEntry({ userId: b.userId, classId: cls.id });
        await refundForClassCancellation({
          userId: b.userId,
          classId: cls.id,
          creditSource: b.creditSource,
        });
      })
    );

    await supabase.from("waitlist").delete().eq("class_id", cls.id);
    await loadBookingsData();

    logAction({
      actor: user.email,
      action: "CANCEL_CLASS_ADMIN",
      details: { classId: cls.id, name: cls.name, date: cls.date },
    });

    await loadCoreData();
  };

  const deleteClassByAdmin = async (cls) => {
    if (!cls?.id) return;

    const { data, error } = await supabase.rpc("app_delete_class_and_refund", {
      p_class_id: cls.id,
    });
    if (error) {
      throw new Error(error.message || "Could not delete the class.");
    }

    await loadBookingsData();
    await loadCoreData();

    logAction({
      actor: user.email,
      action: "DELETE_CLASS",
      details: { classId: cls.id, name: cls.name, date: cls.date },
    });

    return data;
  };

  const updateGhostSpots = async (cls, nextValue) => {
    if (!cls?.id || !isAdmin) return;
    const nextGhostSpots = Math.max(0, Number(nextValue) || 0);
    const { error } = await supabase
      .from("classes")
      .update({ ghost_spots: nextGhostSpots })
      .eq("id", cls.id);
    if (error) {
      alert(error.message || "Could not update ghost spots.");
      return;
    }

    setClasses((prev) =>
      prev.map((entry) => (entry.id === cls.id ? { ...entry, ghostSpots: nextGhostSpots } : entry))
    );
    setSelectedClass((prev) =>
      prev?.id === cls.id ? { ...prev, ghostSpots: nextGhostSpots } : prev
    );

    logAction({
      actor: user.email,
      action: "EDIT_CLASS",
      details: { classId: cls.id, field: "ghostSpots", value: nextGhostSpots },
    });
  };



  /* =========================
     Admin: Add / Edit / Repeat
  ========================= */
  const addClass = async () => {
    if (
      !newClass.name ||
      !newClass.date ||
      !newClass.time ||
      !newClass.spots ||
      !newClass.location
    ) return;

    const fail = (error) => {
      if (!error) return false;
      // eslint-disable-next-line no-console
      console.error(error);
      alert(error.message || "Failed to save class.");
      return true;
    };

    const locationId = locationMap[newClass.location] || null;
    const teacherId = newClass.teacher ? teacherMap[newClass.teacher] || null : null;
    if (!locationId) {
      alert("Please select a valid location.");
      return;
    }

    if (editingClass) {
      if (editSeries) {
        const baseDate = new Date(editingClass.date).toISOString().slice(0, 10);
        const { data: series } = await supabase
          .from("classes")
          .select("id")
          .eq("name", editingClass.name)
          .gte("class_date", baseDate);
        if (Array.isArray(series)) {
          const results = await Promise.all(
            series.map((item) =>
              supabase
                .from("classes")
                .update({
                  name: newClass.name,
                  class_date: newClass.date,
                  class_time: newClass.time,
                  spots: Number(newClass.spots),
                  location_id: locationId,
                  teacher_id: teacherId,
                  class_color: newClass.color || "#f4c430",
                })
                .eq("id", item.id)
            )
          );
          if (results.some((res) => res.error)) {
            const firstError = results.find((res) => res.error)?.error;
            if (fail(firstError)) return;
          }
        }
      } else {
        const { error } = await supabase
          .from("classes")
          .update({
            name: newClass.name,
            class_date: newClass.date,
            class_time: newClass.time,
            spots: Number(newClass.spots),
            location_id: locationId,
            teacher_id: teacherId,
            class_color: newClass.color || "#f4c430",
          })
          .eq("id", editingClass.id);
        if (fail(error)) return;
      }
      if (editSeries) {
        logAction({
          actor: user.email,
          action: "EDIT_CLASS_SERIES",
          details: { name: editingClass.name, fromDate: editingClass.date },
        });
      }
    } else {
      const repeats = Math.max(1, newClass.repeatWeeks || 1);
      const baseDate = new Date(newClass.date);

      const entries = Array.from({ length: repeats }).map((_, i) => {
        const d = new Date(baseDate);
        d.setDate(d.getDate() + i * 7);
        return {
          name: newClass.name,
          class_date: toISODate(d),
          class_time: newClass.time,
          spots: Number(newClass.spots),
          location_id: locationId,
          teacher_id: teacherId,
          class_color: newClass.color || "#f4c430",
          is_cancelled: false,
        };
      });

      const { error } = await supabase.from("classes").insert(entries);
      if (fail(error)) return;
    }

    logAction({
      actor: user.email,
      action: editingClass ? "EDIT_CLASS" : "CREATE_CLASS",
      details: {
        name: newClass.name,
        date: newClass.date,
        location: newClass.location,
        teacher: newClass.teacher || "",
      },
    });

    await loadCoreData();

    setEditingClass(null);
    setEditSeries(false);
    setAdminDate(null);
      setNewClass({
        name: "",
        date: "",
        time: "",
        spots: "",
        location: "",
        teacher: "",
        color: "#f4c430",
        repeatWeeks: 1,
      });
    setIsCustomTime(false);

    
  };

  /* =========================
     Calendar
  ========================= */
  const monthDays = useMemo(() => getMonthDays(selectedMonth), [selectedMonth]);

  const monthGrid = useMemo(() => {
    const firstDayIndex = weekdayIndexMonFirst(monthDays[0]);
    const leading = Array.from({ length: firstDayIndex }).map(() => null);
    const cells = [...leading, ...monthDays];
    return Array.from({ length: Math.ceil(cells.length / 7) * 7 }).map(
      (_, i) => cells[i] || null
    );
  }, [monthDays]);

  const weekDays = useMemo(() => {
    const start = getWeekStartMonday(selectedDate);
    if (!start) return [];
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [selectedDate]);

  const weekClassCounts = useMemo(() => {
    if (scheduleViewMode !== "week") return {};
    const counts = {};
    weekDays.forEach((d) => {
      const iso = toISODate(d);
      counts[iso] = 0;
    });
    classes.forEach((c) => {
      if (counts[c.date] !== undefined) counts[c.date] += 1;
    });
    return counts;
  }, [scheduleViewMode, weekDays, classes]);

  const monthColumnTemplate = useMemo(() => {
    if (scheduleViewMode !== "month") return null;

    const classesByDate = classes.reduce((acc, cls) => {
      if (!acc[cls.date]) acc[cls.date] = [];
      acc[cls.date].push(cls);
      return acc;
    }, {});

    const estimateCompactCardWidth = (cls) => {
      const spotsLeft = Math.max(0, Number(cls.spots) || 0);
      const timeText = String(cls.time || "");
      const spotsText = `${spotsLeft} left`;
      const classCountBonus = 8;

      const textUnits =
        String(cls.name || "").length * 7.6 +
        timeText.length * 5.6 +
        spotsText.length * 5.4;

      const shellWidth = 64;
      const metaWidth = classCountBonus;

      return Math.min(
        340,
        Math.max(94, Math.ceil(shellWidth + textUnits + metaWidth))
      );
    };

    const columnRequiredWidths = Array.from({ length: 7 }).map((_, columnIndex) => {
      let maxCount = 0;
      let maxWidth = 96;

      for (let cellIndex = columnIndex; cellIndex < monthGrid.length; cellIndex += 7) {
        const date = monthGrid[cellIndex];
        if (!date) continue;

        const iso = toISODate(date);
        const dayClasses = classesByDate[iso] || [];
        maxCount = Math.max(maxCount, dayClasses.length);

        const dateMaxWidth = dayClasses.reduce(
          (largest, cls) => Math.max(largest, estimateCompactCardWidth(cls)),
          96
        );
        maxWidth = Math.max(maxWidth, dateMaxWidth);
      }

      return { maxCount, maxWidth };
    });

    const widestActiveColumn = columnRequiredWidths.reduce((largest, { maxCount, maxWidth }) => {
      if (maxCount === 0) return largest;
      return Math.max(largest, maxWidth);
    }, 96);

    return columnRequiredWidths
      .map(({ maxCount }) => {
        if (maxCount === 0) return "minmax(88px, 0.68fr)";

        const minWidth = Math.max(124, Math.min(304, widestActiveColumn + 4));
        const flexWeight = Math.min(2.55, 0.96 + widestActiveColumn / 240);
        return `minmax(${Math.round(minWidth)}px, ${flexWeight.toFixed(2)}fr)`;
      })
      .join(" ");
  }, [scheduleViewMode, monthGrid, classes]);

  const weekColumnTemplate = useMemo(() => {
    if (scheduleViewMode !== "week") return null;
    return weekDays
      .map((d) => {
        const count = weekClassCounts[toISODate(d)] || 0;
        const hasClasses = count > 0;
        const weight = hasClasses ? 1.45 : 0.7;
        const minWidth = hasClasses ? 176 : 120;
        return `minmax(${minWidth}px, ${weight}fr)`;
      })
      .join(" ");
  }, [scheduleViewMode, weekDays, weekClassCounts]);

  const calendarColumnTemplate =
    scheduleViewMode === "week" ? weekColumnTemplate : monthColumnTemplate;

  const renderClassItem = (c, options = {}) => {
    const variant = options.variant || "full";
    const isBooked = hasUserBooked(c.id);
    const realBookedCount = getBookedCount(c.id);
    const ghostSpotCount = getGhostSpotCount(c.id);
    const displayGhostSpotCount = getDisplayGhostSpotCount(c);
    const isFull = realBookedCount >= c.spots;
    const isCancelled = c.isCancelled;
    const spotsLeft = getDisplaySpotsLeft(c);
    const isPopular = spotsLeft <= 3 && spotsLeft > 0 && !isCancelled;
    const range = getClassTimeRange(c.date, c.time);
    const now = new Date();
    const isOngoing =
      range && range.start && range.end
        ? now >= range.start && now <= range.end
        : false;
    const attendanceList = getAttendanceList(c.id);

    const waitlist = waitlists[c.id] || [];
    const waitlistIndex = waitlist.findIndex((entry) => entry === user.id);
    const isOnWaitlist = waitlistIndex !== -1;

    if (variant === "compact") {
      return (
      <div
        key={c.id}
        className={`class-item calendar-class compact ${isFull ? "is-full" : ""} ${
          isOnWaitlist ? "on-waitlist" : ""
        } ${isCancelled ? "is-cancelled" : ""}`}
        style={{ "--class-bar": c.color || "#f4c430" }}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedClass(c);
        }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setSelectedClass(c);
            }
          }}
        >
          <span className="compact-line">
            <strong>{c.name}</strong>
            {isBooked && <span className="compact-tag booked">Booked</span>}
            {isOngoing && <span className="compact-tag ongoing">Ongoing</span>}
            {isPopular && <span className="compact-tag popular">Popular</span>}
            <span className="compact-separator">•</span>
            <span>{c.time}</span>
            <span className="compact-separator">•</span>
            <span>{spotsLeft} left</span>
          </span>
        </div>
      );
    }

    return (
      <div
        key={c.id}
        className={`class-item calendar-class ${variant === "modal" ? "modal-class" : ""} ${
          isFull ? "is-full" : ""
        } ${
          isOnWaitlist ? "on-waitlist" : ""
        } ${isCancelled ? "is-cancelled" : ""}`}
        style={{ "--class-bar": c.color || "#f4c430" }}
      >
        {effectiveIsAdmin && (
          <div className="admin-actions">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedClass(null);
                setEditingClass(c);
                setAdminDate(c.date);
                setNewClass({
                  ...c,
                  teacher: c.teacher || "",
                  color: c.color || "#f4c430",
                  repeatWeeks: 1,
                });
                setIsCustomTime(c.time && !timeSlots.includes(c.time));
                scrollToAdminForm();
              }}
            >
              Edit
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedClass(null);
                setEditingClass(null);
                setAdminDate(c.date);
                setNewClass({
                  ...c,
                  teacher: c.teacher || "",
                  color: c.color || "#f4c430",
                  repeatWeeks: 1,
                });
                setIsCustomTime(c.time && !timeSlots.includes(c.time));
                scrollToAdminForm();
                logAction({
                  actor: user.email,
                  action: "DUPLICATE_CLASS",
                  details: { classId: c.id, name: c.name, date: c.date },
                });
              }}
            >
              Duplicate
            </Button>
            {!isCancelled && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  const confirmed = window.confirm(
                    `Cancel class "${c.name}" on ${c.date} at ${c.time}?`
                  );
                  if (!confirmed) return;
                  cancelClassByAdmin(c);
                }}
              >
                Cancel class
              </Button>
            )}
            {!isCancelled && (
              <div className="tooltip-wrapper">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    const remaining = Math.max(0, getDisplaySpotsLeft(c));
                    if (remaining <= 0) return;
                    const confirmed = window.confirm(
                      `Send attention call for "${c.name}" on ${c.date} at ${c.time}? This will email all registered users except those already booked.\n\nSpots left: ${remaining}.`
                    );
                    if (!confirmed) return;
                    sendAttentionCall(c);
                  }}
                  disabled={spotsLeft <= 0}
                >
                  Attention call
                </Button>
                {spotsLeft <= 0 && <span className="tooltip-bubble">Class is full</span>}
              </div>
            )}
            <Button
              onClick={async (e) => {
                e.stopPropagation();
                setAttendanceOpen((prev) => ({
                  ...prev,
                  [c.id]: !prev[c.id],
                }));
              }}
            >
              {attendanceOpen[c.id] ? "Hide attendance" : "Attendance"}
            </Button>
            {!isCancelled && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  openAdminAddUserModal(c);
                }}
              >
                Add member
              </Button>
            )}
            <Button
              onClick={async (e) => {
                e.stopPropagation();
                const { data: bookedRows } = await supabase
                  .from("bookings")
                  .select("user_id,class_id,credit_source")
                  .eq("class_id", c.id)
                  .eq("status", "booked");
                const bookedCount = Array.isArray(bookedRows) ? bookedRows.length : 0;
                const confirmed = window.confirm(
                  `Delete class "${c.name}" on ${c.date} at ${c.time}? This will refund ${bookedCount} reservation${bookedCount === 1 ? "" : "s"}.`
                );
                if (!confirmed) return;

                try {
                  await deleteClassByAdmin(c);
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.error(error);
                  alert(error?.message || "Could not delete the class. Please try again.");
                }
              }}
            >
              Delete
            </Button>
          </div>
        )}

        <div>
          <strong>{c.name}</strong>
          <div className="class-tags">
            {isBooked && <span className="badge badge-booked">Booked</span>}
            {isCancelled && <span className="badge badge-cancelled">Cancelled</span>}
            {isOngoing && <span className="badge badge-ongoing">Ongoing</span>}
            {isPopular && <span className="badge badge-popular">Popular</span>}
            {isFull && (
              <span className="badge badge-full">
                Full
              </span>
            )}
            {isOnWaitlist && (
              <span className="badge badge-waitlist">
                On waitlist
              </span>
            )}
          </div>
        </div>
        <div>📍 {c.location}</div>
        {c.teacher && <div>👩‍🏫 {c.teacher}</div>}
        <div>{c.time}</div>
        <div>{spotsLeft} spots left</div>

        {effectiveIsAdmin && variant === "modal" && (
          <div className="class-ghost-controls">
            <span>Ghosts: {ghostSpotCount}</span>
            {ghostSpotCount !== displayGhostSpotCount && (
              <span className="class-ghost-note">Showing {displayGhostSpotCount}</span>
            )}
            <Button
              className="ghost-button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                updateGhostSpots(c, ghostSpotCount + 1);
              }}
            >
              + Add ghost
            </Button>
            <Button
              className="ghost-button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                updateGhostSpots(c, ghostSpotCount - 1);
              }}
              disabled={ghostSpotCount <= 0}
            >
              - Remove ghost
            </Button>
          </div>
        )}

        {(realBookedCount > 0 || ghostSpotCount > 0 || waitlist.length > 0) && (
          <div className="waitlist-meta">
            {effectiveIsAdmin ? realBookedCount : getDisplayBookedCount(c)} attending
            {effectiveIsAdmin && displayGhostSpotCount > 0 ? ` • ${displayGhostSpotCount} ghosts shown` : ""}
            {(isFull || waitlist.length > 0) ? ` • ${waitlist.length} on waitlist` : ""}
          </div>
        )}

          {effectiveIsAdmin && attendanceOpen[c.id] && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
                Reserved students
              </div>
              {attendanceList.length === 0 ? (
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  No reservations yet.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 6 }}>
                  {attendanceList.map((entry, idx) => (
                    <div key={`${c.id}-${idx}`} className="attendance-row">
                      <div className="attendance-row-main">
                        <span>{entry.name}</span>
                        {entry.tag && <span className="attendance-tag">{entry.tag}</span>}
                      </div>
                      {entry.removable && (
                        <Button
                          className="ghost-button attendance-remove-button"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            adminRemoveManualBooking(entry, c);
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <Button
                className="ghost-button"
                onClick={async (e) => {
                  e.stopPropagation();
                  const { data: bookedRows } = await supabase
                    .from("bookings")
                    .select("user_id,class_id,credit_source")
                    .eq("class_id", c.id)
                    .eq("status", "booked");
                  const bookedCount = Array.isArray(bookedRows) ? bookedRows.length : 0;
                  const confirmed = window.confirm(
                    `Reset attendance for "${c.name}" on ${c.date}? This will cancel ${bookedCount} reservation${bookedCount === 1 ? "" : "s"} and clear the waitlist.`
                  );
                  if (!confirmed) return;

                  if (Array.isArray(bookedRows)) {
                    await Promise.all(
                      bookedRows.map(async (b) => {
                        await cancelBookingEntry({ userId: b.user_id, classId: b.class_id });
                        await refundForClassCancellation({
                          userId: b.user_id,
                          classId: b.class_id,
                          creditSource: b.credit_source,
                        });
                      })
                    );
                  }

                  await supabase.from("waitlist").delete().eq("class_id", c.id);
                  await loadBookingsData();
                  await loadCoreData();

                  logAction({
                    actor: user.email,
                    action: "RESET_CLASS_ATTENDANCE",
                    details: { classId: c.id, name: c.name, date: c.date },
                  });
                }}
                style={{ marginTop: 8 }}
              >
                Reset attendance
              </Button>
            </div>
          )}

        {(() => {
          const isPast = isClassPast(c);

          if (isPast) {
            return (
              <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>
                Class already happened
              </div>
            );
          }

          if (isCancelled) {
            return (
              <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>
                Class cancelled
              </div>
            );
          }

          if (isBooked) {
            return (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  cancelBooking(c.id);
                }}
              >
                Cancel
              </Button>
            );
          }

          const canUseAmigaTicket = user?.id && getAmigaTicketBalance(user.id) > 0;
          const amigaDisabled = spotsLeft < 2 || isFull || isOnWaitlist;

          return (
            <div className="class-action-stack">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  if (isOnWaitlist) {
                    leaveWaitlist(c.id);
                    return;
                  }
                  reserveSpot(c.id);
                }}
              >
                {isOnWaitlist ? "Leave waitlist" : isFull ? "Join waitlist" : "Reserve"}
              </Button>
              {canUseAmigaTicket && (
                <Button
                  className="ghost-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    reserveSpot(c.id, { creditSource: "amiga_ticket" });
                  }}
                  disabled={amigaDisabled}
                >
                  Reserve with Amiga ticket
                </Button>
              )}
            </div>
          );
        })()}
      </div>
    );
  };

  /* =========================
     Login screen
  ========================= */
  if (!user) {
    return (
      <div className="min-h-screen" style={appThemeStyle}>
        <div className="login-box fade-in">
          <Card className="login-card">
          <div className="login-tabs">
            <button
              type="button"
              className={`login-tab ${publicView === "login" ? "active" : ""}`}
              onClick={() => setPublicView("login")}
            >
              Login
            </button>
            <button
              type="button"
              className={`login-tab ${publicView === "prices" ? "active" : ""}`}
              onClick={() => setPublicView("prices")}
            >
              Prices
            </button>
            <button
              type="button"
              className={`login-tab ${publicView === "reviews" ? "active" : ""}`}
              onClick={() => {
                setPublicView("reviews");
                loadReviews();
              }}
            >
              Reviews
            </button>
          </div>

          {publicView === "login" && (
            <>
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <img src={logoSrc} alt={appStyling.brandName} style={{ height: 72 }} />
              </div>
              <h2><UserIcon /> {authMode === "login" ? "Login" : "Create account"}</h2>
              <div className="login-form">
                {pendingCheckoutIntent && (
                  <div
                    style={{
                      fontSize: 12,
                      padding: "10px 12px",
                      border: "1px solid rgba(255,255,255,0.12)",
                      borderRadius: 12,
                      opacity: 0.85,
                    }}
                  >
                    Step 1 of 2: {authMode === "login" ? "log in" : "create your account"}.
                    <br />
                    Step 2 of 2: continue to Mollie payment for{" "}
                    {describeCheckoutIntent(pendingCheckoutIntent, membershipPlans)}.
                  </div>
                )}
                <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                {authMode === "register" && (
                  <>
                    <Input
                      placeholder="Full name"
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                    />
                    <Input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        if (authMode === "login") login();
                        if (authMode === "register") register();
                      }}
                    />
                    <Input
                      type="password"
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <label className="email-checkbox" style={{ fontSize: 12, lineHeight: 1.4 }}>
                      <input
                        type="checkbox"
                        checked={registerAcceptedTerms}
                        onChange={(e) => setRegisterAcceptedTerms(e.target.checked)}
                      />
                      <span>
                        I declare that I've read and agree with the{" "}
                        <a
                          href="https://baileproject.nl/privacy"
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "#ef4444" }}
                        >
                          general and privacy terms
                        </a>
                        .
                      </span>
                    </label>
                  </>
                )}
                {authMode === "login" && (
                  <Input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      login();
                    }}
                  />
                )}
                {authError && (
                  <div style={{ fontSize: 12, color: "#fca5a5" }}>{authError}</div>
                )}
                {authNotice && (
                  <div style={{ fontSize: 12, opacity: 0.7 }}>{authNotice}</div>
                )}
                {authMode === "login" ? (
                  <>
                    <Button onClick={login} disabled={authLoading}>
                      {authLoading ? (
                        <>
                          <span className="spinner" /> Signing in...
                        </>
                      ) : (
                        pendingCheckoutIntent ? "Log in, then pay" : "Enter the studio"
                      )}
                    </Button>
                    <Button
                      className="ghost-button"
                      onClick={sendPasswordReset}
                      disabled={authLoading}
                    >
                      Forgot password?
                    </Button>
                    <Button
                      className="ghost-button"
                      onClick={() => setAuthMode("register")}
                      disabled={authLoading}
                    >
                      Create an account
                    </Button>
                  </>
                ) : (
                  <>
                    <Button onClick={register} disabled={authLoading}>
                      {authLoading ? (
                        <>
                          <span className="spinner" /> Creating account...
                        </>
                      ) : (
                        pendingCheckoutIntent ? "Create account, then pay" : "Create account"
                      )}
                    </Button>
                    <Button
                      className="ghost-button"
                      onClick={() => setAuthMode("login")}
                      disabled={authLoading}
                    >
                      Back to login
                    </Button>
                  </>
                )}
              </div>
            </>
          )}

          {publicView === "prices" && (
            <div className="reviews-public">
              <h2>Prices</h2>
              <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 12 }}>
                See the offer first, then create your account only when you want to buy.
              </div>

              <div className="account-block" style={{ marginBottom: 12 }}>
                <h3 className="account-section-title">Single classes & credits</h3>
                <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 10 }}>
                  All prices shown include 21% VAT.
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                  {creditPackages.map((pack) => (
                    <div
                      key={`public-credit-${pack.amount}`}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        minWidth: 160,
                        flex: "1 1 160px",
                        padding: 14,
                        border: "1px solid rgba(255,255,255,0.12)",
                        borderRadius: 14,
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>{pack.label}</div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        {pack.amount === 1
                          ? "Ideal for a first class."
                          : "Flexible credits you can use when it suits you."}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800 }}>{pack.price}</div>
                      <Button onClick={() => purchaseCredits(pack)}>
                        Buy {pack.amount === 1 ? "1 class" : pack.label.toLowerCase()}
                      </Button>
                    </div>
                  ))}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      minWidth: 160,
                      flex: "1 1 160px",
                      padding: 14,
                      border: "1px solid rgba(255,255,255,0.12)",
                      borderRadius: 14,
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>{amigaTicketPack.label}</div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      Reserve 2 spots in one class for you and a guest.
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 800 }}>{amigaTicketPack.price}</div>
                    <Button onClick={purchaseAmigaTicket}>Buy Amiga ticket</Button>
                  </div>
                </div>
              </div>

              <div className="account-block">
                <h3 className="account-section-title">Memberships</h3>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>
                  Best for dancers who want monthly credits and a steadier rhythm.
                </div>
                {publicMembershipPlans.length === 0 ? (
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    No memberships are available right now.
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    {publicMembershipPlans.map((plan) => (
                      <div
                        key={`public-membership-${plan.id}`}
                        style={{
                          padding: 14,
                          border: plan.id === publicMembershipPlanId
                            ? "2px solid rgba(244,196,48,0.9)"
                            : "1px solid rgba(255,255,255,0.12)",
                          borderRadius: 14,
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                          <div>
                            <div style={{ fontWeight: 700 }}>{plan.name}</div>
                            <div style={{ fontSize: 12, opacity: 0.7 }}>
                              {plan.creditsPerMonth} credits / month • minimum {formatMonthCount(plan.minimumMonths)}
                            </div>
                          </div>
                          <div style={{ fontSize: 20, fontWeight: 800 }}>{formatEuro(plan.price)}</div>
                        </div>
                        <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <Button
                            className={plan.id === publicMembershipPlanId ? "" : "ghost-button"}
                            onClick={() => {
                              setPublicMembershipPlanId(plan.id);
                              subscribeMembership(plan);
                            }}
                          >
                            Become a member
                          </Button>
                          <Button
                            className="ghost-button"
                            type="button"
                            onClick={() =>
                              window.open(
                                "https://www.baileproject.com/become-a-baile-member",
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          >
                            Read more
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <label className="email-checkbox" style={{ alignItems: "flex-start", lineHeight: 1.4, marginTop: 12 }}>
                  <input
                    type="checkbox"
                    checked={membershipAcceptedTerms}
                    onChange={(e) => {
                      setMembershipAcceptedTerms(e.target.checked);
                      if (membershipCheckoutError) setMembershipCheckoutError("");
                    }}
                  />
                  <span>
                    I declare that I've read and agree with the{" "}
                    <a
                      href="https://baileproject.nl/privacy"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "#ef4444" }}
                    >
                      general and privacy terms
                    </a>{" "}
                    of Baile Project and Baile Project memberships.
                  </span>
                </label>
                {(checkoutError || membershipCheckoutError || authNotice) && (
                  <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
                    {membershipCheckoutError || checkoutError || authNotice}
                  </div>
                )}
              </div>
            </div>
          )}

          {publicView === "reviews" && (
            <div className="reviews-public">
              <h2>Reviews</h2>
              {reviews.length === 0 && (
                <p style={{ opacity: 0.6 }}>No reviews yet.</p>
              )}
              <div className="reviews-grid">
                {reviews.slice(0, 6).map((review) => (
                  <div key={review.id} className="review-card">
                    <div className="review-class">
                      {review.class_name || review.className || "Class"}
                    </div>
                    <div className="review-meta">
                      <span className="review-rating">
                        {getReviewEmoji(review.rating)}
                      </span>
                      <span>
                        {new Date(review.created_at || review.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="review-comment">{review.comment}</div>
                    <div className="review-author">Anonymous</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </Card>

          {reviewToken && (
            <Card className="review-form-card">
            <h3>Thanks for joining class</h3>
            <p style={{ fontSize: 12, opacity: 0.7 }}>
              We would love to hear your feedback.
            </p>
            <div className="form-field">
              <label>Rating</label>
              <div className="review-mood-picker">
                {REVIEW_MOODS.map((mood) => (
                  <button
                    key={mood.rating}
                    type="button"
                    className={`review-mood-button ${
                      reviewRating === mood.rating ? "active" : ""
                    }`}
                    onClick={() => setReviewRating(mood.rating)}
                    title={mood.label}
                    aria-label={`${mood.label} (${mood.rating})`}
                  >
                    <span>{mood.emoji}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="form-field">
              <label>Review</label>
              <textarea
                className="review-textarea"
                placeholder="Write a short review"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
              />
            </div>
            <Button onClick={submitReview}>Submit review</Button>
            {reviewStatus && (
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>{reviewStatus}</div>
            )}
            </Card>
          )}

        </div>
      </div>
    );
  }

  /* =========================
     Main app
  ========================= */
  const userReviewedClassIds = new Set(
    user?.email
      ? reviews
          .filter((review) => String(review.email || "").toLowerCase() === String(user.email).toLowerCase())
          .map((review) => review.class_id || review.classId)
          .filter(Boolean)
      : []
  );
  const reviewPromptWindowMs = 14 * 24 * 60 * 60 * 1000;
  const reviewPromptClass = user?.id
    ? getUserBookings(user.id)
        .map((b) => classes.find((c) => c.id === b.classId))
        .filter(
          (cls) =>
            cls &&
            !cls.isCancelled &&
            isClassPast(cls) &&
            !userReviewedClassIds.has(cls.id) &&
            Date.now() - (getClassStartDateTime(cls)?.getTime() ?? 0) < reviewPromptWindowMs
        )
        .sort((a, b) => getClassStartDateTime(b) - getClassStartDateTime(a))[0] || null
    : null;
  const reviewModalClass = classes.find((cls) => cls.id === selectedPastReviewClassId);
  const openReviewModal = (cls) => {
    setSelectedPastReviewClassId(cls.id);
    setPastReviewComment("");
    setPastReviewRating(7);
    setPastReviewStatus("");
  };
  const closeReviewModal = () => {
    setSelectedPastReviewClassId("");
    setPastReviewComment("");
    setPastReviewStatus("");
  };
  return (
    <div className="min-h-screen" style={appThemeStyle}>
      {reviewModalClass && !userReviewedClassIds.has(reviewModalClass.id) && (
        <div className="modal-backdrop" onClick={closeReviewModal}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>Leave a review</h3>
              <button type="button" className="modal-close" onClick={closeReviewModal}>
                Close
              </button>
            </div>
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>
              {reviewModalClass.name} • {reviewModalClass.date} • {reviewModalClass.time}
            </div>
            <div className="form-field">
              <label>Rating</label>
              <div className="review-mood-picker">
                {REVIEW_MOODS.map((mood) => (
                  <button
                    key={mood.rating}
                    type="button"
                    className={`review-mood-button ${
                      pastReviewRating === mood.rating ? "active" : ""
                    }`}
                    onClick={() => setPastReviewRating(mood.rating)}
                    title={mood.label}
                    aria-label={`${mood.label} (${mood.rating})`}
                  >
                    <span>{mood.emoji}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="form-field">
              <label>Review</label>
              <textarea
                className="review-textarea"
                placeholder="Write a short review"
                value={pastReviewComment}
                onChange={(e) => setPastReviewComment(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Button onClick={() => submitPastClassReview(reviewModalClass)}>Submit review</Button>
              <Button className="ghost-button" onClick={closeReviewModal}>
                Cancel
              </Button>
            </div>
            {pastReviewStatus && (
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>{pastReviewStatus}</div>
            )}
          </div>
        </div>
      )}
      <div className="header">
        <div className="header-left">
          <img src={logoSrc} className="logo" alt={appStyling.brandName} />
          <h1><CalendarIcon /> {appStyling.appTitle}</h1>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className={`header-tab ${view === "schedule" ? "active" : ""}`}
            onClick={() => setView("schedule")}
          >
            Schedule
          </button>
          <button
            type="button"
            className={`header-tab ${view === "reviews" ? "active" : ""}`}
            onClick={() => setView("reviews")}
          >
            Reviews
          </button>
          <button
            type="button"
            className={`header-tab ${view === "account" ? "active" : ""}`}
            onClick={() => setView("account")}
          >
            Account
          </button>
          {!effectiveIsAdmin && (
            <button
              type="button"
              className={`header-tab ${view === "account" && userAccountTab === "credits" ? "active" : ""}`}
              onClick={openCreditsMembershipsTab}
            >
              Get Credits
            </button>
          )}
          <button
            type="button"
            className="header-tab"
            onClick={() => {
              setHelpRequestStatus("");
              setShowHelpModal(true);
            }}
          >
            Need help?
          </button>
          <button
            type="button"
            className="header-tab header-tab-icon"
            aria-label="Open Baile Project website"
            title="Baile Project website"
            onClick={() =>
              window.open("https://www.baileproject.com", "_blank", "noopener,noreferrer")
            }
          >
            <WebsiteIcon />
          </button>
          <button
            type="button"
            className="header-tab header-tab-icon"
            aria-label="Open Instagram"
            title="Instagram"
            onClick={() =>
              window.open("https://www.instagram.com/thebaileproject/", "_blank", "noopener,noreferrer")
            }
          >
            <InstagramIcon />
          </button>
        </div>
      </div>

      {/* SCHEDULE */}
      {view === "schedule" && (
        <div
          className={`view-shell ${isMobileScheduleViewport ? "is-mobile-schedule" : ""}`}
        >
          {reviewPromptClass && !reviewPromptDismissed && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                flexWrap: "wrap",
                padding: "10px 14px",
                marginBottom: 12,
                borderRadius: 12,
                border: "1px solid rgba(244, 196, 48, 0.4)",
                background: "rgba(244, 196, 48, 0.08)",
              }}
            >
              <span>
                How was <strong>{reviewPromptClass.name}</strong> ({reviewPromptClass.date})?
              </span>
              <span style={{ display: "flex", gap: 8 }}>
                <Button onClick={() => openReviewModal(reviewPromptClass)}>Leave a review</Button>
                <Button className="ghost-button" onClick={() => setReviewPromptDismissed(true)}>
                  Not now
                </Button>
              </span>
            </div>
          )}
          <div className="schedule-toolbar">
            <div
              className={`schedule-controls ${
                scheduleViewMode === "week" ? "week-controls" : "month-controls"
              }`}
            >
              {scheduleViewMode === "month" ? (
                <>
                  <Button onClick={goToPreviousMonth}>◀</Button>
                  <div className="schedule-date-display schedule-month-display">
                    <span className="schedule-date-display-text">
                      {formatMonthLabel(selectedMonth) || selectedMonth}
                    </span>
                    <span className="schedule-date-display-icon" aria-hidden="true">
                      <CalendarIcon />
                    </span>
                    <Input
                      type="month"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      aria-label="Select month"
                    />
                  </div>
                  <Button onClick={goToNextMonth}>▶</Button>
                </>
              ) : (
                <>
                  <Button onClick={goToPreviousWeek}>◀</Button>
                  <div className="schedule-date-display">
                    <span className="schedule-date-display-text">
                      {(isMobileScheduleViewport
                        ? formatWeekRangeLabel(selectedDate, true)
                        : formatWeekRangeLabel(selectedDate)) || selectedDate}
                    </span>
                    <span className="schedule-date-display-icon" aria-hidden="true">
                      <CalendarIcon />
                    </span>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      aria-label="Select week date"
                    />
                  </div>
                  <Button onClick={goToNextWeek}>▶</Button>
                </>
              )}
            </div>
            <div className="schedule-toggle">
              <button
                type="button"
                className={scheduleViewMode === "week" ? "active" : ""}
                onClick={() => handleScheduleViewModeChange("week")}
              >
                Week
              </button>
              <button
                type="button"
                className={scheduleViewMode === "month" ? "active" : ""}
                onClick={() => handleScheduleViewModeChange("month")}
              >
                Month
              </button>
            </div>
          </div>


          {effectiveIsAdmin && adminDate && (
  <Card ref={adminFormRef} className="class-editor-card">

              <h3>
                {editingClass ? "Edit class" : "Add class on"} {newClass.date || adminDate}
                {getWeekdayLabel(newClass.date || adminDate)
                  ? ` (${getWeekdayLabel(newClass.date || adminDate)})`
                  : ""}
              </h3>

              <div className="form-field">
                <label>Class template</label>
                <div className="template-controls">
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => {
                      setSelectedTemplateId(e.target.value);
                      applyTemplate(e.target.value);
                    }}
                  >
                    <option value="">Select template</option>
                    {classTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} • {template.time} • {template.location}
                        {template.teacher ? ` • ${template.teacher}` : ""} • {template.spots} spots
                      </option>
                    ))}
                  </select>
                  <Button onClick={saveTemplate}>Save template</Button>
                  {selectedTemplateId && (
                    <Button onClick={() => removeTemplate(selectedTemplateId)}>
                      Remove
                    </Button>
                  )}
                </div>
                {classTemplateStatus && (
                  <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
                    {classTemplateStatus}
                  </div>
                )}
              </div>

              {editingClass && (
                <label>
                  <input
                    type="checkbox"
                    checked={editSeries}
                    onChange={(e) => setEditSeries(e.target.checked)}
                  /> Edit future classes
                </label>
              )}

              <div className="class-editor-grid">
              {["name", "date", "location", "teacher", "time", "spots"].map((field) => (
  <div className="form-field" key={field}>
    <label>{field}</label>

    {field === "location" ? (
      <select
        value={newClass.location}
        onChange={(e) =>
          setNewClass({ ...newClass, location: e.target.value })
        }
      >
        <option value="">Select location</option>
        {locations.map((loc) => (
          <option key={loc.id} value={loc.name}>
            {loc.name}
          </option>
        ))}
      </select>
    ) : field === "teacher" ? (
      <select
        value={newClass.teacher}
        onChange={(e) =>
          setNewClass({ ...newClass, teacher: e.target.value })
        }
      >
        <option value="">Select teacher</option>
        {teachers.map((teacher) => (
          <option key={teacher.id} value={teacher.name}>
            {teacher.name}
          </option>
        ))}
      </select>
    ) : field === "date" ? (
      <div className="field-controls">
        <Input
          type="date"
          value={newClass.date}
          onChange={(e) =>
            setNewClass({ ...newClass, date: e.target.value })
          }
        />
        <Button onClick={() => shiftClassDate(-1)}>−1 day</Button>
        <Button onClick={() => shiftClassDate(1)}>+1 day</Button>
      </div>
    ) : field === "time" ? (
      <div className="field-controls">
        <select
          value={
            isCustomTime || (newClass.time && !timeSlots.includes(newClass.time))
              ? "__custom__"
              : newClass.time
          }
          onChange={(e) => {
            const value = e.target.value;
            if (value === "__custom__") {
              setIsCustomTime(true);
              return;
            }
            setCustomTime("");
            setIsCustomTime(false);
            setNewClass({ ...newClass, time: value });
          }}
        >
          <option value="">Select time</option>
          {timeSlots.map((slot) => (
            <option key={slot} value={slot}>
              {slot}
            </option>
          ))}
          <option value="__custom__">Custom...</option>
        </select>
        {(isCustomTime || (newClass.time && !timeSlots.includes(newClass.time))) && (
          <Input
            placeholder="Custom time"
            value={customTime || newClass.time}
            onChange={(e) => {
              setCustomTime(e.target.value);
              setNewClass({ ...newClass, time: e.target.value });
            }}
          />
        )}
      </div>
    ) : (
      <Input
        value={newClass[field]}
        onChange={(e) =>
          setNewClass({ ...newClass, [field]: e.target.value })
        }
      />
    )}
  </div>
))}

              <div className="form-field">
                <label>Bar color</label>
                <select
                  value={newClass.color || "#f4c430"}
                  onChange={(e) => setNewClass({ ...newClass, color: e.target.value })}
                >
                  {classColorOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>


              <div className="form-field">
                <label>Repeat weekly</label>
                <Input
                  type="number"
                  min="1"
                  value={newClass.repeatWeeks ?? ""}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (value === "") {
                      setNewClass({ ...newClass, repeatWeeks: "" });
                      return;
                    }

                    setNewClass({
                      ...newClass,
                      repeatWeeks: Math.max(1, Number(value) || 1),
                    });
                  }}
                  onBlur={() => {
                    if (newClass.repeatWeeks === "") {
                      setNewClass({ ...newClass, repeatWeeks: 1 });
                    }
                  }}
                />
              </div>
              </div>

              <div className="class-editor-actions">
  <Button onClick={addClass}>
    {editingClass ? "Save changes" : "Add class"}
  </Button>

  <Button
    onClick={() => {
      setAdminDate(null);
      setEditingClass(null);
      setEditSeries(false);
      setNewClass({
        name: "",
        date: "",
        time: "",
        spots: "",
        location: "",
        teacher: "",
        color: "#f4c430",
        repeatWeeks: 1,
      });
      setIsCustomTime(false);
    }}
  >
    Cancel
  </Button>
</div>

            </Card>
          )}

          <div
            ref={weekScrollRef}
            className={`week-scroll-shell ${scheduleViewMode === "week" ? "is-week-mode" : ""}`}
            onPointerDownCapture={handleWeekPointerDown}
            onPointerMoveCapture={handleWeekPointerMove}
            onPointerUpCapture={handleWeekPointerEnd}
            onPointerCancelCapture={handleWeekPointerEnd}
            onClickCapture={handleWeekClickCapture}
            onWheel={handleWeekWheel}
          >
            <div
              className="weekday-header"
              style={
                calendarColumnTemplate
                  ? { gridTemplateColumns: calendarColumnTemplate }
                  : undefined
              }
            >
              {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => <div key={d}>{d}</div>)}
            </div>

            <div
              className={`calendar ${scheduleViewMode === "week" ? "week-view" : ""}`}
              style={
                calendarColumnTemplate
                  ? { gridTemplateColumns: calendarColumnTemplate }
                  : undefined
              }
            >
            {(scheduleViewMode === "week" ? weekDays : monthGrid).map((date, i) => {
              const iso = date ? toISODate(date) : null;
              const today = new Date();
today.setHours(0, 0, 0, 0);

const todayISO = toISODate(today);
const isToday = iso === todayISO;
const isPast = new Date(iso) < today;

              const dayClasses = iso
                ? classes
                    .filter((c) => c.date === iso)
                    .sort((a, b) => parseTimeMinutes(a.time) - parseTimeMinutes(b.time))
                : [];
              const hasClasses = dayClasses.length > 0;

              // ⛔ Empty placeholder cell (no square, no click)
if (!date) {
  return scheduleViewMode === "month" ? <div key={i} className="day empty" /> : null;
}

return (
  <div
    key={i}
    className={`day ${hasClasses ? "has-classes" : "no-classes"} ${isToday ? "today" : ""} ${isPast ? "past" : ""}`}

    onClick={() => {

  if (effectiveIsAdmin && date && !isPast) {

    setAdminDate(iso);
    setEditingClass(null);
    setNewClass({
      name: "",
      date: iso,
      time: "",
      spots: "",
      location: "",
      teacher: "",
      color: "#f4c430",
      repeatWeeks: 1,
    });
    setSelectedTemplateId("");
    setIsCustomTime(false);

    // 👇 Smooth scroll to admin form
    scrollToAdminForm();

  }
}}

                >
                  {scheduleViewMode === "week" && date && (
                    <div className="week-day-heading">
                      <span className="week-day-name">{getWeekdayLabel(iso)}</span>
                      <span className="week-day-date">{date.getDate()}</span>
                    </div>
                  )}
                  {date && <div className="day-number">{date.getDate()}</div>}

                  <div className="day-body">
                    <div className={`day-classes ${scheduleViewMode === "week" ? "with-time" : ""}`}>
                      {dayClasses.map((cls) => {
                        const timeLabel = String(cls.time || "").split(" - ")[0].trim() || "—";
                        if (scheduleViewMode === "week") {
                          return (
                            <div key={cls.id} className="class-row">
                              <span className="class-time">{timeLabel}</span>
                              {renderClassItem(cls, { variant: "full" })}
                            </div>
                          );
                        }
                        return renderClassItem(cls, { variant: "compact" });
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>

          {selectedClass && (
            <div
              className="modal-backdrop class-details-modal-backdrop"
              onClick={() => setSelectedClass(null)}
            >
              <div
                className="modal-card class-details-modal-card"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                <div className="modal-header">
                  <div>
                    <strong>Class details</strong>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      {selectedClass.date} • {getWeekdayLabel(selectedClass.date)} • {selectedClass.time}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="modal-close"
                    onClick={() => setSelectedClass(null)}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
                {renderClassItem(selectedClass, { variant: "modal" })}
              </div>
            </div>
          )}

          {adminAddClass && (
            <div
              className="modal-backdrop admin-add-user-modal-backdrop"
              onClick={closeAdminAddUserModal}
            >
              <div
                className="modal-card admin-add-user-modal-card"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="admin-add-user-modal-title"
              >
                <div className="modal-header">
                  <div>
                    <strong id="admin-add-user-modal-title">Add member to class</strong>
                    <div style={{ fontSize: 12, opacity: 0.72, marginTop: 4 }}>
                      {adminAddClass.name} • {adminAddClass.date} • {adminAddClass.time}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="modal-close"
                    onClick={closeAdminAddUserModal}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>

                <div className="admin-add-user-modal-body">
                  <div className="admin-add-user-summary">
                    <span>
                      {Math.max(0, Number(adminAddClass.spots || 0) - getBookedCount(adminAddClass.id))} spots left
                    </span>
                    <span>
                      {adminAddCreditSource === "amiga_ticket" ? "Amiga ticket uses 2 spots" : "Regular add uses 1 spot"}
                    </span>
                  </div>

                  <div className="admin-add-user-credit-source" role="radiogroup" aria-label="Booking type">
                    <label>
                      <input
                        type="radio"
                        name="admin-add-credit-source"
                        checked={adminAddCreditSource === "regular"}
                        onChange={() => setAdminAddCreditSource("regular")}
                      />
                      Regular booking
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="admin-add-credit-source"
                        checked={adminAddCreditSource === "amiga_ticket"}
                        onChange={() => setAdminAddCreditSource("amiga_ticket")}
                      />
                      Amiga ticket
                    </label>
                  </div>

                  <form
                    className="admin-add-user-search"
                    onSubmit={(e) => {
                      e.preventDefault();
                      setAdminAddSearchTerm(adminAddSearchDraft.trim());
                    }}
                  >
                    <Input
                      placeholder="Search member by name or email"
                      value={adminAddSearchDraft}
                      onChange={(e) => setAdminAddSearchDraft(e.target.value)}
                    />
                    <Button type="submit">Search</Button>
                  </form>

                  {adminAddStatus && <div className="admin-add-user-status">{adminAddStatus}</div>}

                  <div className="admin-add-user-results">
                    {adminAddSearchTerm.trim().length < 2 ? (
                      <div className="admin-add-user-empty">Search at least 2 characters.</div>
                    ) : adminAddSearchResults.length === 0 ? (
                      <div className="admin-add-user-empty">No matching members found.</div>
                    ) : (
                      adminAddSearchResults.map(({ userId: resultUserId, profile, isBooked }) => {
                        const seatsNeeded = adminAddCreditSource === "amiga_ticket" ? 2 : 1;
                        const seatsLeft = Math.max(
                          0,
                          Number(adminAddClass.spots || 0) - getBookedCount(adminAddClass.id)
                        );
                        const notEnoughSpots = seatsLeft < seatsNeeded;
                        return (
                          <div
                            key={resultUserId}
                            className={`admin-add-user-result ${isBooked ? "is-blocked" : ""}`}
                          >
                            <div>
                              <div className="admin-add-user-name">
                                {getProfileDisplayName(profile, resultUserId)}
                              </div>
                              <div className="admin-add-user-email">
                                {profile?.email || resultUserId}
                                {isBooked ? " • already booked" : ""}
                              </div>
                            </div>
                            <Button
                              className={isBooked ? "ghost-button" : ""}
                              disabled={
                                isBooked ||
                                notEnoughSpots ||
                                adminAddLoadingUserId === resultUserId ||
                                Boolean(adminAddLoadingUserId)
                              }
                              onClick={() => adminAddUserToClass(resultUserId)}
                            >
                              {isBooked
                                ? "Booked"
                                : notEnoughSpots
                                  ? "No spots"
                                  : adminAddLoadingUserId === resultUserId
                                    ? "Adding..."
                                    : adminAddCreditSource === "amiga_ticket"
                                      ? "Add with Amiga"
                                      : "Add"}
                            </Button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {showNoCreditsModal && (
            <div
              className="modal-backdrop no-credits-modal-backdrop"
              onClick={() => setShowNoCreditsModal(false)}
            >
              <div
                className="modal-card no-credits-modal-card"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="no-credits-modal-title"
              >
                <div className="modal-header">
                  <div>
                    <strong id="no-credits-modal-title">No credits left</strong>
                    <div style={{ fontSize: 12, opacity: 0.72, marginTop: 4 }}>
                      You need credits or an active membership to reserve this class.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="modal-close"
                    onClick={() => setShowNoCreditsModal(false)}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
                <div className="no-credits-modal-body">
                  <div className="no-credits-modal-copy">
                    Buy a credit pack or manage your membership to continue booking.
                  </div>
                  <div className="no-credits-modal-actions">
                    <Button className="ghost-button" onClick={() => setShowNoCreditsModal(false)}>
                      Not now
                    </Button>
                    <Button onClick={openCreditsMembershipsTab}>
                      Get Credits
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* REVIEW INVITE */}
      {view === "review" && reviewToken && (
        <div className="view-shell">
          <Card className="review-form-card">
            <h2>Leave a review</h2>
            <p style={{ fontSize: 14, opacity: 0.7 }}>
              Thanks for joining class. We would love to hear your feedback.
            </p>
            <div className="form-field">
              <label>Rating</label>
              <div className="review-mood-picker">
                {REVIEW_MOODS.map((mood) => (
                  <button
                    key={mood.rating}
                    type="button"
                    className={`review-mood-button ${
                      reviewRating === mood.rating ? "active" : ""
                    }`}
                    onClick={() => setReviewRating(mood.rating)}
                    title={mood.label}
                    aria-label={`${mood.label} (${mood.rating})`}
                  >
                    <span>{mood.emoji}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="form-field">
              <label>Review</label>
              <textarea
                className="review-textarea"
                placeholder="Write a short review"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
              />
            </div>
            <Button onClick={submitReview}>Submit review</Button>
            {reviewStatus && (
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>{reviewStatus}</div>
            )}
          </Card>
        </div>
      )}

      {/* REVIEWS */}
      {view === "reviews" && (
        <div className="view-shell">
          <Card>
            <div className="reviews-page">
            <h2>Reviews</h2>
            {reviews.length === 0 && (
              <p style={{ opacity: 0.6 }}>No reviews yet.</p>
            )}
            {Object.values(
              reviews.reduce((acc, review) => {
                const className = review.class_name || review.className || "Class";
                const key = className;
                if (!acc[key]) {
                  acc[key] = {
                    className,
                    items: [],
                  };
                }
                acc[key].items.push(review);
                return acc;
              }, {})
            ).map((group) => (
              <div key={group.className} className="review-group">
                <div className="review-group-header">
                  <div>
                    <strong>{group.className}</strong>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>{group.items.length} review(s)</div>
                  </div>
                </div>
                <div className="reviews-grid">
                  {group.items.map((review) => (
                    <div key={review.id} className="review-card">
                      <div className="review-meta">
                        <span className="review-rating">
                          {getReviewEmoji(review.rating)}
                        </span>
                        <span>
                          {new Date(review.created_at || review.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="review-comment">{review.comment}</div>
                      <div className="review-author">Anonymous</div>
                      {effectiveIsAdmin && (
                        <Button
                          className="ghost-button review-delete-button"
                          onClick={() => deleteReviewByAdmin(review)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            </div>
          </Card>
        </div>
      )}

      {/* ACCOUNT */}
      {view === "account" && (
  <div className="view-shell">
  <Card>
  <div className="account-section">

    <h2>My account</h2>

    {canUseDevTools && (
      <div className="account-block" style={{ border: "1px solid rgba(244, 196, 48, 0.28)", background: "rgba(244, 196, 48, 0.06)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
          <div>
            <h3 className="account-section-title" style={{ margin: 0 }}>Developer tools</h3>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Only available for your account.</div>
          </div>
          <Button className="ghost-button" onClick={() => setDevToolsVisible((prev) => !prev)}>
            {devToolsVisible ? "Hide" : "Show"}
          </Button>
        </div>
        {devToolsVisible && (
          <div style={{ marginTop: 10 }}>
            {!devToolsUnlocked ? (
              <>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
                  Verify your password to unlock local testing tools.
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <Input
                    type="password"
                    value={devToolsPasswordDraft}
                    onChange={(e) => {
                      setDevToolsPasswordDraft(e.target.value);
                      if (devToolsStatus) setDevToolsStatus("");
                    }}
                    placeholder="Your password"
                  />
                  <Button className="primary-button" onClick={unlockDevTools} disabled={devToolsLoading}>
                    {devToolsLoading ? "Checking..." : "Unlock"}
                  </Button>
                </div>
                {devToolsStatus && (
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>{devToolsStatus}</div>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
                  Developer tools are unlocked for this browser.
                </div>
                <Button className="ghost-button" onClick={toggleViewingAsNormalAccount}>
                  {viewingAsNormalAccount ? "Return to admin view" : "Switch to normal account view"}
                </Button>
                {devToolsStatus && (
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>{devToolsStatus}</div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    )}

    {effectiveIsAdmin ? (
      <>
        <div className="account-tabs">
          {[
            { id: "info", label: "Account information" },
            { id: "manage", label: "Manage locations & teachers & cancellation" },
            { id: "users", label: "Manage user accounts & membership plans" },
            { id: "styling", label: "App styling" },
            { id: "emails", label: "Email templates" },
            { id: "analytics", label: "Analytics" },
            { id: "invoices", label: "Invoices" },
            { id: "audit", label: "Audit log" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`account-tab-button ${
                adminAccountTab === tab.id ? "active" : ""
              }`}
              onClick={() => setAdminAccountTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div
          className={`account-grid ${
            ["emails", "styling"].includes(adminAccountTab) ? "account-grid-wide" : ""
          }`}
        >
          <div className="account-column">
            {adminAccountTab === "info" && (
              <div className="account-block">
                <h3 className="account-section-title">Account information</h3>
                <div style={{ fontWeight: 700 }}>{user.email}</div>
                <div style={{ marginTop: 6 }}>
                  <strong>Credits:</strong> {getAvailableCredits(user.id)}
                </div>
                <div style={{ marginTop: 4 }}>
                  <strong>Amiga tickets:</strong> {getAmigaTicketBalance(user.id)}
                </div>
                <div className="form-field" style={{ marginTop: 12 }}>
                  <label>Your name</label>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <Input
                      value={profileNameDraft}
                      onChange={(e) => setProfileNameDraft(e.target.value)}
                      placeholder="Add your name"
                    />
                    <Button
                      className="primary-button"
                      onClick={saveProfileName}
                      disabled={profileNameSaving}
                    >
                      {profileNameSaving ? "Saving..." : "Save name"}
                    </Button>
                  </div>
                  {profileNameStatus && (
                    <div style={{ fontSize: 12, opacity: 0.7 }}>{profileNameStatus}</div>
                  )}
                </div>
                <div className="form-field" style={{ marginTop: 12 }}>
                  <label>Billing address (for invoices)</label>
                  <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
                    If a full invoice is required, please fill in these fields.
                  </div>
                  <Input
                    value={billingAddressStreetDraft}
                    onChange={(e) => setBillingAddressStreetDraft(e.target.value)}
                    placeholder="Street + number"
                  />
                  <Input
                    value={billingAddressPostcodeDraft}
                    onChange={(e) => setBillingAddressPostcodeDraft(e.target.value)}
                    placeholder="Postcode"
                    style={{ marginTop: 8 }}
                  />
                  <Input
                    value={billingAddressCityDraft}
                    onChange={(e) => setBillingAddressCityDraft(e.target.value)}
                    placeholder="City"
                    style={{ marginTop: 8 }}
                  />
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                    <Button
                      className="ghost-button"
                      onClick={saveBillingAddress}
                      disabled={billingAddressSaving}
                    >
                      {billingAddressSaving ? "Saving..." : "Save billing address"}
                    </Button>
                    {billingAddressStatus && (
                      <div style={{ fontSize: 12, opacity: 0.7 }}>{billingAddressStatus}</div>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
                    Change your password.
                  </div>
                  {isPasswordRecovery && (
                    <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
                      Recovery mode active: current password is not required.
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    {!isPasswordRecovery && (
                      <Input
                        type="password"
                        value={currentPasswordDraft}
                        onChange={(e) => {
                          setCurrentPasswordDraft(e.target.value);
                          if (passwordChangeStatus) setPasswordChangeStatus("");
                        }}
                        placeholder="Current password"
                      />
                    )}
                    <Input
                      type="password"
                      value={newPasswordDraft}
                      onChange={(e) => {
                        setNewPasswordDraft(e.target.value);
                        if (passwordChangeStatus) setPasswordChangeStatus("");
                      }}
                      placeholder="New password"
                    />
                    <Input
                      type="password"
                      value={confirmNewPasswordDraft}
                      onChange={(e) => {
                        setConfirmNewPasswordDraft(e.target.value);
                        if (passwordChangeStatus) setPasswordChangeStatus("");
                      }}
                      placeholder="Confirm new password"
                    />
                  </div>
                  <Button
                    className="ghost-button"
                    onClick={requestPasswordChange}
                    disabled={passwordChangeLoading}
                    style={{ marginTop: 8 }}
                  >
                    {passwordChangeLoading ? "Updating..." : "Update password"}
                  </Button>
                  {passwordChangeStatus && (
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
                      {passwordChangeStatus}
                    </div>
                  )}
                </div>
              </div>
            )}

            {adminAccountTab === "manage" && (
              <>
                <div className="account-block">
                  <h3 className="account-section-title">Manage locations</h3>

                  {locations.map((loc) => (
                    <div
                      key={loc.id}
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        marginTop: 6,
                      }}
                    >
                      <Input
                        value={loc.name}
                        onChange={(e) => {
                          const value = e.target.value;
                          setLocations((prev) =>
                            prev.map((item) =>
                              item.id === loc.id ? { ...item, name: value } : item
                            )
                          );
                        }}
                        onBlur={async () => {
                          if (!loc.name.trim()) return;
                          await supabase
                            .from("locations")
                            .update({ name: loc.name.trim() })
                            .eq("id", loc.id);
                          await loadCoreData();
                        }}
                      />

                      <Button
                        onClick={async () => {
                          await supabase.from("locations").delete().eq("id", loc.id);
                          await loadCoreData();

                          logAction({
                            actor: user.email,
                            action: "REMOVE_LOCATION",
                            details: { location: loc.name },
                          });
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  ))}

                  <Button
                    style={{ marginTop: 10 }}
                    onClick={async () => {
                      const { data } = await supabase
                        .from("locations")
                        .insert({ name: "New location", active: true })
                        .select("id,name")
                        .single();
                      if (data) {
                        await loadCoreData();
                      }
                      logAction({
                        actor: user.email,
                        action: "ADD_LOCATION",
                        details: {},
                      });
                    }}
                  >
                    + Add location
                  </Button>
                </div>

                <div className="account-block">
                  <h3 className="account-section-title">Manage teachers</h3>

                  {teachers.map((teacher) => (
                    <div
                      key={teacher.id}
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        marginTop: 6,
                      }}
                    >
                      <Input
                        value={teacher.name}
                        onChange={(e) => {
                          const value = e.target.value;
                          setTeachers((prev) =>
                            prev.map((item) =>
                              item.id === teacher.id ? { ...item, name: value } : item
                            )
                          );
                        }}
                        onBlur={async () => {
                          if (!teacher.name.trim()) return;
                          await supabase
                            .from("teachers")
                            .update({ name: teacher.name.trim() })
                            .eq("id", teacher.id);
                          await loadCoreData();
                        }}
                      />

                      <Button
                        onClick={async () => {
                          await supabase.from("teachers").delete().eq("id", teacher.id);
                          await loadCoreData();

                          logAction({
                            actor: user.email,
                            action: "REMOVE_TEACHER",
                            details: { teacher: teacher.name },
                          });
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  ))}

                  <Button
                    style={{ marginTop: 10 }}
                    onClick={async () => {
                      const { data } = await supabase
                        .from("teachers")
                        .insert({ name: "New teacher", active: true })
                        .select("id,name")
                        .single();
                      if (data) {
                        await loadCoreData();
                      }
                      logAction({
                        actor: user.email,
                        action: "ADD_TEACHER",
                        details: {},
                      });
                    }}
                  >
                    + Add teacher
                  </Button>
                </div>

                <div className="account-block">
                  <h3 className="account-section-title">Cancellation policy</h3>
                  <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
                    Users can cancel until the selected hours before class.
                  </div>
                  <select
                    value={cancelPolicyHours}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      logAction({
                        actor: user.email,
                        action: "CANCEL_POLICY_UPDATED",
                        details: { from: cancelPolicyHours, to: next },
                      });
                      setCancelPolicyHours(next);
                    }}
                  >
                    {cancelPolicyOptions.map((hours) => (
                      <option key={hours} value={hours}>
                        {hours} hour{hours === 1 ? "" : "s"} before
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {adminAccountTab === "users" && (
              <>
                <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
                  Note: All prices include 21% VAT.
                </div>
                <div className="account-block">
                  <h3 className="account-section-title">Credit price</h3>
                  <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
                    Set how much 1 credit and the 10-credit pack cost in euros.
                  </div>
                  <div style={{ display: "grid", gap: 10, maxWidth: 420 }}>
                    <div>
                      <label style={{ fontSize: 12, opacity: 0.75, display: "block", marginBottom: 4 }}>
                        1 credit price (EUR)
                      </label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={creditPriceDraft}
                        onChange={(e) => setCreditPriceDraft(Number(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, opacity: 0.75, display: "block", marginBottom: 4 }}>
                        10 credit pack price (EUR)
                      </label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={creditPack10PriceDraft}
                        onChange={(e) => setCreditPack10PriceDraft(Number(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
                    <Button
                      onClick={async () => {
                        const next = Number(creditPriceDraft) || 0;
                        const nextPack10 = Number(creditPack10PriceDraft) || 0;
                        const { error } = await supabase
                          .from("app_settings")
                          .upsert(
                            [
                              { key: "credit_price_per_unit", value: next },
                              { key: "credit_price_pack_10", value: nextPack10 },
                            ],
                            { onConflict: "key" }
                          );
                        if (error) {
                          alert(error.message || "Failed to save credit price.");
                          return;
                        }
                        logAction({
                          actor: user.email,
                          action: "CREDIT_PRICE_UPDATED",
                          details: {
                            singleCreditFrom: creditPricePerUnit,
                            singleCreditTo: next,
                            creditPack10From: creditPack10Price,
                            creditPack10To: nextPack10,
                          },
                        });
                        setCreditPricePerUnit(next);
                        setCreditPriceDraft(next);
                        setCreditPack10Price(nextPack10);
                        setCreditPack10PriceDraft(nextPack10);
                        alert("Credit price saved.");
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </div>

                <div className="account-block">
                  <h3 className="account-section-title">Amiga ticket price</h3>
                  <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
                    Set how much 1 Amiga ticket costs in euros.
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={amigaTicketPriceDraft}
                      onChange={(e) => setAmigaTicketPriceDraft(Number(e.target.value) || 0)}
                    />
                    <Button
                      onClick={async () => {
                        const next = Number(amigaTicketPriceDraft) || 0;
                        const { error } = await supabase
                          .from("app_settings")
                          .upsert(
                            { key: "amiga_ticket_price", value: next },
                            { onConflict: "key" }
                          );
                        if (error) {
                          alert(error.message || "Failed to save Amiga ticket price.");
                          return;
                        }
                        logAction({
                          actor: user.email,
                          action: "AMIGA_TICKET_PRICE_UPDATED",
                          details: { from: amigaTicketPricePerUnit, to: next },
                        });
                        setAmigaTicketPricePerUnit(next);
                        alert("Amiga ticket price saved.");
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </div>

                <div className="account-block">
                  <h3 className="account-section-title">Membership plans</h3>
                  <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
                    Adjust plan name, monthly price, credits per plan, and minimum term.
                  </div>
                  <div className="membership-plan-list">
                    <div className="membership-plan-head">
                      <div>Plan name</div>
                      <div>Monthly price (€)</div>
                      <div>Credits / month</div>
                      <div>Minimum months</div>
                    </div>
                    {adminVisibleMembershipPlanDrafts.map((plan, idx) => (
                      <div key={plan.id} className="membership-plan-row">
                        <Input
                          type="text"
                          value={plan.name}
                          onChange={(e) => {
                            const updated = [...adminVisibleMembershipPlanDrafts];
                            updated[idx] = { ...plan, name: e.target.value };
                            setMembershipPlanDrafts(updated);
                          }}
                        />
                        <Input
                          type="number"
                          min="0"
                          value={plan.price}
                          onChange={(e) => {
                            const nextValue = Number(e.target.value) || 0;
                            const updated = [...adminVisibleMembershipPlanDrafts];
                            updated[idx] = { ...plan, price: nextValue };
                            setMembershipPlanDrafts(updated);
                          }}
                        />
                        <Input
                          type="number"
                          min="0"
                          value={plan.creditsPerMonth}
                          onChange={(e) => {
                            const nextValue = Number(e.target.value) || 0;
                            const updated = [...adminVisibleMembershipPlanDrafts];
                            updated[idx] = { ...plan, creditsPerMonth: nextValue };
                            setMembershipPlanDrafts(updated);
                          }}
                        />
                        <Input
                          type="number"
                          min="1"
                          value={plan.minimumMonths}
                          onChange={(e) => {
                            const nextValue = Math.max(1, Number(e.target.value) || 1);
                            const updated = [...adminVisibleMembershipPlanDrafts];
                            updated[idx] = { ...plan, minimumMonths: nextValue };
                            setMembershipPlanDrafts(updated);
                          }}
                        />
                      </div>
                    ))}
                    <div style={{ marginTop: 6 }}>
                        <Button
                          onClick={async () => {
                            const plansToSave = adminVisibleMembershipPlanDrafts;
                            const normalizedPlans = plansToSave.map((plan) => ({
                              ...plan,
                              name: String(plan.name || "").trim(),
                            }));
                            if (normalizedPlans.some((plan) => !plan.name)) {
                              alert("Each membership plan needs a name.");
                              return;
                            }
                            await Promise.all(
                              normalizedPlans.map((plan) =>
                                supabase
                                  .from("membership_plans")
                                  .update({
                                    name: plan.name,
                                    price_eur: plan.price,
                                    credits_per_month: plan.creditsPerMonth,
                                    minimum_months: Math.max(1, Number(plan.minimumMonths) || 1),
                                  })
                                  .eq("id", plan.id)
                              )
                            );

                          normalizedPlans.forEach((plan) => {
                            const current = membershipPlans.find((p) => p.id === plan.id);
                            if (current && current.name !== plan.name) {
                              logAction({
                                actor: user.email,
                                action: "MEMBERSHIP_PLAN_UPDATED",
                                details: { planId: plan.id, field: "name", value: plan.name },
                              });
                            }
                            if (current && current.price !== plan.price) {
                              logAction({
                                actor: user.email,
                                action: "MEMBERSHIP_PLAN_UPDATED",
                                details: { planId: plan.id, field: "price", value: plan.price },
                              });
                            }
                            if (current && current.creditsPerMonth !== plan.creditsPerMonth) {
                              logAction({
                                actor: user.email,
                                action: "MEMBERSHIP_PLAN_UPDATED",
                                details: {
                                  planId: plan.id,
                                  field: "creditsPerMonth",
                                  value: plan.creditsPerMonth,
                                },
                              });
                            }
                            if (current && current.minimumMonths !== plan.minimumMonths) {
                              logAction({
                                actor: user.email,
                                action: "MEMBERSHIP_PLAN_UPDATED",
                                details: {
                                  planId: plan.id,
                                  field: "minimumMonths",
                                  value: plan.minimumMonths,
                                },
                              });
                            }
                          });

                          const sortedPlans = sortMembershipPlans(normalizedPlans);
                          setMembershipPlans(sortedPlans);
                          setMembershipPlanDrafts(sortedPlans);
                          alert("Membership plans saved.");
                        }}
                      >
                        Save all plans
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="account-block">
                  <h3 className="account-section-title">Review invite automation</h3>
                  <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
                    Automatically email review requests after class participation.
                  </div>

                  <div style={{ display: "grid", gap: 10 }}>
                    <label className="email-checkbox">
                      <input
                        type="checkbox"
                        checked={reviewInviteEnabledDraft}
                        onChange={(e) => setReviewInviteEnabledDraft(e.target.checked)}
                      />
                      Enable automatic review invites
                    </label>

                    <label className="email-checkbox">
                      <input
                        type="checkbox"
                        checked={reviewInviteExcludeMembersDraft}
                        onChange={(e) => setReviewInviteExcludeMembersDraft(e.target.checked)}
                      />
                      Exclude active members
                    </label>

                    <div style={{ display: "grid", gap: 6 }}>
                      <label>Minimum days between invites per user</label>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={reviewInviteFrequencyDaysDraft}
                        onChange={(e) =>
                          setReviewInviteFrequencyDaysDraft(Math.max(1, Number(e.target.value) || 1))
                        }
                      />
                    </div>

                    <div style={{ display: "grid", gap: 6 }}>
                      <label>Wait hours after class end before sending</label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={reviewInviteDelayHoursDraft}
                        onChange={(e) =>
                          setReviewInviteDelayHoursDraft(Math.max(0, Number(e.target.value) || 0))
                        }
                      />
                    </div>

                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <Button
                        onClick={async () => {
                          const nextSettings = [
                            { key: "review_invite_enabled", value: reviewInviteEnabledDraft ? 1 : 0 },
                            {
                              key: "review_invite_exclude_members",
                              value: reviewInviteExcludeMembersDraft ? 1 : 0,
                            },
                            {
                              key: "review_invite_frequency_days",
                              value: reviewInviteFrequencyDaysDraft,
                            },
                            { key: "review_invite_delay_hours", value: reviewInviteDelayHoursDraft },
                          ];
                          const { error } = await supabase
                            .from("app_settings")
                            .upsert(nextSettings, { onConflict: "key" });
                          if (error) {
                            alert(error.message || "Failed to save review invite settings.");
                            return;
                          }
                          setReviewInviteEnabled(reviewInviteEnabledDraft);
                          setReviewInviteExcludeMembers(reviewInviteExcludeMembersDraft);
                          setReviewInviteFrequencyDays(reviewInviteFrequencyDaysDraft);
                          setReviewInviteDelayHours(reviewInviteDelayHoursDraft);
                          logAction({
                            actor: user.email,
                            action: "REVIEW_INVITE_SETTINGS_UPDATED",
                            details: {
                              enabled: reviewInviteEnabledDraft,
                              excludeMembers: reviewInviteExcludeMembersDraft,
                              frequencyDays: reviewInviteFrequencyDaysDraft,
                              delayHours: reviewInviteDelayHoursDraft,
                            },
                          });
                          alert("Review invite settings saved.");
                        }}
                      >
                        Save review invite settings
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="account-block">
                  <h3 className="account-section-title">User accounts</h3>
                  <div
                    style={{
                      fontSize: 12,
                      opacity: 0.75,
                      marginBottom: 8,
                    }}
                  >
                      {getReadyLegacyReminderUserIds().length} legacy members still missing payment setup
                  </div>
                  <div className="account-user-toolbar">
                    <Input
                      placeholder="Search by name or email..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                    />
                    <select value={userSortMode} onChange={(e) => setUserSortMode(e.target.value)}>
                      <option value="default">All users</option>
                      <option value="members_only">Members only</option>
                      <option value="credits_desc">Most credits first</option>
                      <option value="signup_desc">Newest signups</option>
                    </select>
                    <Button
                      className="ghost-button"
                      onClick={sendAllLegacyMembershipReminders}
                      disabled={legacyReminderBulkSending || getReadyLegacyReminderUserIds().length === 0}
                    >
                      {legacyReminderBulkSending ? "Sending reminders..." : "Send all legacy reminders"}
                    </Button>
                    <div className="account-user-count">
                      {filteredUserIds.length} shown
                      {filteredUserIds.length !== Object.keys(profiles).length
                        ? ` • ${Object.keys(profiles).length} total`
                        : ""}
                    </div>
                  </div>

                  {Object.keys(profiles).length === 0 && (
                    <p style={{ opacity: 0.6 }}>No users yet.</p>
                  )}

                  {filteredUserIds.map((userId) => {
                    const profile = profiles[userId];
                    const email = profile?.email || userId;
                    const creditAmount = getAvailableCredits(userId);
                    const amigaTicketAmount = getAmigaTicketBalance(userId);
                    const legacyDraft = getLegacyMembershipDraft(userId);
                    const membershipCreditsPerMonth = getMembershipCreditsPerMonth(userId);
                    const membershipMonthlyPrice = getMembershipMonthlyPrice(userId);
                    const legacyMigrationStatus = getLegacyMigrationStatus(userId);
                    const legacyFirstPaymentConfirmation = getLegacyFirstPaymentConfirmation(userId);
                    const paymentHistoryRows = buildMembershipPaymentHistoryViewModel(
                      membershipPaymentHistory[userId] || []
                    );
                    return (
                      <details
                        key={userId}
                        className="account-user-row"
                        open={userSearch.trim() || userSortMode !== "default" ? true : undefined}
                      >
                        <summary className="account-user-summary">
                          <div className="account-user-email">{email}</div>
                          <span
                            className={`membership-status ${
                              memberships[userId]?.status === "active" ? "active" : "inactive"
                            }`}
                          >
                            {memberships[userId]?.status === "active"
                              ? membershipPlans.find((p) => p.id === memberships[userId].planId)?.name ||
                                memberships[userId].planId
                              : "No membership"}
                          </span>
                          <div className="account-user-summary-stats">
                            Credits: {creditAmount} • Amiga: {amigaTicketAmount}
                            {profiles[userId]?.legacy_member ? " • Legacy" : ""}
                          </div>
                        </summary>
                        <div className="account-user-body">
                          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>
                            Signed up: {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : "Unknown"}
                          </div>
                          <Button
                            className="ghost-button"
                            onClick={() =>
                              setCreditHistoryOpen((prev) => ({
                                ...prev,
                                [userId]: !prev[userId],
                              }))
                            }
                          >
                            {creditHistoryOpen[userId] ? "Hide history" : "Show history"}
                          </Button>

                        <div className="account-user-balances">
                          <div className="account-user-balance-row">
                            <div className="account-user-balance-label">Credits</div>
                            <div className="account-user-balance-actions">
                              <Button
                                className="mini-button"
                                onClick={async () => {
                                  if (!isAdmin) return;

                                  await addCreditEntry({
                                    userId,
                                    delta: -1,
                                    reason: "ADMIN_ADJUST",
                                  });

                                  logAction({
                                    actor: user.email,
                                    action: "CHANGE_USER_CREDITS",
                                    details: { targetUser: email, delta: -1 },
                                  });
                                }}
                              >
                                −
                              </Button>

                              <strong className="account-credit-amount">{creditAmount}</strong>

                              <Button
                                className="mini-button"
                                onClick={async () => {
                                  if (!isAdmin) return;

                                  await addCreditEntry({
                                    userId,
                                    delta: +1,
                                    reason: "ADMIN_ADJUST",
                                  });

                                  logAction({
                                    actor: user.email,
                                    action: "CHANGE_USER_CREDITS",
                                    details: { targetUser: email, delta: +1 },
                                  });
                                }}
                              >
                                +
                              </Button>
                            </div>
                          </div>
                          <div className="account-user-balance-row">
                            <div className="account-user-balance-label">Amiga tickets</div>
                            <div className="account-user-balance-actions">
                              <Button
                                className="mini-button"
                                onClick={async () => {
                                  if (!isAdmin) return;

                                  await addAmigaTicketEntry({
                                    userId,
                                    delta: -1,
                                    reason: "ADMIN_ADJUST",
                                  });

                                  logAction({
                                    actor: user.email,
                                    action: "CHANGE_USER_AMIGA_TICKETS",
                                    details: { targetUser: email, delta: -1 },
                                  });
                                }}
                              >
                                −
                              </Button>

                              <strong className="account-credit-amount">{amigaTicketAmount}</strong>

                              <Button
                                className="mini-button"
                                onClick={async () => {
                                  if (!isAdmin) return;

                                  await addAmigaTicketEntry({
                                    userId,
                                    delta: +1,
                                    reason: "ADMIN_ADJUST",
                                  });

                                  logAction({
                                    actor: user.email,
                                    action: "CHANGE_USER_AMIGA_TICKETS",
                                    details: { targetUser: email, delta: +1 },
                                  });
                                }}
                              >
                                +
                              </Button>
                            </div>
                          </div>
                        </div>

                        <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <select
                            value={resolveVisibleMembershipPlanId(
                              membershipDrafts[userId] ||
                                memberships[userId]?.planId,
                              adminVisibleMembershipPlanDrafts
                            )}
                            onChange={(e) =>
                              setMembershipDrafts((prev) => ({
                                ...prev,
                                [userId]: e.target.value,
                              }))
                            }
                          >
                            {adminVisibleMembershipPlanDrafts.map((plan) => (
                              <option key={plan.id} value={plan.id}>
                                {plan.name}
                              </option>
                            ))}
                          </select>
                          <Button
                            onClick={async () =>
                              setUserMembership({
                                userId,
                                planId: resolveVisibleMembershipPlanId(
                                  membershipDrafts[userId] ||
                                    memberships[userId]?.planId,
                                  adminVisibleMembershipPlanDrafts
                                ),
                              })
                            }
                            disabled={
                              memberships[userId]?.status === "active" &&
                              (memberships[userId]?.planId || "") ===
                                resolveVisibleMembershipPlanId(
                                  membershipDrafts[userId] || memberships[userId]?.planId,
                                  adminVisibleMembershipPlanDrafts
                                )
                            }
                          >
                            {memberships[userId]?.status === "active" &&
                            (memberships[userId]?.planId || "") ===
                              resolveVisibleMembershipPlanId(
                                membershipDrafts[userId] || memberships[userId]?.planId,
                                adminVisibleMembershipPlanDrafts
                              )
                              ? "Active"
                              : "Activate membership"}
                          </Button>
                          <Button onClick={async () => clearUserMembership(userId)}>
                            Deactivate
                          </Button>
                          <Button
                            className="ghost-button"
                            onClick={() => deleteUserAccount(userId)}
                            disabled={userDeleteLoading[userId] || userId === user?.id}
                            style={{ borderColor: "rgba(248,113,113,0.6)", color: "#fca5a5" }}
                          >
                            {userDeleteLoading[userId] ? "Deleting..." : "Delete account"}
                          </Button>
                        </div>
                        <div
                          style={{
                            marginTop: 10,
                            padding: 10,
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 10,
                          }}
                        >
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              fontSize: 13,
                              marginBottom: 8,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(profiles[userId]?.legacy_member)}
                              onChange={(e) => toggleLegacyMemberTag(userId, e.target.checked)}
                            />
                            Old member (legacy migration)
                          </label>
                          {profiles[userId]?.legacy_member && (
                            <>
                              <div
                                style={{
                                  marginBottom: 8,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color:
                                    legacyMigrationStatus.kind === "active"
                                      ? "#86efac"
                                      : legacyMigrationStatus.kind === "ready"
                                        ? "#fcd34d"
                                        : "#d1d5db",
                                }}
                              >
                                {legacyMigrationStatus.label}
                              </div>
                              <div
                                style={{
                                  marginBottom: 8,
                                  fontSize: 12,
                                  opacity: 0.8,
                                  color: legacyFirstPaymentConfirmation ? "#86efac" : "#d1d5db",
                                }}
                              >
                                {legacyFirstPaymentConfirmation
                                  ? `First Mollie payment confirmed on ${new Date(
                                      legacyFirstPaymentConfirmation.timestamp
                                    ).toLocaleString()}`
                                  : "First Mollie payment not confirmed yet"}
                              </div>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <div>
                                  <label style={{ fontSize: 12, opacity: 0.75, display: "block" }}>
                                    End date
                                  </label>
                                  <Input
                                    type="date"
                                    value={legacyDraft.endDate}
                                    onChange={(e) =>
                                      setLegacyMembershipDraftField(userId, "endDate", e.target.value)
                                    }
                                  />
                                </div>
                                <div>
                                  <label style={{ fontSize: 12, opacity: 0.75, display: "block" }}>
                                    Credits / month
                                  </label>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={legacyDraft.creditsPerMonth}
                                    onChange={(e) =>
                                      setLegacyMembershipDraftField(
                                        userId,
                                        "creditsPerMonth",
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                                <div>
                                  <label style={{ fontSize: 12, opacity: 0.75, display: "block" }}>
                                    Monthly amount (EUR)
                                  </label>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={legacyDraft.monthlyPriceEur}
                                    onChange={(e) =>
                                      setLegacyMembershipDraftField(
                                        userId,
                                        "monthlyPriceEur",
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                              </div>
                              <div
                                style={{
                                  marginTop: 8,
                                  display: "flex",
                                  gap: 8,
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  fontSize: 12,
                                  opacity: 0.8,
                                }}
                              >
                                  <Button onClick={() => saveLegacyMembershipOverrides(userId)}>
                                    Save legacy settings
                                  </Button>
                                  {legacyMigrationStatus.kind === "ready" && (
                                    <Button
                                      className="ghost-button"
                                      onClick={() => sendLegacyMembershipReminder(userId)}
                                      disabled={legacyReminderSending[userId]}
                                    >
                                      {legacyReminderSending[userId] ? "Sending..." : "Send reminder"}
                                    </Button>
                                  )}
                                  <span>
                                    Active values: €{membershipMonthlyPrice}/mo • {membershipCreditsPerMonth} credits
                                  {memberships[userId]?.cancelledAt
                                    ? ` • Ends ${new Date(
                                        memberships[userId].cancelledAt
                                      ).toLocaleDateString()}`
                                    : ""}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                        {paymentHistoryRows.length > 0 && (
                          <div style={{ marginTop: 12, padding: 10, border: "1px solid rgba(244, 196, 48, 0.2)", borderRadius: 10, background: "rgba(244, 196, 48, 0.06)" }}>
                            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Membership payment history</div>
                            {paymentHistoryRows.map((entry) => (
                              <div key={entry.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 6, fontSize: 12, alignItems: "center", color: entry.canRetry ? "#b00020" : undefined }}>
                                <div>
                                  <div>{entry.periodLabel}</div>
                                  <div style={{ opacity: 0.7 }}>
                                    {entry.statusLabel}
                                    {entry.canRetry && entry.failureLabel ? ` • ${entry.failureLabel}` : ""}
                                  </div>
                                </div>
                                <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                                  <div>{entry.amountLabel}</div>
                                  {entry.canRetry ? (
                                    <button
                                      onClick={async () => {
                                        try {
                                          const retryUrl = await createMembershipRetryPaymentUrl(entry, userId);
                                          window.open(retryUrl, "_blank", "noopener,noreferrer");
                                        } catch (err) {
                                          alert(`Could not generate payment link: ${err?.message || "Unknown error"}`);
                                        }
                                      }}
                                      style={{
                                        padding: "4px 8px",
                                        fontSize: 11,
                                        backgroundColor: "#f4c430",
                                        color: "#000",
                                        border: "none",
                                        borderRadius: 4,
                                        cursor: "pointer",
                                      }}
                                    >
                                      Complete payment
                                    </button>
                                  ) : (
                                    <div style={{ opacity: 0.7 }}>{entry.settlementLabel}</div>
                                  )}
                                  {effectiveIsAdmin && entry.canRetry && (
                                    <button
                                      onClick={() => sendMembershipPaymentReminder(userId, entry)}
                                      disabled={membershipReminderSending[`${userId}-${entry.period}`]}
                                      style={{
                                        padding: "4px 8px",
                                        fontSize: 11,
                                        backgroundColor: "#f4c430",
                                        color: "#000",
                                        border: "none",
                                        borderRadius: 4,
                                        cursor: membershipReminderSending[`${userId}-${entry.period}`] ? "default" : "pointer",
                                        opacity: membershipReminderSending[`${userId}-${entry.period}`] ? 0.6 : 1,
                                      }}
                                    >
                                      {membershipReminderSending[`${userId}-${entry.period}`] ? "Sending..." : "Send reminder"}
                                    </button>
                                  )}
                                  {effectiveIsAdmin && membershipReminderSent[`${userId}-${entry.period}`] && (
                                    <div style={{ color: "#74d99f", fontSize: 11 }}>Reminder sent</div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {membershipActionStatus[userId] && (
                          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
                            {membershipActionStatus[userId]}
                          </div>
                        )}

                        {creditHistoryOpen[userId] &&
                          creditLedger.filter((entry) => entry.userId === userId).length > 0 && (
                            <div className="credit-history">
                              <div className="credit-history-title">Credit history</div>
                              {Object.entries(
                                creditLedger
                                  .filter((entry) => entry.userId === userId)
                                  .reduce((acc, entry) => {
                                    const dateKey = new Date(entry.timestamp).toLocaleDateString();
                                    if (!acc[dateKey]) acc[dateKey] = [];
                                    acc[dateKey].push(entry);
                                    return acc;
                                  }, {})
                              )
                                .sort(([a], [b]) => new Date(b) - new Date(a))
                                .map(([dateKey, entries], idx) => {
                                const isOpen = creditHistoryDateOpen[`${userId}-${dateKey}`];
                                return (
                                  <div key={dateKey}>
                                    <button
                                      type="button"
                                      className="credit-history-date-toggle"
                                      onClick={() =>
                                        setCreditHistoryDateOpen((prev) => ({
                                          ...prev,
                                          [`${userId}-${dateKey}`]: !prev[`${userId}-${dateKey}`],
                                        }))
                                      }
                                    >
                                      <span>{dateKey}</span>
                                      <span>{isOpen ? "Hide" : "Show"}</span>
                                    </button>
                                    {isOpen &&
                                      entries.map((entry) => (
                                        <div key={entry.id} className="credit-history-row">
                                          <span className="credit-history-date">
                                            {new Date(entry.timestamp).toLocaleTimeString([], {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </span>
                                          <span className="credit-history-delta">
                                            {entry.delta > 0 ? `+${entry.delta}` : entry.delta}
                                          </span>
                                          <span className="credit-history-reason">
                                            {entry.reason.replaceAll("_", " ").toLowerCase()}
                                          </span>
                                        </div>
                                      ))}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </>
            )}

            {adminAccountTab === "styling" && (
              <div className="account-block">
                <h3 className="account-section-title">App styling</h3>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 12 }}>
                  Customize branding and colors for the full app. The current Baile look is kept
                  as the baseline preset.
                </div>
                <div className="email-settings-form">
                  <div className="email-settings-section">
                    <h4>Branding</h4>
                    <label>Brand name</label>
                    <Input
                      value={appStylingDraft.brandName}
                      onChange={(e) =>
                        setAppStylingDraft((prev) => ({
                          ...prev,
                          brandName: e.target.value,
                        }))
                      }
                    />
                    <label>Header title</label>
                    <Input
                      value={appStylingDraft.appTitle}
                      onChange={(e) =>
                        setAppStylingDraft((prev) => ({
                          ...prev,
                          appTitle: e.target.value,
                        }))
                      }
                    />
                    <label>Logo URL (optional)</label>
                    <Input
                      value={appStylingDraft.logoUrl}
                      onChange={(e) =>
                        setAppStylingDraft((prev) => ({
                          ...prev,
                          logoUrl: e.target.value,
                        }))
                      }
                      placeholder="https://..."
                    />
                  </div>

                  <div className="email-settings-section">
                    <h4>Colors</h4>
                    {[
                      { key: "appBackground", label: "Page background" },
                      { key: "surfaceBackground", label: "Cards/background surfaces" },
                      { key: "headerBackground", label: "Header background" },
                      { key: "inputBackground", label: "Input/select background" },
                      { key: "borderColor", label: "Borders" },
                      { key: "textColor", label: "Primary text" },
                      { key: "mutedTextColor", label: "Muted text" },
                      { key: "buttonFrom", label: "Button gradient start" },
                      { key: "buttonTo", label: "Button gradient end" },
                      { key: "buttonText", label: "Button text" },
                    ].map((field) => (
                      <div key={field.key}>
                        <label>{field.label}</label>
                        <div className="app-styling-color-row">
                          <Input
                            type="color"
                            className="color-input"
                            value={appStylingDraft[field.key]}
                            onChange={(e) =>
                              setAppStylingDraft((prev) => ({
                                ...prev,
                                [field.key]: e.target.value,
                              }))
                            }
                          />
                          <div className="app-styling-color-code">
                            {String(appStylingDraft[field.key] || "").toUpperCase()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="email-settings-section">
                    <h4>Preview</h4>
                    <div
                      className="app-styling-preview"
                      style={{
                        background: appStylingDraft.surfaceBackground,
                        borderColor: appStylingDraft.borderColor,
                        color: appStylingDraft.textColor,
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>{appStylingDraft.brandName}</div>
                      <div style={{ fontSize: 12, color: appStylingDraft.mutedTextColor }}>
                        {appStylingDraft.appTitle}
                      </div>
                      <button
                        type="button"
                        style={{
                          marginTop: 8,
                          background: `linear-gradient(180deg, ${appStylingDraft.buttonFrom}, ${appStylingDraft.buttonTo})`,
                          color: appStylingDraft.buttonText,
                          boxShadow: "none",
                        }}
                      >
                        Primary button preview
                      </button>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Button
                        className="ghost-button"
                        onClick={() => {
                          setAppStyling({ ...appStylingDraft });
                          setAppStylingStatus(
                            "Preview applied locally. Click Save app styling to persist."
                          );
                        }}
                      >
                        Preview
                      </Button>
                      <Button className="primary-button" onClick={saveAppStyling}>
                        Save app styling
                      </Button>
                      <Button
                        className="ghost-button"
                        onClick={() => {
                          setAppStylingDraft(DEFAULT_APP_STYLING);
                          setAppStylingStatus("Reset to baseline values.");
                        }}
                      >
                        Reset to baseline
                      </Button>
                    </div>
                    {appStylingStatus && (
                      <div style={{ fontSize: 12, opacity: 0.7 }}>{appStylingStatus}</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {adminAccountTab === "emails" && (() => {
              const selectedTemplate = emailTemplateDrafts[selectedEmailTemplateKey];
              const siteUrl = emailBrandingDraft.siteUrl || window.location.origin;
              const sampleData = {
                first_name: "Kevin",
                brand_name: emailBrandingDraft.brandName,
                site_url: siteUrl,
                class_name: "Salsa Basics",
                class_date: "2026-02-07",
                class_time: "19:00",
                class_location: "Studio A",
                class_teacher: "Ana",
                review_url: `${siteUrl}?review=preview-token`,
                credits: 5,
                membership_name: "Standard",
              };
              const previewHtml =
                selectedTemplate && emailBrandingDraft
                  ? buildEmailPreviewHtml({
                      branding: emailBrandingDraft,
                      template: selectedTemplate,
                      data: sampleData,
                    })
                  : "";

              return (
                <div className="account-block">
                  <h3 className="account-section-title">Email templates</h3>
                  {emailTemplatesLoading && (
                    <div style={{ fontSize: 12, opacity: 0.7 }}>Loading templates...</div>
                  )}
                  {!emailTemplatesLoading && emailTemplates.length === 0 && (
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      No templates found. Run the email templates migration.
                    </div>
                  )}
                  {!emailTemplatesLoading && emailTemplates.length > 0 && (
                    <div className="email-settings-grid">
                      <div className="email-settings-form">
                        <details className="email-settings-section email-branding-collapsible">
                          <summary>Email branding</summary>
                          <label>Brand name</label>
                          <Input
                            value={emailBrandingDraft.brandName}
                            onChange={(e) =>
                              setEmailBrandingDraft((prev) => ({
                                ...prev,
                                brandName: e.target.value,
                              }))
                            }
                          />
                          <label>Logo URL</label>
                          <Input
                            value={emailBrandingDraft.logoUrl}
                            onChange={(e) =>
                              setEmailBrandingDraft((prev) => ({
                                ...prev,
                                logoUrl: e.target.value,
                              }))
                            }
                          />
                          <label>Default hero image URL</label>
                          <Input
                            value={emailBrandingDraft.heroUrl}
                            onChange={(e) =>
                              setEmailBrandingDraft((prev) => ({
                                ...prev,
                                heroUrl: e.target.value,
                              }))
                            }
                          />
                          <label>Accent color</label>
                          <Input
                            type="color"
                            className="color-input"
                            value={emailBrandingDraft.accentColor}
                            onChange={(e) =>
                              setEmailBrandingDraft((prev) => ({
                                ...prev,
                                accentColor: e.target.value,
                              }))
                            }
                          />
                          <label>Header background</label>
                          <Input
                            type="color"
                            className="color-input"
                            value={emailBrandingDraft.headerBg}
                            onChange={(e) =>
                              setEmailBrandingDraft((prev) => ({
                                ...prev,
                                headerBg: e.target.value,
                              }))
                            }
                          />
                          <label>Footer text</label>
                          <textarea
                            className="email-textarea"
                            value={emailBrandingDraft.footerText}
                            onChange={(e) =>
                              setEmailBrandingDraft((prev) => ({
                                ...prev,
                                footerText: e.target.value,
                              }))
                            }
                          />
                          <label>Signature</label>
                          <Input
                            value={emailBrandingDraft.signature}
                            onChange={(e) =>
                              setEmailBrandingDraft((prev) => ({
                                ...prev,
                                signature: e.target.value,
                              }))
                            }
                          />
                          <label>Site URL</label>
                          <Input
                            value={emailBrandingDraft.siteUrl}
                            onChange={(e) =>
                              setEmailBrandingDraft((prev) => ({
                                ...prev,
                                siteUrl: e.target.value,
                              }))
                            }
                          />
                          <Button onClick={saveEmailBranding} style={{ marginTop: 8 }}>
                            Save branding
                          </Button>
                          {emailBrandingStatus && (
                            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
                              {emailBrandingStatus}
                            </div>
                          )}
                        </details>

                        <div className="email-settings-section">
                          <h4>Template content</h4>
                          <label>Template</label>
                          <select
                            value={selectedEmailTemplateKey}
                            onChange={(e) => setSelectedEmailTemplateKey(e.target.value)}
                          >
                            {emailTemplates.map((template) => (
                              <option key={template.key} value={template.key}>
                                {template.name}
                              </option>
                            ))}
                          </select>

                          {selectedTemplate && (
                            <>
                              <label>Subject</label>
                              <Input
                                value={selectedTemplate.subject}
                                onChange={(e) => updateEmailTemplateDraft("subject", e.target.value)}
                              />
                              <label>Preheader</label>
                              <Input
                                value={selectedTemplate.preheader}
                                onChange={(e) => updateEmailTemplateDraft("preheader", e.target.value)}
                              />
                              <label>Hero image URL</label>
                              <Input
                                value={selectedTemplate.heroUrl}
                                onChange={(e) => updateEmailTemplateDraft("heroUrl", e.target.value)}
                              />
                              <label>Heading</label>
                              <Input
                                value={selectedTemplate.heading}
                                onChange={(e) => updateEmailTemplateDraft("heading", e.target.value)}
                              />
                              <label>Greeting</label>
                              <Input
                                value={selectedTemplate.greeting}
                                onChange={(e) => updateEmailTemplateDraft("greeting", e.target.value)}
                              />
                              <label>Body</label>
                              <div className="email-markdown-editor">
                                <textarea
                                  className="email-textarea"
                                  rows={8}
                                  value={selectedTemplate.body}
                                  onChange={(e) => updateEmailTemplateDraft("body", e.target.value)}
                                />
                                <div className="email-markdown-preview">
                                  <div className="email-preview-label">Markdown preview</div>
                                  <div
                                    className="email-markdown-content"
                                    dangerouslySetInnerHTML={{
                                      __html: markdownToHtml(
                                        applyEmailTokens(selectedTemplate.body || "", sampleData, {
                                          html: false,
                                          accentColor: emailBrandingDraft.accentColor,
                                        })
                                      )
                                        .replace(
                                          /<p>/g,
                                          '<p style="margin:0 0 12px;">'
                                        )
                                        .replace(
                                          /<ul>/g,
                                          '<ul style="margin:0 0 12px;padding-left:18px;">'
                                        )
                                        .replace(
                                          /<li>/g,
                                          '<li style="margin:0 0 6px;">'
                                        ),
                                    }}
                                  />
                                </div>
                              </div>
                              <label>CTA label</label>
                              <Input
                                value={selectedTemplate.ctaLabel}
                                onChange={(e) => updateEmailTemplateDraft("ctaLabel", e.target.value)}
                              />
                              <label>CTA URL</label>
                              <Input
                                value={selectedTemplate.ctaUrl}
                                onChange={(e) => updateEmailTemplateDraft("ctaUrl", e.target.value)}
                              />
                              <label>Signature</label>
                              <Input
                                value={selectedTemplate.signature}
                                onChange={(e) => updateEmailTemplateDraft("signature", e.target.value)}
                              />
                              <label>Footer (optional)</label>
                              <textarea
                                className="email-textarea"
                                value={selectedTemplate.footer}
                                onChange={(e) => updateEmailTemplateDraft("footer", e.target.value)}
                              />
                              <label className="email-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedTemplate.isActive}
                                  onChange={(e) => updateEmailTemplateDraft("isActive", e.target.checked)}
                                />
                                Active template
                              </label>
                              <Button onClick={saveEmailTemplate} style={{ marginTop: 8 }}>
                                Save template
                              </Button>
                              <Button onClick={sendTestEmail} className="ghost-button">
                                Send test to me
                              </Button>
                              {emailTemplateStatus && (
                                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
                                  {emailTemplateStatus}
                                </div>
                              )}
                              {emailTestStatus && (
                                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
                                  {emailTestStatus}
                                </div>
                              )}
                              {emailDebugInfo && (
                                <details className="email-debug-details">
                                  <summary>Debug (local)</summary>
                                  <div>Supabase URL: {emailDebugInfo.supabaseUrl || "—"}</div>
                                  <div>Session iss: {emailDebugInfo.sessionIss || "—"}</div>
                                  <div>Session ref: {emailDebugInfo.sessionRef || "—"}</div>
                                  <div>Anon ref: {emailDebugInfo.anonRef || "—"}</div>
                                </details>
                              )}
                            </>
                          )}
                        </div>

                        <div className="email-settings-section">
                          <h4>Available tokens</h4>
                          <div className="email-token-list">
                            <span>{`{{first_name}}`}</span>
                            <span>{`{{brand_name}}`}</span>
                            <span>{`{{site_url}}`}</span>
                            <span>{`{{class_name}}`}</span>
                            <span>{`{{class_date}}`}</span>
                            <span>{`{{class_time}}`}</span>
                            <span>{`{{class_location}}`}</span>
                            <span>{`{{class_teacher}}`}</span>
                            <span>{`{{review_url}}`}</span>
                            <span>{`{{spots_left}}`}</span>
                            <span>{`{{credits}}`}</span>
                            <span>{`{{credits_remaining}}`}</span>
                            <span>{`{{membership_name}}`}</span>
                            <span>{`{{membership_end_date}}`}</span>
                            <span>{`{{amount_paid}}`}</span>
                          </div>
                          <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>
                            Tokens will be replaced with real data at send time.
                          </div>
                        </div>
                      </div>

                      <div className="email-settings-preview">
                        <div className="email-preview-header">Preview</div>
                        <div className="email-preview-frame">
                          {selectedTemplate ? (
                            <iframe
                              title="Email preview"
                              srcDoc={previewHtml}
                              className="email-preview-iframe"
                            />
                          ) : (
                            <div style={{ fontSize: 12, opacity: 0.7 }}>Select a template.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {adminAccountTab === "invoices" && (
              <div style={{ display: "grid", gap: 14 }}>
                <div className="account-block">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                    <h3 className="account-section-title">Invoices</h3>
                    <Button
                      className="ghost-button"
                      onClick={loadInvoices}
                      disabled={invoicesLoading}
                    >
                      {invoicesLoading ? "Loading..." : "Refresh"}
                    </Button>
                  </div>

                  <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
                    <Input
                      placeholder="Search invoice number, payment id, or user email..."
                      value={invoiceSearch}
                      onChange={(e) => setInvoiceSearch(e.target.value)}
                    />
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <Input
                        type="date"
                        value={invoiceDateFrom}
                        onChange={(e) => setInvoiceDateFrom(e.target.value)}
                      />
                      <Input
                        type="date"
                        value={invoiceDateTo}
                        onChange={(e) => setInvoiceDateTo(e.target.value)}
                      />
                      <Button
                        className="ghost-button"
                        onClick={() => {
                          setInvoiceSearch("");
                          setInvoiceDateFrom("");
                          setInvoiceDateTo("");
                        }}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>

                  {invoicesError && (
                    <div style={{ fontSize: 12, color: "#fca5a5", marginTop: 6 }}>
                      {invoicesError}
                    </div>
                  )}

                  {!invoicesLoading && invoices.length === 0 && (
                    <p style={{ opacity: 0.6, marginTop: 10 }}>No invoices yet.</p>
                  )}

                  <div className="invoice-list">
                    {invoices
                      .filter((invoice) => {
                        const profile = profiles[invoice.user_id];
                        const email = profile?.email || invoice.customer_email || "";
                        const haystack = [
                          invoice.invoice_number,
                          invoice.payment_id,
                          email,
                          profile?.name || invoice.customer_name || "",
                        ]
                          .join(" ")
                          .toLowerCase();
                        const matchesSearch = invoiceSearch
                          ? haystack.includes(invoiceSearch.trim().toLowerCase())
                          : true;
                        const date = invoice.invoice_date || "";
                        const afterFrom = invoiceDateFrom ? date >= invoiceDateFrom : true;
                        const beforeTo = invoiceDateTo ? date <= invoiceDateTo : true;
                        return matchesSearch && afterFrom && beforeTo;
                      })
                      .map((invoice) => {
                        const profile = profiles[invoice.user_id];
                        const email = profile?.email || invoice.customer_email || invoice.user_id || "—";
                        const name = profile?.name || invoice.customer_name || "";
                        return (
                          <div key={invoice.id} className="invoice-row">
                            <div className="invoice-meta">
                              <div className="invoice-number">{invoice.invoice_number}</div>
                              <div className="invoice-user">
                                {name ? `${name} • ${email}` : email}
                              </div>
                              <div className="invoice-date">
                                {invoice.invoice_date}
                              </div>
                            </div>
                            <div className="invoice-actions">
                              <div className="invoice-amount">{formatEuro(invoice.amount_incl)}</div>
                              {invoice.pdf_url ? (
                                <Button
                                  className="ghost-button"
                                  onClick={() => openInvoicePdf(invoice.pdf_url, invoice.id)}
                                  disabled={invoiceDownloadLoading[invoice.id]}
                                >
                                  {invoiceDownloadLoading[invoice.id] ? "Opening..." : "Open PDF"}
                                </Button>
                              ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                                  <span style={{ fontSize: 12, opacity: 0.6 }}>
                                    PDF pending
                                  </span>
                                  <Button
                                    className="ghost-button"
                                    onClick={() => regenerateInvoicePdf(invoice.id)}
                                    disabled={invoiceRetryLoading[invoice.id]}
                                  >
                                    {invoiceRetryLoading[invoice.id] ? "Generating..." : "Generate PDF"}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                <div className="account-block">
                  <details className="email-settings-section">
                    <summary>Invoice presets</summary>
                    <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>
                      Configure issuer info and default text used in generated invoice PDFs.
                    </div>
                    <label>Issuer name</label>
                    <Input
                      value={invoicePresetsDraft.issuerName}
                      onChange={(e) =>
                        setInvoicePresetsDraft((prev) => ({ ...prev, issuerName: e.target.value }))
                      }
                    />

                    <label>Issuer address (one line per row)</label>
                    <textarea
                      className="email-textarea"
                      value={invoicePresetsDraft.issuerAddress}
                      onChange={(e) =>
                        setInvoicePresetsDraft((prev) => ({ ...prev, issuerAddress: e.target.value }))
                      }
                    />

                    <label>VAT number</label>
                    <Input
                      value={invoicePresetsDraft.issuerVatNumber}
                      onChange={(e) =>
                        setInvoicePresetsDraft((prev) => ({ ...prev, issuerVatNumber: e.target.value }))
                      }
                    />

                    <label>KVK number</label>
                    <Input
                      value={invoicePresetsDraft.issuerKvkNumber}
                      onChange={(e) =>
                        setInvoicePresetsDraft((prev) => ({ ...prev, issuerKvkNumber: e.target.value }))
                      }
                    />

                    <label>Issuer email</label>
                    <Input
                      value={invoicePresetsDraft.issuerEmail}
                      onChange={(e) =>
                        setInvoicePresetsDraft((prev) => ({ ...prev, issuerEmail: e.target.value }))
                      }
                    />

                    <label>Issuer phone</label>
                    <Input
                      value={invoicePresetsDraft.issuerPhone}
                      onChange={(e) =>
                        setInvoicePresetsDraft((prev) => ({ ...prev, issuerPhone: e.target.value }))
                      }
                    />

                    <label>Default line item description</label>
                    <Input
                      value={invoicePresetsDraft.defaultDescription}
                      onChange={(e) =>
                        setInvoicePresetsDraft((prev) => ({
                          ...prev,
                          defaultDescription: e.target.value,
                        }))
                      }
                    />

                    <label>Footer note</label>
                    <textarea
                      className="email-textarea"
                      value={invoicePresetsDraft.footerNote}
                      onChange={(e) =>
                        setInvoicePresetsDraft((prev) => ({ ...prev, footerNote: e.target.value }))
                      }
                    />

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                      <Button onClick={saveInvoicePresets}>Save invoice presets</Button>
                      <Button
                        className="ghost-button"
                        onClick={() => {
                          setInvoicePresetsDraft(invoicePresets);
                          setInvoicePresetsStatus("");
                        }}
                      >
                        Reset
                      </Button>
                      {invoicePresetsStatus && (
                        <span style={{ fontSize: 12, opacity: 0.75 }}>{invoicePresetsStatus}</span>
                      )}
                    </div>
                  </details>
                </div>
              </div>
            )}

            {adminAccountTab === "audit" && (
              <div className="account-block">
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <h3 className="account-section-title">Audit log</h3>

                  <Button onClick={() => setShowAuditLog((v) => !v)}>
                    {showAuditLog ? "Hide" : "Show"}
                  </Button>
                </div>

                {showAuditLog && (
                  <>
                    {auditLog.length === 0 && (
                      <p style={{ opacity: 0.6 }}>No activity yet.</p>
                    )}

                    <Input
                      placeholder="Search audit log..."
                      value={auditSearch}
                      onChange={(e) => setAuditSearch(e.target.value)}
                      style={{ marginTop: 8, marginBottom: 8 }}
                    />

                    {Object.entries(
                      auditLog
                        .filter((entry) => {
                          if (!auditSearch.trim()) return true;
                          const haystack = [
                            entry.actor,
                            entry.action,
                            JSON.stringify(entry.details || {}),
                          ]
                            .join(" ")
                            .toLowerCase();
                          return haystack.includes(auditSearch.trim().toLowerCase());
                        })
                        .reduce((groups, entry) => {
                          const dateKey = new Date(entry.timestamp).toLocaleDateString();
                          if (!groups[dateKey]) groups[dateKey] = [];
                          groups[dateKey].push(entry);
                          return groups;
                        }, {})
                    )
                      .sort(([a], [b]) => new Date(b) - new Date(a))
                      .map(([dateKey, entries]) => {
                        const isOpen = auditDateOpen[dateKey];
                        return (
                          <div key={dateKey} style={{ marginTop: 10 }}>
                            <button
                              type="button"
                              className="credit-history-date-toggle"
                              onClick={() =>
                                setAuditDateOpen((prev) => ({
                                  ...prev,
                                  [dateKey]: !prev[dateKey],
                                }))
                              }
                            >
                              <span>{dateKey}</span>
                              <span>{isOpen ? "Hide" : "Show"}</span>
                            </button>
                            {isOpen &&
                              entries.map((entry) => (
                                <div
                                  key={entry.id}
                                  style={{
                                    fontSize: 13,
                                    padding: "6px 0",
                                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                                    opacity: 0.9,
                                  }}
                                >
                                  <div style={{ fontSize: 11, opacity: 0.6 }}>
                                    {getAuditCategory(entry.action).toUpperCase()}
                                  </div>
                                  <div>
                                    <strong>{entry.actor}</strong>{" "}
                                    <span style={{ opacity: 0.75 }}>
                                      {actionLabels[entry.action] || entry.action.toLowerCase()}
                                    </span>
                                  </div>

                                  <div style={{ fontSize: 11, opacity: 0.6 }}>
                                    {new Date(entry.timestamp).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      second: "2-digit",
                                    })}
                                  </div>

                                  {entry.details && (
                                    <div style={{ marginTop: 4 }}>
                                      <Button
                                        className="ghost-button"
                                        onClick={() =>
                                          setAuditDetailsOpen((prev) => ({
                                            ...prev,
                                            [entry.id]: !prev[entry.id],
                                          }))
                                        }
                                      >
                                        {auditDetailsOpen[entry.id] ? "Hide details" : "Show details"}
                                      </Button>
                                      {auditDetailsOpen[entry.id] && (
                                        <pre
                                          style={{
                                            fontSize: 11,
                                            marginTop: 4,
                                            whiteSpace: "pre-wrap",
                                            opacity: 0.7,
                                          }}
                                        >
                                          {JSON.stringify(entry.details, null, 2)}
                                        </pre>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                          </div>
                        );
                      })}
                  </>
                )}
              </div>
            )}

            {adminAccountTab === "analytics" && (() => {
              const payoutLabel = new Intl.NumberFormat("en-GB", {
                style: "currency",
                currency: "EUR",
              }).format(analytics.payoutTotal || 0);
              const monthOptions = getRecentMonthOptions(36);

              return (
                <div className="account-block">
                  <div className="analytics-toolbar">
                    <h3 className="account-section-title">Analytics</h3>
                    <div className="analytics-controls">
                      <select
                        value={analyticsRange}
                        onChange={(e) => setAnalyticsRange(e.target.value)}
                      >
                        <option value="month">By month</option>
                        <option value="all">All time</option>
                      </select>
                      {analyticsRange === "month" && (
                        <select
                          value={analyticsMonth}
                          onChange={(e) => setAnalyticsMonth(e.target.value)}
                        >
                          {monthOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>

                  {analyticsError && (
                    <div style={{ fontSize: 12, color: "#fca5a5", marginTop: 8 }}>
                      {analyticsError}
                    </div>
                  )}

                  {(() => {
                    const stats = [
                      { key: "creditsSold", label: "Total credits sold", value: analytics.creditsSold || 0 },
                      { key: "newAccounts", label: "New accounts", value: analytics.newAccounts || 0 },
                      {
                        key: analyticsRange === "all" ? "activeMemberships" : "newMemberships",
                        label: analyticsRange === "all" ? "Active memberships" : "New memberships in month",
                        value: analyticsRange === "all" ? analytics.activeMemberships : analytics.newMemberships,
                      },
                      { key: "payoutTotal", label: `Payout ${analyticsRange === "all" ? "(estimated)" : "for month (estimated)"}`, value: payoutLabel },
                      { key: "classesHappened", label: "Total classes happened", value: analytics.classesHappened },
                      { key: "totalPeopleParticipated", label: "Total people participated", value: analytics.totalPeopleParticipated },
                      { key: "uniqueParticipants", label: "Unique participants", value: analytics.uniqueParticipants },
                      { key: "membershipChurn", label: "Membership cancellations", value: analytics.membershipChurn },
                    ];
                    return (
                      <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
                        {stats.map((stat) => (
                          <div className="analytics-stat-row" key={stat.key}>
                            <span>{stat.label}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <strong>{analyticsLoading ? "..." : stat.value}</strong>
                              {analyticsRange === "all" && (
                                <Button
                                  className="ghost-button"
                                  style={{ padding: "3px 7px", fontSize: 11 }}
                                  onClick={() => setSelectedAnalyticsMetric(stat)}
                                >
                                  View trend
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {selectedAnalyticsMetric && analyticsRange === "all" && (() => {
                    const formatMonth = (month, options = { month: "short", year: "numeric" }) =>
                      month
                        ? new Date(`${month}-01T00:00:00`).toLocaleDateString(undefined, options)
                        : "";
                    const monthlyValues = new Map(
                      analyticsMonthly.map((row) => [row.month, Number(row[selectedAnalyticsMetric.key]) || 0])
                    );
                    const firstMonth = analyticsMonthly[0]?.month;
                    const lastMonth = analyticsMonthly[analyticsMonthly.length - 1]?.month;
                    const chartMonths = [];
                    if (firstMonth && lastMonth) {
                      const cursor = new Date(`${firstMonth}-01T00:00:00Z`);
                      const end = new Date(`${lastMonth}-01T00:00:00Z`);
                      while (cursor <= end) {
                        chartMonths.push(`${cursor.getUTCFullYear()}-${String(cursor.getUTCMonth() + 1).padStart(2, "0")}`);
                        cursor.setUTCMonth(cursor.getUTCMonth() + 1);
                      }
                    }
                    const values = chartMonths.map((month) => monthlyValues.get(month) || 0);
                    const maximum = Math.max(...values, 1);
                    const chartLeft = 58;
                    const chartRight = 620;
                    const chartTop = 18;
                    const chartBottom = 150;
                    const chartWidth = chartRight - chartLeft;
                    const chartHeight = chartBottom - chartTop;
                    const getX = (index) =>
                      values.length <= 1 ? chartLeft + chartWidth / 2 : chartLeft + (index * chartWidth) / (values.length - 1);
                    const getY = (value) => chartBottom - (value / maximum) * chartHeight;
                    const points = values
                      .map((value, index) => {
                        return `${getX(index)},${getY(value)}`;
                      })
                      .join(" ");
                    const axisValues = [maximum, maximum / 2, 0];
                    const formatValue = (value) =>
                      selectedAnalyticsMetric.key === "payoutTotal" ? `€${value.toFixed(2)}` : Math.round(value);
                    return (
                      <div className="modal-backdrop" onClick={() => setSelectedAnalyticsMetric(null)}>
                        <div
                          className="modal-card"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="analytics-trend-title"
                          onClick={(event) => event.stopPropagation()}
                          style={{ width: "min(680px, calc(100vw - 32px))" }}
                        >
                          <div className="modal-header">
                            <strong id="analytics-trend-title">{selectedAnalyticsMetric.label}</strong>
                            <button className="modal-close" onClick={() => setSelectedAnalyticsMetric(null)} aria-label="Close trend">x</button>
                          </div>
                          <div style={{ padding: "8px 16px 18px" }}>
                            {analyticsMonthly.length ? (
                              <>
                                <svg viewBox="0 0 640 230" role="img" aria-label={`${selectedAnalyticsMetric.label} by month`} style={{ width: "100%", height: "auto" }}>
                                  {axisValues.map((value) => {
                                    const y = getY(value);
                                    return (
                                      <g key={value}>
                                        <line x1={chartLeft} x2={chartRight} y1={y} y2={y} stroke="rgba(255,255,255,0.18)" />
                                        <text x={chartLeft - 8} y={y + 4} fill="rgba(255,255,255,0.65)" fontSize="11" textAnchor="end">
                                          {formatValue(value)}
                                        </text>
                                      </g>
                                    );
                                  })}
                                  <line x1={chartLeft} x2={chartLeft} y1={chartTop} y2={chartBottom} stroke="rgba(255,255,255,0.3)" />
                                  <line x1={chartLeft} x2={chartRight} y1={chartBottom} y2={chartBottom} stroke="rgba(255,255,255,0.3)" />
                                  <polyline fill="none" stroke="#f4c430" strokeWidth="3" points={points} />
                                  {values.map((value, index) => (
                                    <g key={chartMonths[index]}>
                                      <circle cx={getX(index)} cy={getY(value)} r="4" fill="#f4c430">
                                        <title>{`${formatMonth(chartMonths[index])}: ${formatValue(value)}`}</title>
                                      </circle>
                                      <line x1={getX(index)} x2={getX(index)} y1={chartBottom} y2={chartBottom + 5} stroke="rgba(255,255,255,0.4)" />
                                      <text
                                        x={getX(index)}
                                        y="178"
                                        fill="rgba(255,255,255,0.65)"
                                        fontSize="10"
                                        textAnchor="end"
                                        transform={`rotate(-45 ${getX(index)} 178)`}
                                      >
                                        {formatMonth(chartMonths[index], { month: "short", year: "2-digit" })}
                                      </text>
                                    </g>
                                  ))}
                                </svg>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, opacity: 0.7 }}>
                                  <span>{formatMonth(firstMonth)}</span>
                                  <span>Peak: {formatValue(maximum)}</span>
                                  <span>{formatMonth(lastMonth)}</span>
                                </div>
                              </>
                            ) : (
                              <div style={{ opacity: 0.7 }}>No all-time data is available yet.</div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div style={{ fontSize: 11, opacity: 0.6, marginTop: 10 }}>
                    Based on invoices, membership records, class attendance, and ledger entries.
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </>
    ) : (
      <>
      <div className="account-tabs">
        <button
          type="button"
          className={`account-tab-button ${userAccountTab === "account" ? "active" : ""}`}
          onClick={() => setUserAccountTab("account")}
        >
          Account
        </button>
        <button
          type="button"
          className={`account-tab-button ${userAccountTab === "classes" ? "active" : ""}`}
          onClick={() => setUserAccountTab("classes")}
        >
          My bookings
        </button>
        <button
          type="button"
          className={`account-tab-button ${userAccountTab === "credits" ? "active" : ""}`}
          onClick={() => setUserAccountTab("credits")}
        >
          Credits & Memberships
        </button>
        <button
          type="button"
          className={`account-tab-button ${userAccountTab === "invoices" ? "active" : ""}`}
          onClick={() => setUserAccountTab("invoices")}
        >
          Invoices
        </button>
      </div>
      <div
        className={`account-grid ${
          ["classes", "credits", "invoices"].includes(userAccountTab) ? "account-grid-wide" : ""
        } ${userAccountTab === "account" && !effectiveIsAdmin ? "account-grid-user-account-layout" : ""}`}
      >
        {["account", "credits"].includes(userAccountTab) && (
        <div className="account-column">
          {userAccountTab === "account" && (
          <div className="account-block account-info-block">
            <h3 className="account-section-title">Account information</h3>
            <div style={{ fontWeight: 700 }}>{user.email}</div>
            <div className="form-field" style={{ marginTop: 12 }}>
              <label>Your name</label>
              <div className="account-inline-actions">
                <Input
                  value={profileNameDraft}
                  onChange={(e) => setProfileNameDraft(e.target.value)}
                  placeholder="Add your name"
                />
                <Button
                  className="primary-button"
                  onClick={saveProfileName}
                  disabled={profileNameSaving}
                >
                  {profileNameSaving ? "Saving..." : "Save name"}
                </Button>
              </div>
              {profileNameStatus && (
                <div style={{ fontSize: 12, opacity: 0.7 }}>{profileNameStatus}</div>
              )}
            </div>
            <div className="form-field" style={{ marginTop: 12 }}>
              <label>Billing address (for invoices)</label>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
                If a full invoice is required, please fill in these fields.
              </div>
              <Input
                value={billingAddressStreetDraft}
                onChange={(e) => setBillingAddressStreetDraft(e.target.value)}
                placeholder="Street + number"
              />
              <Input
                value={billingAddressPostcodeDraft}
                onChange={(e) => setBillingAddressPostcodeDraft(e.target.value)}
                placeholder="Postcode"
                style={{ marginTop: 8 }}
              />
              <Input
                value={billingAddressCityDraft}
                onChange={(e) => setBillingAddressCityDraft(e.target.value)}
                placeholder="City"
                style={{ marginTop: 8 }}
              />
              <div className="account-inline-actions account-inline-actions-wrap" style={{ marginTop: 8 }}>
                <Button
                  className="ghost-button"
                  onClick={saveBillingAddress}
                  disabled={billingAddressSaving}
                >
                  {billingAddressSaving ? "Saving..." : "Save billing address"}
                </Button>
                {billingAddressStatus && (
                  <div style={{ fontSize: 12, opacity: 0.7 }}>{billingAddressStatus}</div>
                )}
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
                Change your password.
              </div>
              {isPasswordRecovery && (
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
                  Recovery mode active: current password is not required.
                </div>
              )}
              <div className="account-inline-actions account-inline-actions-wrap">
                {!isPasswordRecovery && (
                  <Input
                    type="password"
                    value={currentPasswordDraft}
                    onChange={(e) => {
                      setCurrentPasswordDraft(e.target.value);
                      if (passwordChangeStatus) setPasswordChangeStatus("");
                    }}
                    placeholder="Current password"
                  />
                )}
                <Input
                  type="password"
                  value={newPasswordDraft}
                  onChange={(e) => {
                    setNewPasswordDraft(e.target.value);
                    if (passwordChangeStatus) setPasswordChangeStatus("");
                  }}
                  placeholder="New password"
                />
                <Input
                  type="password"
                  value={confirmNewPasswordDraft}
                  onChange={(e) => {
                    setConfirmNewPasswordDraft(e.target.value);
                    if (passwordChangeStatus) setPasswordChangeStatus("");
                  }}
                  placeholder="Confirm new password"
                />
              </div>
              <Button
                className="ghost-button"
                onClick={requestPasswordChange}
                disabled={passwordChangeLoading}
                style={{ marginTop: 8 }}
              >
                {passwordChangeLoading ? "Updating..." : "Update password"}
              </Button>
              {passwordChangeStatus && (
                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
                  {passwordChangeStatus}
                </div>
              )}
            </div>
          </div>
          )}

          {userAccountTab === "credits" && !effectiveIsAdmin && (
            <div className="account-block account-balance-highlight account-balance-block">
              <div className="account-balance-highlight-label">Available credits</div>
              <div className="account-balance-highlight-value">
                {getAvailableCredits(user.id)}
              </div>
              <div className="account-balance-highlight-meta">
                <span>Amiga tickets</span>
                <strong>{getAmigaTicketBalance(user.id)}</strong>
              </div>
            </div>
          )}

          {userAccountTab === "credits" && !effectiveIsAdmin && (
            <div className="account-block account-buy-credits-block">
              <h3 className="account-section-title">Buy credits</h3>
              <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 10 }}>
                All prices shown include 21% VAT.
              </div>
            <div className="account-purchase-grid">
              {creditPackages.map((pack) => (
                <div key={pack.amount} className="account-purchase-card">
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      opacity: pack.amount === 10 ? 0.85 : 0,
                      height: 14,
                    }}
                  >
                    Most popular
                  </span>
                  <Button
                    onClick={() => purchaseCredits(pack)}
                    style={{
                      border: pack.amount === 10 ? "2px solid #ef4444" : undefined,
                      boxShadow:
                        pack.amount === 10
                          ? "0 0 0 2px rgba(239,68,68,0.25)"
                          : undefined,
                    }}
                    disabled={checkoutLoading}
                  >
                    {pack.label} • {pack.price}
                  </Button>
                </div>
              ))}
              <div className="account-purchase-card">
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    opacity: 0,
                    height: 14,
                  }}
                >
                  Featured
                </span>
                <Button onClick={purchaseAmigaTicket} disabled={checkoutLoading}>
                  {amigaTicketPack.label} • {amigaTicketPack.price}
                </Button>
              </div>
            </div>
              <div style={{ fontSize: 11, opacity: 0.75, marginTop: 8 }}>
                1 class = 1 credit.
              </div>
              <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>
                Amiga ticket = 1 ticket to reserve 2 spots in one class (you + 1 guest).
              </div>
              {checkoutError && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#fca5a5" }}>
                  {checkoutError}
                </div>
              )}
              <div style={{ fontSize: 11, opacity: 0.6, marginTop: 8 }}>
                Payments powered by Mollie. Credits expire 12 months after purchase.
              </div>
            </div>
          )}

          {userAccountTab === "credits" && !effectiveIsAdmin && (
            <div className="account-block account-memberships-block">
              {(() => {
                const legacyStatus = getLegacyMigrationStatus(user.id);
                const currentMembershipPlan = membershipPlans.find(
                  (plan) => plan.id === memberships[user.id]?.planId
                );
                return (
                  <>
              <h3 className="account-section-title">Memberships</h3>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>
                Members get monthly credits and priority access.
              </div>
              <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 10 }}>
                All prices shown include 21% VAT.
              </div>
              {isMembershipActiveNow(user.id) && (
                <div className="account-membership-summary">
                  <div>
                    <div style={{ fontWeight: 700 }}>
                      Current plan:{" "}
                      {membershipPlans.find((p) => p.id === memberships[user.id].planId)?.name ||
                        memberships[user.id].planId}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      {`€${getMembershipMonthlyPrice(user.id)}/mo • ${getMembershipCreditsPerMonth(
                        user.id
                      )} credits/month • min. ${formatMonthCount(getMembershipMinimumMonths(user.id))}`}
                      {profiles[user.id]?.legacy_member ? " • Legacy member" : ""}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 6 }}>
                      Credits used this month: {getMembershipUsed(user.id)} • Remaining:{" "}
                      {getMembershipRemaining(user.id)}
                    </div>
                    {!canCancelMembershipNow(user.id) && (
                      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                        Minimum term ends on {getMembershipMinimumTermEndDate(user.id) || "—"}
                      </div>
                    )}
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                      {isMembershipCancellationScheduled(user.id) ? "Ends on" : "Next renewal"}:{" "}
                      {getNextRenewalDate(user.id) || "—"}
                    </div>
                  </div>
                  <Button
                    onClick={cancelMembershipAtRenewal}
                    disabled={
                      checkoutLoading ||
                      isMembershipCancellationScheduled(user.id) ||
                      !canCancelMembershipNow(user.id)
                    }
                  >
                    {isMembershipCancellationScheduled(user.id)
                      ? "Cancellation scheduled"
                      : !canCancelMembershipNow(user.id)
                        ? "Too early to cancel"
                      : "Cancel subscription"}
                  </Button>
                </div>
              )}
              {legacyStatus.kind === "ready" && currentMembershipPlan && (
                <div
                  style={{
                    marginBottom: 12,
                    padding: 12,
                    border: "1px solid rgba(244, 196, 48, 0.45)",
                    borderRadius: 14,
                    background: "rgba(244, 196, 48, 0.08)",
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    Complete your legacy membership payment setup
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 10 }}>
                    Your first payment will use your personalized legacy agreement:
                    {` €${getMembershipMonthlyPrice(user.id)}/mo • ${getMembershipCreditsPerMonth(
                      user.id
                    )} credits/month`}
                    {memberships[user.id]?.cancelledAt
                      ? ` • Ends ${new Date(memberships[user.id].cancelledAt).toLocaleDateString()}`
                      : ""}
                  </div>
                  <Button
                    onClick={() =>
                      subscribeMembership(currentMembershipPlan, { requireLegacySetup: true })
                    }
                    disabled={checkoutLoading || !membershipAcceptedTerms}
                  >
                    Complete legacy payment setup
                  </Button>
                </div>
              )}
              {(() => {
                const membershipPaymentRows = buildMembershipPaymentHistoryViewModel(
                  membershipPaymentHistory[user.id] || []
                );
                return membershipPaymentRows.length > 0 ? (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: 12,
                      border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: 14,
                      background: "rgba(255,255,255,0.03)",
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Monthly payment history</div>
                    {membershipPaymentRows.map((entry) => (
                      <div
                        key={entry.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 8,
                          marginBottom: 6,
                          fontSize: 12,
                          alignItems: "center",
                          color: entry.canRetry ? "#b00020" : undefined,
                        }}
                      >
                        <div>
                          <div>{entry.periodLabel}</div>
                          <div style={{ opacity: 0.7 }}>
                            {entry.statusLabel}
                            {entry.canRetry && entry.failureLabel ? ` • ${entry.failureLabel}` : ""}
                          </div>
                        </div>
                        <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                          <div>{entry.amountLabel}</div>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {entry.canRetry ? (
                              <Button
                                className="ghost-button"
                                onClick={async () => {
                                  try {
                                    const retryUrl = await createMembershipRetryPaymentUrl(entry);
                                    window.location.href = retryUrl;
                                  } catch (err) {
                                    // eslint-disable-next-line no-console
                                    console.error("Failed to generate payment link:", err);
                                    alert(
                                      `Could not generate payment link: ${err.message || "Unknown error"}`
                                    );
                                  }
                                }}
                              >
                                Complete payment
                              </Button>
                            ) : (
                              <div style={{ opacity: 0.7 }}>{entry.settlementLabel}</div>
                            )}
                            {effectiveIsAdmin && entry.canRetry ? (
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                                <Button
                                  className="ghost-button"
                                  onClick={() => sendMembershipPaymentReminder(user.id, entry)}
                                  disabled={membershipReminderSending[`${user.id}-${entry.period}`]}
                                >
                                  {membershipReminderSending[`${user.id}-${entry.period}`] ? "Sending..." : "Send reminder"}
                                </Button>
                                {membershipReminderSent[`${user.id}-${entry.period}`] && (
                                  <div style={{ color: "#74d99f", fontSize: 11 }}>Reminder sent</div>
                                )}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <select
                  value={resolveVisibleMembershipPlanId(
                    membershipDrafts[user.id] ||
                      memberships[user.id]?.planId,
                    publicMembershipPlans
                  )}
                  onChange={(e) =>
                    setMembershipDrafts((prev) => ({
                      ...prev,
                      [user.id]: e.target.value,
                    }))
                  }
                >
                  {publicMembershipPlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} • €{plan.price}/mo • {plan.creditsPerMonth} credits • min.{" "}
                      {formatMonthCount(plan.minimumMonths)}
                    </option>
                  ))}
                </select>
                <Button
                  className="ghost-button"
                  type="button"
                  onClick={() =>
                    window.open(
                      "https://www.baileproject.com/become-a-baile-member",
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  Read more
                </Button>
                <Button
                  onClick={() =>
                    subscribeMembership(
                      membershipPlans.find(
                        (p) =>
                          p.id ===
                          resolveVisibleMembershipPlanId(
                            membershipDrafts[user.id] || memberships[user.id]?.planId,
                            publicMembershipPlans
                          )
                      )
                    )
                  }
                  disabled={checkoutLoading || !membershipAcceptedTerms}
                >
                  {isMembershipActiveNow(user.id) ? "Switch membership" : "Become member"}
                </Button>
              </div>
              <label className="email-checkbox" style={{ alignItems: "flex-start", lineHeight: 1.4 }}>
                <input
                  type="checkbox"
                  checked={membershipAcceptedTerms}
                  onChange={(e) => {
                    setMembershipAcceptedTerms(e.target.checked);
                    if (membershipCheckoutError) setMembershipCheckoutError("");
                  }}
                />
                <span>
                  I declare that I've read and agree with the{" "}
                  <a
                    href="https://baileproject.nl/privacy"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "#ef4444" }}
                  >
                    general and privacy terms
                  </a>{" "}
                  of Baile Project and Baile Project memberships.
                </span>
              </label>
              <div style={{ fontSize: 11, opacity: 0.6, marginTop: 8 }}>
                Memberships will be billed monthly via Mollie.
              </div>
              {membershipCheckoutStatus && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#bbf7d0" }}>
                  {membershipCheckoutStatus}
                </div>
              )}
              {membershipCheckoutError && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#fca5a5" }}>
                  {membershipCheckoutError}
                </div>
              )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
        )}
        <div className="account-column">
          {(() => {
            const userBookings = getUserBookings(user.id);
            const reviewedClassIds = new Set(
              reviews
                .filter((review) => String(review.email || "").toLowerCase() === String(user.email || "").toLowerCase())
                .map((review) => review.class_id || review.classId)
                .filter(Boolean)
            );

            const upcoming = [];
            const past = [];

            userBookings.forEach((b) => {
              const cls = classes.find((c) => c.id === b.classId);
              if (!cls) return;

              if (!isClassPast(cls) && !cls.isCancelled) {
                upcoming.push(cls);
              } else {
                past.push(cls);
              }
            });
            upcoming.sort((a, b) => {
              const aStart = getClassStartDateTime(a);
              const bStart = getClassStartDateTime(b);
              if (aStart && bStart) return aStart - bStart;
              if (aStart) return -1;
              if (bStart) return 1;
              return String(a.id).localeCompare(String(b.id));
            });
            past.sort((a, b) => {
              const aStart = getClassStartDateTime(a);
              const bStart = getClassStartDateTime(b);
              if (aStart && bStart) return bStart - aStart;
              if (aStart) return -1;
              if (bStart) return 1;
              return String(a.id).localeCompare(String(b.id));
            });

	            const membership = memberships[user.id] || null;
	            const membershipCreditsPerMonth = getMembershipCreditsPerMonth(user.id);
	            const currentPeriod = new Date().toISOString().slice(0, 7);
            const membershipGrantPeriods = new Set(Object.keys(membershipUsage[user.id] || {}));
            if (membership?.startedAt) membershipGrantPeriods.add(String(membership.startedAt).slice(0, 7));
            if (isMembershipActiveNow(user.id)) membershipGrantPeriods.add(currentPeriod);
            const membershipGrantEntries =
              membershipCreditsPerMonth > 0
                ? Array.from(membershipGrantPeriods).map((period) => ({
                    id: `membership-grant-${user.id}-${period}`,
                    userId: user.id,
                    delta: membershipCreditsPerMonth,
                    reason: "MEMBERSHIP_CREDITS_GRANTED",
                    timestamp:
                      String(membership?.startedAt || "").slice(0, 7) === period
                        ? membership.startedAt
                        : `${period}-01T00:00:00.000Z`,
                  }))
                : [];
            const membershipUsageEntries = bookings
              .filter((entry) => entry.userId === user.id && entry.creditSource === "membership")
              .flatMap((entry) => {
                const usage = [];
                if (entry.createdAt) {
                  usage.push({
                    id: `membership-use-${entry.id}-book`,
                    userId: user.id,
                    delta: -1,
                    reason: "BOOK_CLASS_MEMBERSHIP",
                    timestamp: entry.createdAt,
                  });
                }
                if (entry.status === "cancelled" && entry.cancelledAt) {
                  usage.push({
                    id: `membership-use-${entry.id}-cancel`,
                    userId: user.id,
                    delta: +1,
                    reason: "CANCEL_CLASS_MEMBERSHIP",
                    timestamp: entry.cancelledAt,
                  });
                }
                return usage;
              });

            const userLedgerAll = [
              ...creditLedger.filter((entry) => entry.userId === user.id),
              ...amigaTicketLedger.filter((entry) => entry.userId === user.id),
              ...membershipGrantEntries,
              ...membershipUsageEntries,
            ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            let userLedger = userLedgerAll;
            if (!showAllUserCredits) {
              const compact = userLedgerAll.slice(0, 6);
              const latestMembershipGrant = userLedgerAll.find(
                (entry) => entry.reason === "MEMBERSHIP_CREDITS_GRANTED"
              );
              const hasGrantInCompact = compact.some(
                (entry) => entry.reason === "MEMBERSHIP_CREDITS_GRANTED"
              );
              if (latestMembershipGrant && !hasGrantInCompact) {
                userLedger = [...compact.slice(0, 5), latestMembershipGrant].sort(
                  (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
                );
              } else {
                userLedger = compact;
              }
            }

            return (
              <>
                {userAccountTab === "classes" && (
                  <>
                <div className="account-block">
                  <h3 className="account-section-title">Upcoming classes ({upcoming.length})</h3>

                  {upcoming.length === 0 && (
                    <p style={{ opacity: 0.7 }}>No upcoming classes.</p>
                  )}

                  {upcoming.map((cls) => (
                    <div
                      key={cls.id}
                      className={`class-item ${cls.isCancelled ? "is-cancelled" : ""}`}
                      style={{ marginTop: 8, "--class-bar": cls.color || "#f4c430" }}
                    >
                      <strong>{cls.name}</strong>
                      <div>📍 {cls.location}</div>
                      {cls.teacher && <div>👩‍🏫 {cls.teacher}</div>}
                      <div>{cls.date} • {cls.time}</div>

                      {cls.isCancelled ? (
                        <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>
                          Cancelled by admin
                        </div>
                      ) : (
                        <Button
                          onClick={() => cancelBooking(cls.id)}
                          style={{ marginTop: 6 }}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="account-block">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                    <h3 className="account-section-title muted" style={{ margin: 0 }}>
                      Past classes ({past.length})
                    </h3>
                    {past.length > 0 && (
                      <Button
                        className="ghost-button"
                        onClick={() => setPastClassesOpen((v) => !v)}
                      >
                        {pastClassesOpen ? "Hide" : "Show"}
                      </Button>
                    )}
                  </div>

                  {!pastClassesOpen && past.length > 0 && (
                    <p style={{ opacity: 0.6, marginTop: 10 }}>
                      Open to view your class history.
                    </p>
                  )}

                  {past.length === 0 && (
                    <p style={{ opacity: 0.5 }}>No past classes yet.</p>
                  )}

                  {pastClassesOpen && past.map((cls) => (
                    <div
                      key={cls.id}
                      className={`class-item ${cls.isCancelled ? "is-cancelled" : ""}`}
                      style={{
                        marginTop: 8,
                        opacity: 0.85,
                        "--class-bar": cls.color || "#f4c430",
                      }}
                    >
                      <strong>{cls.name}</strong>
                      <div>📍 {cls.location}</div>
                      {cls.teacher && <div>👩‍🏫 {cls.teacher}</div>}
                      <div>{cls.date} • {cls.time}</div>
                      <div style={{ marginTop: 8 }}>
                        {cls.isCancelled ? (
                          <div style={{ fontSize: 12, opacity: 0.7 }}>Cancelled classes can’t be reviewed.</div>
                        ) : reviewedClassIds.has(cls.id) ? (
                          <div style={{ fontSize: 12, opacity: 0.7 }}>Already reviewed.</div>
                        ) : (
                          <Button
                            className="ghost-button"
                            onClick={() => {
                              setSelectedPastReviewClassId(cls.id);
                              setPastReviewComment("");
                              setPastReviewRating(7);
                              setPastReviewStatus("");
                            }}
                          >
                            Review
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                  </>
                )}

                {userAccountTab === "credits" && !effectiveIsAdmin && (
                  <div className="account-block account-history-block">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <h3 className="account-section-title" style={{ margin: 0 }}>
                        My credit & ticket history
                      </h3>
                      {userLedgerAll.length > 6 && (
                        <Button
                          className="ghost-button"
                          onClick={() => setShowAllUserCredits((v) => !v)}
                        >
                          {showAllUserCredits ? "Show less" : "Show all"}
                        </Button>
                      )}
                    </div>
                    {userLedger.length === 0 ? (
                      <p style={{ opacity: 0.6 }}>No credit or ticket activity yet.</p>
                    ) : (
                      <div className="credit-history">
                        {userLedger.map((entry) => (
                          <div key={entry.id} className="credit-history-row">
                            <span className="credit-history-date">
                              {new Date(entry.timestamp).toLocaleDateString()}
                            </span>
                            <span className="credit-history-delta">
                              {entry.delta > 0 ? `+${entry.delta}` : entry.delta}
                            </span>
                            <span className="credit-history-reason">
                              {entry.reason.replaceAll("_", " ").toLowerCase()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {userAccountTab === "invoices" && !effectiveIsAdmin && (
                  <div className="account-block">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                      }}
                    >
                      <h3 className="account-section-title">My invoices</h3>
                      <Button
                        className="ghost-button"
                        onClick={loadInvoices}
                        disabled={invoicesLoading}
                      >
                        {invoicesLoading ? "Loading..." : "Refresh"}
                      </Button>
                    </div>

                    {invoicesError && (
                      <div style={{ fontSize: 12, color: "#fca5a5", marginTop: 6 }}>
                        {invoicesError}
                      </div>
                    )}

                    {!invoicesLoading && invoices.length === 0 && (
                      <p style={{ opacity: 0.6, marginTop: 10 }}>No invoices yet.</p>
                    )}

                    <div className="invoice-list">
                      {invoices
                        .filter((invoice) => invoice.user_id === user.id)
                        .map((invoice) => (
                          <div key={invoice.id} className="invoice-row">
                            <div className="invoice-meta">
                              <div className="invoice-number">{invoice.invoice_number}</div>
                              <div className="invoice-date">{invoice.invoice_date}</div>
                              <div className="invoice-user">{invoice.payment_id || "—"}</div>
                            </div>
                            <div className="invoice-actions">
                              <div className="invoice-amount">{formatEuro(invoice.amount_incl)}</div>
                              {invoice.pdf_url ? (
                                <Button
                                  className="ghost-button"
                                  onClick={() => openInvoicePdf(invoice.pdf_url, invoice.id)}
                                  disabled={invoiceDownloadLoading[invoice.id]}
                                >
                                  {invoiceDownloadLoading[invoice.id] ? "Opening..." : "Open PDF"}
                                </Button>
                              ) : (
                                <span style={{ fontSize: 12, opacity: 0.6 }}>PDF pending</span>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
      </>
    )}
    <Button onClick={logout} className="logout-button" style={{ marginTop: 20 }}>
      <LogOutIcon /> Log out
    </Button>
      </div>

  </Card>
</div>
)}

    {showHelpModal && (
      <div
        className="modal-backdrop help-modal-backdrop"
        onClick={() => {
          if (helpRequestSending) return;
          setShowHelpModal(false);
        }}
      >
        <div
          className="modal-card help-modal-card"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="help-modal-title"
        >
          <div className="modal-header">
            <div>
              <strong id="help-modal-title">Need help?</strong>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                Send your question to our team.
              </div>
            </div>
            <button
              type="button"
              className="modal-close"
              onClick={() => setShowHelpModal(false)}
              disabled={helpRequestSending}
            >
              Close
            </button>
          </div>

          <div className="form-field">
            <label>Your email</label>
            <Input value={user?.email || ""} disabled />
          </div>
          <div className="form-field" style={{ marginTop: 10 }}>
            <label>Your question</label>
            <textarea
              className="review-textarea"
              placeholder="Write your question here..."
              value={helpQuestionDraft}
              onChange={(e) => {
                setHelpQuestionDraft(e.target.value);
                if (helpRequestStatus) setHelpRequestStatus("");
              }}
              rows={5}
            />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
            <Button
              className="ghost-button"
              type="button"
              onClick={() => setShowHelpModal(false)}
              disabled={helpRequestSending}
            >
              Cancel
            </Button>
            <Button type="button" onClick={submitHelpRequest} disabled={helpRequestSending}>
              {helpRequestSending ? "Sending..." : "Send question"}
            </Button>
          </div>
          {helpRequestStatus && (
            <div
              style={{
                fontSize: 12,
                marginTop: 8,
                color: helpRequestStatus.toLowerCase().includes("sent") ? "#bbf7d0" : "#fca5a5",
              }}
            >
              {helpRequestStatus}
            </div>
          )}
        </div>
      </div>
    )}

    {renderPasswordRecoveryModal()}

    </div>
  );

  }
